<template>
  <div>
    <b-modal
      id="grace-time-modal"
      :title="$t('Set Grace Time')"
      hide-footer
      centered
    >
      <b-form @submit.prevent>
        <b-form-group
          :label="$t('Grace Time (minutes)')"
          :invalid-feedback="$t('Please enter valid number')"
          ref="grace"
        >
          <b-form-input
            type="number"
            v-model="graceTimeInput"
            :placeholder="$t('Enter grace time in minutes')"
          />
        </b-form-group>

        <div class="d-flex justify-content-end mt-2">
          <b-button
            variant="success"
            @click="applyGrace()"
            :disabled="applying"
          >
            <b-spinner v-if="applying" small type="grow" />
            <span v-else>{{ $t("Apply") }}</span>
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <!-- Edit Doc Type Modal -->
    <b-modal
      id="edit-doc-type-modal"
      :title="$t('Edit Document Type')"
      centered
      hide-footer
    >
      <b-form-group :label="$t('Document Type Name')" v-if="editDocObj">
        <b-form-input
          v-model.trim="editDocObj.title"
          :placeholder="$t('Enter name')"
          autofocus
          @keyup.enter="saveEditDocType()"
        />
      </b-form-group>
      <div class="d-flex justify-content-end mt-1">
        <b-button
          variant="outline-secondary"
          class="mr-1"
          @click="$bvModal.hide('edit-doc-type-modal')"
        >
          {{ $t("Cancel") }}
        </b-button>
        <b-button
          variant="primary"
          :disabled="savingDocType || !editDocObj || !editDocObj.title"
          @click="saveEditDocType()"
        >
          <b-spinner v-if="savingDocType" small />
          <span v-else>{{ $t("Save") }}</span>
        </b-button>
      </div>
    </b-modal>

    <!-- Return Loan Modal -->
    <b-modal
      id="return-loan-modal"
      :title="$t('Return Loan')"
      hide-footer
      centered
    >
      <b-form @submit.prevent v-if="returnLoanObj">
        <b-form-group
          :label="$t('Return Date')"
          :invalid-feedback="$t('Date is required')"
          ref="returnDate"
        >
          <flat-pickr
            :config="config"
            v-model="returnLoanObj.returnedOn"
            class="form-control"
            :placeholder="$t('Select return date')"
          />
        </b-form-group>

        <div class="d-flex justify-content-end mt-2">
          <b-button
            variant="success"
            @click="saveLoanReturn()"
            :disabled="returningLoan"
          >
            <b-spinner v-if="returningLoan" small type="grow" />
            <span v-else>{{ $t("Save") }}</span>
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <!-- Edit Amount Modal -->
    <b-modal
      id="deposit-amount-edit-modal"
      :title="$t('Edit Amount')"
      centered
      hide-footer
    >
      <b-form @submit.prevent v-if="editAmountObj">
        <b-form-group
          :label="$t('Amount')"
          :invalid-feedback="$t('Amount is required')"
          ref="edit_amount"
        >
          <b-form-input
            type="number"
            v-model="editAmountObj.amount"
            :placeholder="$t('Enter amount')"
          />
        </b-form-group>

        <div class="d-flex justify-content-end mt-2">
          <b-button
            variant="success"
            @click="saveEditAmount()"
            :disabled="isUpdatingAmount"
          >
            <b-spinner v-if="isUpdatingAmount" small type="grow" />
            <span v-else>{{ $t("Save") }}</span>
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <b-sidebar
      id="sidebar-right"
      ref="mySidebar"
      bg-variant="white"
      v-model="visibility"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">
          {{ $t(sidebarTitle) }}
        </h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hideSideBar()"
        />
      </div>
      <b-form class="p-2" @submit.prevent>
        <b-row class="mt-1">
          <b-col md="12">
            <b-form-group
              :label="$t('Date')"
              :invalid-feedback="$t('Date is required')"
              ref="inc_date"
            >
              <flat-pickr
                :config="config"
                v-model="incObj.date"
                class="form-control"
                :placeholder="$t('Select Date')"
              />
              <!-- @on-change="checkDate()" -->
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group
              :label="$t('Type')"
              :invalid-feedback="$t('Type is required')"
            >
              <v-select
                v-model="incObj.amountType"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="incAmountTypeOptions"
                :reduce="(opt) => opt.value"
                label="text"
                :clearable="false"
                @input="onIncAmountTypeChange()"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" v-if="incObj.amountType === 'percent'">
            <b-form-group
              :label="$t('Percentage')"
              :invalid-feedback="$t('Percentage is required')"
              ref="inc_percentage"
            >
              <b-form-input
                ref="inc_percentage"
                type="number"
                min="0"
                step="0.01"
                :placeholder="$t('Enter percentage of basic salary')"
                v-model="incObj.percentage"
                @input="onIncPercentageInput()"
              />
              <small class="text-muted">
                {{ $t("Basic salary") }}:
                {{ formatIncrementBasicSalary() }}
              </small>
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group
              :label="$t('Amount')"
              :invalid-feedback="$t('Amount is required')"
              ref="amount"
            >
              <b-form-input
                type="number"
                id="mc-first-name"
                :placeholder="$t('Enter amount')"
                v-model="incObj.amount"
                :disabled="incObj.amountType === 'percent'"
                @input="checkAmount()"
              />
            </b-form-group>
          </b-col>
          <b-col
            md="12"
            v-if="
              !incrementTotalObj ||
              incrementTotalObj.value !== 'increment-total'
            "
          >
            <b-form-group :invalid-feedback="$t('Selection is required.')">
              <div class="">
                <label class="bv-no-focus-ring col-form-label pt-0"
                  >{{ $t("Update Basic Salary") }}
                  <feather-icon
                    class="cursor-pointer"
                    icon="InfoIcon"
                    size="20"
                    v-b-tooltip.hover.bottomright
                    :title="
                      $t(
                        'This will add this increment amount to staff basic salary'
                      )
                    "
                /></label>
              </div>
              <v-select
                v-model="incObj.updateSalary"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="updateOptions"
                :reduce="(op) => op.value"
                :clearable="false"
                label="text"
                ref="upd"
              />
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group
              :label="$t('Note')"
              :invalid-feedback="$t('Note is required')"
              ref="note"
            >
              <b-form-textarea
                ref="note"
                :placeholder="$t('Enter note')"
                v-model="incObj.note"
                rows="2"
              ></b-form-textarea>
            </b-form-group>
          </b-col>
          <b-col md="12" v-if="showUserLinkField">
            <b-form-group :label="$t('User ID')">
              <v-select
                v-model="incObj.userID"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="iussUserOptions"
                :reduce="(user) => user.id"
                label="label"
                :loading="iussUsersLoading"
                :disabled="iussUsersLoading"
                :placeholder="$t('Select user')"
                :clearable="false"
              />
            </b-form-group>
          </b-col>

          <b-col class="mt-1" md="12">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="submit"
              variant="success"
              @click="Add()"
              block
              :disabled="request"
            >
              <b-spinner v-if="request" small type="grow" />
              <span v-else> {{ $t(sidebarButton) }} </span>
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-sidebar>

    <!-- v-model="visibility2" -->
    <b-sidebar
      id="sidebar-right"
      ref="mySidebar"
      bg-variant="white"
      v-model="loanHide"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">
          {{ $t(sidebarTitle) }}
        </h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="visibility2 = false"
        />
      </div>
      <b-form class="p-2" @submit.prevent>
        <b-row class="mt-1">
          <b-col md="12" class="mb-1 d-flex">
            <b-button
              v-for="t in typeOptions"
              :key="t.value"
              style="width: 50%"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              :variant="
                t.value == loanObj.loanType ? 'primary' : 'outline-primary'
              "
              :disabled="loanObj.id > 0"
              class="mr-1"
              @click="loanObj.loanType = t.value"
            >
              {{ $t(t.text) }}
            </b-button>
          </b-col>
          <b-col md="12" v-if="false">
            <b-form-group
              :label="$t('Date')"
              :invalid-feedback="$t('Date is required')"
              ref="loan_date"
            >
              <flat-pickr
                :disabled="loanObj.id > 0"
                ref="loan_date"
                :config="config"
                v-model="loanObj.date"
                class="form-control"
                :placeholder="$t('Select Date')"
              />
              <!-- @on-change="checkLoanDate()" -->
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group
              :label="$t('Amount')"
              :invalid-feedback="$t('Amount is required')"
              ref="loan_amount"
            >
              <b-form-input
                :disabled="loanObj.id > 0"
                ref="loan_amount"
                type="number"
                id="mc-first-name"
                :placeholder="$t('Enter amount')"
                v-model="loanObj.amount"
                @input="checkLoanAmount()"
                @focusout="setInstallment()"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              :label="$t('Return in months')"
              :invalid-feedback="$t('Invalid Month')"
              ref="loan_month"
            >
              <b-form-input
                :disabled="loanObj.id > 0"
                ref="loan_month"
                type="number"
                :placeholder="$t('Enter no. of months')"
                v-model="loanObj.months"
                @input="checkMonth()"
                @focusout="setInstallment()"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              :label="$t('Installment Amount')"
              :invalid-feedback="$t('Invalid Amount')"
              ref="return_amount"
            >
              <b-form-input
                :disabled="loanObj.id > 0"
                ref="return_amount"
                type="number"
                :placeholder="$t('Amount to return per month')"
                v-model="loanObj.monthlyInstallments"
                @input="checkRetAmount()"
                @focusout="setbyRetAmount()"
              />
            </b-form-group>
          </b-col>
          <!-- <b-col md="12">
            <b-form-group label="Deduct From" invalid-feedback="Invalid Amount">
              <v-select
                v-model="sameMonth"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="deductOption"
                label="text"
                :reduce="(opt) => opt.value"
                :clearable="false"
                placeholder="Select"
              />
            </b-form-group>
          </b-col> -->
          <b-col md="12">
            <label class="bv-no-focus-ring col-form-label pt-0">{{
              $t("Deduct From")
            }}</label>
          </b-col>

          <b-col md="6">
            <b-form-group label="" :invalid-feedback="$t('Invalid Amount')">
              <v-select
                v-model="loanMonth"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="months"
                label="text"
                :reduce="(opt) => opt.value"
                :clearable="false"
                :placeholder="$t('Select')"
                :disabled="loanObj.id > 0"
                @input="setInstallment()"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="" :invalid-feedback="$t('Invalid Amount')">
              <v-select
                v-model="loanYear"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="years"
                :clearable="false"
                :placeholder="$t('Select')"
                :disabled="loanObj.id > 0"
                @input="setInstallment()"
              />
            </b-form-group>
          </b-col>

          <b-table
            class="mt-50"
            :items="installData"
            :fields="installFields"
            show-empty
            responsive
            hover
            small
          >
            <template #head()="scope">
              <div>
                <span>{{ $t(scope.label) }}</span>
              </div>
            </template>
            <template #cell(month)="data">
              <b-badge variant="light-primary">
                {{ data.item.month }} {{ data.item.year }}
              </b-badge>
            </template>
            <template #cell(amount)="data">
              <b-badge variant="light-primary">
                Rs.{{ data.item.amount }}
              </b-badge>
              <b-badge
                v-if="data.item.isReturned"
                variant="light-success"
                class="ml-1"
              >
                {{ $t("Returned") }}
              </b-badge>
            </template>
          </b-table>

          <b-col class="mt-1" md="12" v-if="loanObj.id == 0">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="submit"
              variant="success"
              @click="AddLoan()"
              block
              :disabled="request"
            >
              <b-spinner v-if="request" small type="grow" />
              <span v-else> {{ $t(sidebarButton) }} </span>
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-sidebar>

    <b-sidebar
      id="sidebar-right"
      ref="mySidebar"
      bg-variant="white"
      v-model="visibility5"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">
          {{ $t(sidebarTitle) }}
        </h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="visibility5 = false"
        />
      </div>
      <b-form class="p-2" @submit.prevent>
        <b-row class="mt-1">
          <b-col md="12" v-if="false">
            <b-form-group
              :label="$t('Date')"
              :invalid-feedback="$t('Date is required')"
            >
              <flat-pickr
                :config="config"
                v-model="depoObj.date"
                class="form-control"
                :placeholder="$t('Select Date')"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group
              :label="$t('Deposit Amount')"
              :invalid-feedback="$t('Amount is required')"
              ref="depo_amount"
            >
              <b-form-input
                ref="depo_amount"
                type="number"
                id="mc-first-name"
                :placeholder="$t('Enter amount')"
                v-model="depoObj.amount"
                @input="checkDAmount()"
                @focusout="setDInstallment()"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              :label="$t('Deposit Months')"
              :invalid-feedback="$t('Invalid Month')"
              ref="depo_month"
            >
              <b-form-input
                ref="depo_month"
                type="number"
                :placeholder="$t('Enter no. of months')"
                v-model="depoObj.months"
                @input="checkDMonth()"
                @focusout="setDInstallment()"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              :label="$t('Amount')"
              :invalid-feedback="$t('Invalid Amount')"
              ref="depo_return"
            >
              <b-form-input
                ref="depo_return"
                type="number"
                :placeholder="$t('Amount to return per month')"
                v-model="depoObj.monthlyInstallments"
                @input="checkDRetAmount()"
                @focusout="setbyDReturn()"
              />
            </b-form-group>
          </b-col>

          <b-col md="12">
            <label class="bv-no-focus-ring col-form-label pt-0">{{
              $t("Deduct From")
            }}</label>
          </b-col>

          <b-col md="6">
            <b-form-group label="" :invalid-feedback="$t('Invalid Amount')">
              <v-select
                v-model="deductMonth"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="months"
                label="text"
                :reduce="(opt) => opt.value"
                :clearable="false"
                @input="GetDeductMonth()"
                :placeholder="$t('Select')"
              />

              <!-- <v-select
                v-model="sameMonth"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="deductOption"
                label="text"
                :reduce="(opt) => opt.value"
                :clearable="false"
                placeholder="Select"
              /> -->
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="" :invalid-feedback="$t('Invalid Amount')">
              <v-select
                v-model="deductYear"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="years"
                :clearable="false"
                :placeholder="$t('Select')"
              />
            </b-form-group>
          </b-col>

          <b-col md="12">
            <label class="bv-no-focus-ring col-form-label pt-0 pb-1">{{
              $t("Skip Months")
            }}</label>
          </b-col>
          <b-col md="12">
            <b-button
              class="btn-icon mr-50 mb-1"
              v-for="mon in months"
              :key="mon.value"
              :variant="skipped.includes(mon.value) ? 'danger' : 'primary'"
              @click="setSkip(mon.value)"
            >
              {{ mon.text.slice(0, 3) }}
            </b-button>
          </b-col>

          <b-col class="mt-1" md="12">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="submit"
              variant="success"
              @click="AddDeposit()"
              block
              :disabled="request"
            >
              <b-spinner v-if="request" small type="grow" />
              <span v-else> {{ $t(sidebarButton) }} </span>
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-sidebar>

    <b-sidebar
      id="sidebar-Fee"
      bg-variant="white"
      v-model="visibility3"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">{{ $t("Manage Allowances") }}</h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="visibility3 = false"
        />
      </div>

      <b-form class="p-2" @submit.prevent>
        <b-row class="d-flex align-items-center">
          <b-col md="5" class="pr-0">
            <b-form-group
              :label="$t('Title')"
              :invalid-feedback="$t('Title is required')"
            >
              <b-form-input
                ref="title_allow"
                v-model="allowObj.title"
                :placeholder="$t('Enter title')"
                @focusout="checkTitle()"
              >
              </b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="5">
            <b-form-group
              :label="$t('Amount')"
              :invalid-feedback="$t('Invalid Amount')"
            >
              <b-form-input
                ref="amount_allow"
                v-model="allowObj.amount"
                type="number"
                :placeholder="$t('Enter amount')"
                @input="checkAllAmount()"
              >
              </b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="2">
            <b-button
              variant="success"
              class="btn-icon mr-0 mr-sm-1 mb-1 mb-sm-0"
              @click="SaveAllow()"
              :disabled="saving"
            >
              <b-spinner v-if="saving" small type="grow" />
              <feather-icon v-else :icon="accIcon" />
            </b-button>
          </b-col>
        </b-row>
        <b-table
          class="mt-1"
          show-empty
          :items="allowances"
          :fields="allowFields"
          responsive
          hover
        >
          <template #head()="scope">
            <div>
              <span>{{ $t(scope.label) }}</span>
            </div>
          </template>
          <template #head(actions)="data">
            <div class="text-right">
              <span>{{ $t(data.label) }}</span>
            </div>
          </template>

          <template #cell(actions)="data">
            <div class="text-right">
              <b-button
                variant="primary"
                class="btn-icon mr-0 ml-sm-1 mb-1 mb-sm-0"
                @click="editAllow(data.item)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
              <b-button
                v-if="false"
                variant="outline-danger"
                class="btn-icon mr-0 ml-sm-1 mb-1 mb-sm-0"
                @click="deleteAllow(data.item.id)"
              >
                <feather-icon icon="Trash2Icon" />
              </b-button>
            </div>
          </template>
        </b-table>
      </b-form>
    </b-sidebar>

    <b-sidebar
      id="sidebar-Fee"
      bg-variant="white"
      v-model="visibility4"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">{{ $t("Manage Deductions") }}</h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="visibility4 = false"
        />
      </div>

      <b-form class="p-2" @submit.prevent>
        <b-row class="d-flex align-items-center">
          <b-col md="7" class="pr-0">
            <b-form-group
              :label="$t('Title')"
              :invalid-feedback="$t('Title is required')"
            >
              <b-form-input
                ref="title_ded"
                v-model="deductObj.title"
                :placeholder="$t('Enter title')"
                @focusout="checkDTitle()"
              >
              </b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="5">
            <b-form-group
              :label="$t('Amount')"
              :invalid-feedback="$t('Invalid Amount')"
            >
              <b-form-input
                ref="amount_ded"
                v-model="deductObj.amount"
                type="number"
                :placeholder="$t('Enter amount')"
                @input="checkDedAmount()"
              >
              </b-form-input>
            </b-form-group>
          </b-col>
          <b-col
            md="12"
            v-if="salaryLedgerObj && salaryLedgerObj.valueBool && linkObj"
          >
            <b-form-group
              :label="$t('Link with')"
              :invalid-feedback="$t('Account is required.')"
            >
              <v-select
                v-model="linkObj.valueNumber"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="coaAccounts"
                :reduce="(opt) => opt.id"
                :clearable="true"
                label="title"
                :placeholder="$t('Select account')"
                @input="setCOA"
              />
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-button
              variant="success"
              block
              class="btn-icon mt-1"
              @click="SaveDeduct()"
              :disabled="saving"
            >
              <b-spinner v-if="saving" small type="grow" />
              <span v-else>{{ $t("Save") }}</span>
            </b-button>
          </b-col>
        </b-row>
        <b-table
          class="mt-1"
          show-empty
          :items="deductions"
          :fields="allowFields"
          responsive
          hover
        >
          <template #head()="scope">
            <div>
              <span>{{ $t(scope.label) }}</span>
            </div>
          </template>
          <template #head(actions)="data">
            <div class="text-right">
              <span>{{ $t(data.label) }}</span>
            </div>
          </template>

          <template #cell(actions)="data">
            <div class="text-right">
              <b-button
                variant="primary"
                class="btn-icon mr-0 ml-sm-1 mb-1 mb-sm-0"
                @click="editDeduct(data.item)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
              <b-button
                v-if="false"
                variant="outline-danger"
                class="btn-icon mr-0 ml-sm-1 mb-1 mb-sm-0"
                @click="deleteDeduct(data.item.id)"
              >
                <feather-icon icon="Trash2Icon" />
              </b-button>
            </div>
          </template>
        </b-table>
      </b-form>
    </b-sidebar>

    <b-sidebar
      id="sidebar-Fee"
      bg-variant="white"
      v-model="visibility6"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
      width="480px"
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">{{ $t("Breakup Policy") }}</h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="visibility6 = false"
        />
      </div>

      <b-form class="p-2" @submit.prevent>
        <!-- Allowance Policy Section -->
        <div
          v-if="reportDomain === 'myskooliuss' || reportDomain === 'myskool'"
        >
          <h6 class="mb-2 font-weight-bold">{{ $t("Allowance Policy") }}</h6>
        </div>

        <b-row
          class="align-items-center mb-1 mb-sm-0"
          v-for="(item, index) in policySett"
          :key="index"
        >
          <b-col sm="6" class="pr-sm-0">
            <b-form-group
              v-if="item.allowanceTypeID == 0"
              label=""
              :invalid-feedback="$t('detail is required.')"
              class="mt-2"
            >
              <b-form-input placeholder="" v-model="salaryText" disabled />
            </b-form-group>
            <b-form-group
              v-else
              :label="$t('Allowance')"
              :invalid-feedback="$t('detail is required.')"
            >
              <v-select
                v-model="item.allowanceTypeID"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="allowances"
                :reduce="(op) => op.id"
                :selectable="(opt) => !SettIDs.includes(opt.id)"
                :clearable="false"
                label="title"
                @input="changeSettAllow()"
              />
            </b-form-group>
          </b-col>
          <b-col sm="3" class="pr-sm-0">
            <b-form-group
              :label="$t('Percentage')"
              :invalid-feedback="$t('Percentage is required.')"
              ref="sett_perc"
            >
              <b-form-input
                ref="sett_perc"
                type="number"
                :placeholder="$t('Enter percentage')"
                v-model="item.perc"
              />
            </b-form-group>
          </b-col>

          <b-col sm="3">
            <b-button
              v-if="index == policySett.length - 1"
              @click="addSett(item)"
              variant="primary"
              class="btn-icon rounded-circle mr-50"
            >
              <feather-icon icon="PlusIcon" />
            </b-button>
            <b-button
              v-if="policySett.length > 1 && item.allowanceTypeID != 0"
              @click="removeSett(item, index)"
              variant="outline-danger"
              class="btn-icon rounded-circle"
            >
              <feather-icon icon="XIcon" />
            </b-button>
          </b-col>
        </b-row>
        <b-row class="mt-1">
          <b-col md="12">
            <b-button
              variant="success"
              class="btn-icon"
              :disabled="savingSett"
              block
              @click="saveSett()"
            >
              <b-spinner v-if="savingSett" small type="grow" />
              <span v-else>{{ $t("Save") }}</span>
            </b-button>
          </b-col>
        </b-row>

        <!-- Deduction Policy Section -->
        <div
          v-if="reportDomain === 'myskooliuss' || reportDomain === 'myskool'"
          class="mt-4"
        >
          <h6 class="mb-2 font-weight-bold">{{ $t("Deduction Policy") }}</h6>

          <b-row
            class="align-items-center mb-1 mb-sm-0"
            v-for="(item, index) in deductionPolicySett"
            :key="'deduction-' + (item.id || index) + '-' + index"
          >
            <b-col sm="6" class="pr-sm-0">
              <b-form-group
                :label="$t('Deduction Type')"
                :invalid-feedback="$t('Deduction type is required.')"
              >
                <v-select
                  v-model="item.allowanceTypeID"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="deductionTypes"
                  :reduce="(op) => op.id"
                  :selectable="
                    (opt) => !deductionPolicySettIDs.includes(opt.id)
                  "
                  :clearable="false"
                  label="title"
                  @input="changeDeductionPolicySettAllow()"
                />
              </b-form-group>
            </b-col>
            <b-col sm="3" class="pr-sm-0">
              <b-form-group
                :label="$t('Percentage')"
                :invalid-feedback="$t('Percentage is required.')"
              >
                <b-form-input
                  v-model="item.perc"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  @input="changeDeductionPolicySettAllow()"
                />
              </b-form-group>
            </b-col>

            <b-col sm="3">
              <b-button
                v-if="index == deductionPolicySett.length - 1"
                @click="addDeductionPolicySett(item)"
                variant="primary"
                class="btn-icon rounded-circle mr-50"
              >
                <feather-icon icon="PlusIcon" />
              </b-button>
              <b-button
                v-if="deductionPolicySett.length > 1"
                @click="removeDeductionPolicySett(item, index)"
                variant="outline-danger"
                class="btn-icon rounded-circle"
              >
                <feather-icon icon="XIcon" />
              </b-button>
            </b-col>
          </b-row>

          <b-row class="mt-1">
            <b-col md="12">
              <b-button
                variant="success"
                class="btn-icon"
                :disabled="savingDeductionPolicySett"
                block
                @click="saveDeductionPolicySett()"
              >
                <b-spinner v-if="savingDeductionPolicySett" small type="grow" />
                <span v-else>{{ $t("Save Deduction Policy") }}</span>
              </b-button>
            </b-col>
          </b-row>
        </div>

        <!-- Contribution Policy Section -->
        <div
          v-if="reportDomain === 'myskooliuss' || reportDomain === 'myskool'"
          class="mt-4"
        >
          <h6 class="mb-2 font-weight-bold">{{ $t("Contribution Policy") }}</h6>

          <b-row
            class="align-items-center mb-1 mb-sm-0"
            v-for="(item, index) in contributionPolicySett"
            :key="'contribution-' + (item.id || index) + '-' + index"
          >
            <b-col sm="6" class="pr-sm-0">
              <b-form-group
                :label="$t('Contribution Type')"
                :invalid-feedback="$t('Contribution type is required.')"
              >
                <v-select
                  v-model="item.allowanceTypeID"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="contributionTypes"
                  :reduce="(op) => op.id"
                  :selectable="
                    (opt) => !contributionPolicySettIDs.includes(opt.id)
                  "
                  :clearable="false"
                  :disabled="isLockedContribution(item)"
                  label="title"
                  @input="changeContributionPolicySettAllow()"
                />
              </b-form-group>
            </b-col>
            <b-col sm="3" class="pr-sm-0">
              <b-form-group
                :label="$t('Percentage')"
                :invalid-feedback="$t('Percentage is required.')"
              >
                <b-form-input
                  v-model="item.perc"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  @input="changeContributionPolicySettAllow()"
                />
              </b-form-group>
            </b-col>

            <b-col sm="3">
              <b-button
                v-if="index == contributionPolicySett.length - 1"
                @click="addContributionPolicySett(item)"
                variant="primary"
                class="btn-icon rounded-circle mr-50"
              >
                <feather-icon icon="PlusIcon" />
              </b-button>
              <b-button
                v-if="contributionPolicySett.length > 1"
                @click="removeContributionPolicySett(item, index)"
                variant="outline-danger"
                class="btn-icon rounded-circle"
              >
                <feather-icon icon="XIcon" />
              </b-button>
            </b-col>
          </b-row>

          <b-row class="mt-1">
            <b-col md="12">
              <b-button
                variant="success"
                class="btn-icon"
                :disabled="savingContributionPolicySett"
                block
                @click="saveContributionPolicySett()"
              >
                <b-spinner
                  v-if="savingContributionPolicySett"
                  small
                  type="grow"
                />
                <span v-else>{{ $t("Save Contribution Policy") }}</span>
              </b-button>
            </b-col>
          </b-row>
        </div>
      </b-form>
    </b-sidebar>

    <b-sidebar
      id="sidebar-Fee"
      bg-variant="white"
      v-model="visibility7"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">{{ $t("Payroll Setting") }}</h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="visibility7 = false"
        />
      </div>

      <b-form class="p-2" @submit.prevent>
        <b-row>
          <b-col
            class="d-flex align-items-center justify-content-between mb-1"
            md="12"
          >
            <label class="bv-no-focus-ring h4 mb-0 p-0 mr-1 text-dark">{{
              $t("Payroll Share Policy")
            }}</label>
            <b-form-checkbox v-model="pCopy.valueBool" switch />
          </b-col>
          <b-col md="12" class="mt-1">
            <b-button
              variant="success"
              :disabled="savingSett"
              block
              @click="savePSett()"
            >
              <b-spinner v-if="savingSett" small type="grow" />
              <span v-else>{{ $t("Save") }}</span>
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-sidebar>

    <b-sidebar
      id="sidebar-right"
      ref="mySidebar"
      bg-variant="white"
      v-model="visibility8"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">{{ $t("Return Deposit") }}</h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="visibility8 = false"
        />
      </div>
      <b-form class="p-2" @submit.prevent>
        <b-row>
          <b-col cols="12">
            <b-form-group
              :label="$t('Amount')"
              :invalid-feedback="$t('Amount is required')"
              ref="ret_amount"
            >
              <b-form-input
                type="number"
                :placeholder="$t('Enter amount')"
                v-model="returnObj.amount"
                disabled
              />
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group
              :label="$t('Date')"
              :invalid-feedback="$t('Date is required')"
              ref="ret_date"
            >
              <flat-pickr
                :config="config"
                v-model="returnObj.date"
                class="form-control"
                :placeholder="$t('Select Date')"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group
              :label="$t('Cheque No.')"
              :invalid-feedback="$t('Cheque No is required.')"
              ref="ret_cheq"
            >
              <b-form-input
                v-model.trim="returnObj.cheque"
                :placeholder="$t('Enter cheque number')"
              />
            </b-form-group>
          </b-col>

          <b-col class="mt-1" md="12">
            <b-button
              variant="success"
              @click="saveReturn()"
              block
              :disabled="savingReturn"
            >
              <b-spinner v-if="savingReturn" small type="grow" />
              <span v-else>{{ $t("Save") }}</span>
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-sidebar>

    <form-wizard
      ref="wizard"
      color="#7367F0"
      :title="null"
      :subtitle="null"
      shape="square"
      finish-button-text="Submit"
      back-button-text="Previous"
      class="steps-transparent mb-2"
      @on-complete="formSubmitted"
    >
      <!-- @on-change="stepChanged" -->
      <!-- account detail tab   :before-change="validationForm"-->
      <tab-content :title="$t('Profile')" icon="feather icon-file-text">
        <!-- :before-change="validationForm" -->
        <b-row
          class="match-height"
          style="display: flex; align-items: center; justify-content: center"
        >
          <b-col md="8">
            <b-card class="">
              <b-row align-h="center">
                <!-- <b-col cols="4"> </b-col> -->
                <b-col class="ml-3" cols="4">
                  <b-form-group
                    class="text-center happiness-photo-group"
                    ref="picture"
                    label=" "
                    label-for="name-input"
                    :invalid-feedback="$t('Picture is required.')"
                  >
                    <div class="happiness-photo-slot">
                      <div
                        v-if="!plus && logoloading !== true"
                        class="happiness-photo-frame happiness-photo-frame--empty happiness-photo-frame--clickable"
                        @click="$refs.fileProfile.click()"
                      >
                        <feather-icon
                          icon="ImageIcon"
                          size="34"
                          class="text-muted"
                        />
                        <small class="text-muted mt-50 d-block">{{
                          $t("Click to upload")
                        }}</small>
                      </div>

                      <div
                        v-else-if="!plus && logoloading === true"
                        class="happiness-photo-frame happiness-photo-frame--empty"
                      >
                        <b-spinner
                          variant="primary"
                          type="grow"
                          :label="$t('Spinning')"
                        />
                      </div>

                      <div v-else class="happiness-photo-frame">
                        <img
                          class="happiness-photo-img"
                          :src="staffPicturePreview"
                          :alt="$t('Staff photo')"
                          referrerpolicy="no-referrer"
                          @error="onDrivePhotoPreviewError($event, plus)"
                          @click="logoloading !== true && $refs.fileProfile.click()"
                        />
                        <div
                          v-if="logoloading === true"
                          class="happiness-photo-upload-overlay"
                        >
                          <b-spinner variant="primary" type="grow" />
                        </div>
                        <b-button
                          v-if="logoloading !== true"
                          :class="
                            showHappinessFoundation
                              ? 'happiness-photo-delete-btn'
                              : 'staff-photo-delete-btn'
                          "
                          :variant="
                            showHappinessFoundation ? 'secondary' : 'danger'
                          "
                          @click.stop="deleteLogo()"
                        >
                          <feather-icon icon="Trash2Icon" size="20" />
                        </b-button>
                      </div>

                      <input
                        type="file"
                        id="fileProfile"
                        hidden
                        ref="fileProfile"
                        @change="selectlogo()"
                        accept="image/*"
                      />
                    </div>
                  </b-form-group>
                </b-col>
              </b-row>
              <hr />

              <b-row>
                <b-col md="6" v-if="reportDomain == 'myskoolhpgs'">
                  <b-form-group
                    :label="$t('Employee Code') + ' *'"
                    :invalid-feedback="$t('Employee code is required.')"
                    ref="emp_code"
                  >
                    <b-form-input
                      type="number"
                      v-model="myObj.empCode"
                      :placeholder="$t('Enter employee code')"
                      :disabled="localID > 0 ? formRights.hidePayroll : false"
                      @focusout="CheckECode()"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6" v-if="isHeadOffice">
                  <b-form-group
                    :label="$t('Campus') + ' *'"
                    :invalid-feedback="$t('Campus is required.')"
                    ref="camp"
                  >
                    <v-select
                      v-model="myObj.campusID"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="campuses"
                      :clearable="false"
                      :reduce="(val) => val.id"
                      label="branch"
                      :placeholder="$t('Select campus')"
                      @input="CheckCampus()"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="12">
                  <b-form-group
                    :label="$t('Report To')"
                    :invalid-feedback="$t('Please select reporting staff.')"
                  >
                    <v-select
                      multiple
                      :closeOnSelect="false"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      v-model="myObj.reportTo"
                      :options="reportToOptions"
                      :reduce="(val) => val"
                      label="name"
                      :clearable="true"
                      :loading="reportToLoading"
                      :filterable="false"
                      :placeholder="
                        $t('Search and select staff members to report to')
                      "
                      @search="searchReportToStaff"
                      @input="onReportToChange"
                      :key="reportToKey"
                      ref="reportToSelect"
                    >
                      <template #option="{ name, designation, department }">
                        <div class="staff-option-item">
                          <div class="staff-main-info">
                            <div class="staff-name-section">
                              <strong class="staff-name">{{ name }}</strong>
                              <span class="staff-icon">
                                <feather-icon
                                  icon="UserIcon"
                                  size="12"
                                  class="detail-icon"
                                />
                              </span>
                            </div>
                            <div class="staff-details-compact">
                              <span
                                v-if="designation"
                                class="detail-item-compact staff-designation"
                              >
                                <feather-icon
                                  icon="BriefcaseIcon"
                                  size="10"
                                  class="detail-icon"
                                />
                                <strong>{{ designation }}</strong>
                              </span>
                              <span
                                v-if="department"
                                class="detail-item-compact"
                              >
                                <feather-icon
                                  icon="BuildingIcon"
                                  size="10"
                                  class="detail-icon"
                                />
                                <strong>{{ department }}</strong>
                              </span>
                            </div>
                          </div>
                        </div>
                      </template>
                      <template
                        #selected-option="{ name, designation, department }"
                      >
                        <div class="d-flex flex-column">
                          <strong>{{ name }}</strong>
                          <small class="text-muted">
                            <span v-if="designation">{{ designation }}</span>
                            <span v-if="designation && department"> • </span>
                            <span v-if="department">{{ department }}</span>
                          </small>
                        </div>
                      </template>
                    </v-select>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="6">
                  <b-form-group
                    :label="$t('Name') + ' *'"
                    :invalid-feedback="$t('Staff name is required.')"
                  >
                    <b-form-input
                      :placeholder="$t('Enter name')"
                      ref="name"
                      v-model.trim="myObj.name"
                      @focusout="CheckName()"
                    />
                  </b-form-group>
                </b-col>

                <!-- moved here for yakubov -->
                <b-col md="6" v-if="reportDomain == 'myskoolyakubov'">
                  <b-form-group
                    :label="$t('Surname')"
                    :invalid-feedback="$t('Surname is required')"
                  >
                    <b-form-input
                      :placeholder="$t('Enter surname')"
                      v-model="myObj.surname"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group
                    :label="$t('Father name')"
                    :invalid-feedback="$t('Father name is required')"
                  >
                    <b-form-input
                      ref="fname"
                      :placeholder="$t('Enter father name')"
                      v-model.trim="myObj.father_name"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6" v-if="reportDomain != 'myskoolyakubov'">
                  <b-form-group
                    :label="$t('Surname')"
                    :invalid-feedback="$t('Surname is required')"
                  >
                    <b-form-input
                      :placeholder="$t('Enter surname')"
                      v-model="myObj.surname"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group
                    :label="$t('Spouse name')"
                    :invalid-feedback="$t('Spouse name is required.')"
                  >
                    <b-form-input
                      ref="spouse"
                      :placeholder="$t('Enter spouse name')"
                      v-model="myObj.spouse"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group
                    :label="$t('Gender')"
                    :invalid-feedback="$t('Gender is required')"
                  >
                    <v-select
                      v-model="myObj.gender"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="genders"
                      :clearable="false"
                      input-id="categoryId"
                      ref="category"
                      @change="checkCategory()"
                      :disabled="dataloading"
                      :placeholder="$t('Select gender')"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group :label="$t('Date of Birth')">
                    <flat-pickr
                      :config="config"
                      v-model="myObj.date_of_birth"
                      class="form-control"
                      :placeholder="$t('Select date of birth')"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group
                    :label="$t('Contact') + ' *'"
                    :invalid-feedback="$t('Contact number is required')"
                  >
                    <vue-tel-input
                      v-model="myObj.contact_number"
                      :inputOptions="vueTelOptions"
                      :defaultCountry="$store.state.countryCode"
                      @input="CheckSContact"
                    ></vue-tel-input>
                    <small class="mr-1 text-danger" v-if="sPhoneerror">{{
                      $t("Phone number is required")
                    }}</small>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group
                    :label="$t('CNIC no')"
                    :invalid-feedback="$t('CNIC no is required.')"
                  >
                    <b-form-input
                      placeholder="XXXXX-XXXXXXX-X"
                      v-model="myObj.cnic"
                      v-mask="'#####-#######-#'"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group :label="$t('Qualification')">
                    <div class="d-flex align-items-center">
                      <v-select
                        v-model="myObj.qualification"
                        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                        :options="qualificationOptions"
                        :clearable="true"
                        :disabled="dataloading"
                        :placeholder="$t('Select qualification')"
                        class="flex-grow-1"
                      />
                      <b-button
                        variant="flat-primary"
                        class="btn-icon ml-50"
                        @click="openQualificationSidebar"
                        v-b-tooltip.hover
                        :title="$t('Manage qualifications')"
                      >
                        <feather-icon icon="EditIcon" />
                      </b-button>
                    </div>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group :label="$t('Certificate')">
                    <b-form-input
                      :placeholder="$t('Enter certificate')"
                      v-model="myObj.certificate"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group
                    :label="$t('Department') + ' *'"
                    :invalid-feedback="$t('department is required.')"
                    ref="dep"
                  >
                    <v-select
                      v-model="myObj.departmentID"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="deptOptions"
                      :reduce="(opt) => Number(opt.id)"
                      label="name"
                      :clearable="false"
                      :disabled="dataloading"
                      :placeholder="$t('Select department')"
                      @input="setDepTimings()"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group
                    :label="$t('Designation')"
                    :invalid-feedback="$t('designation is required.')"
                    ref="design"
                    v-if="showDesignationField"
                  >
                    <v-select
                      v-model="myObj.designation"
                      :options="designationOptions"
                      :reduce="(opt) => opt.name"
                      label="name"
                      :clearable="false"
                      :disabled="dataloading || !myObj.departmentID"
                      :placeholder="
                        myObj.departmentID
                          ? $t('Select designation')
                          : $t('Select department first')
                      "
                      @input="setDesignation()"
                    />

                    <!-- @focusout="CheckDesignation()" -->
                  </b-form-group>
                  <b-form-group
                    :label="$t('Designation')"
                    :invalid-feedback="$t('designation is required.')"
                    ref="design"
                    v-if="!showDesignationField"
                  >
                    <b-form-input
                      :placeholder="$t('Enter designation')"
                      v-model="myObj.designation"
                    />

                    <!-- @focusout="CheckDesignation()" -->
                  </b-form-group>
                </b-col>

                <b-col md="6">
                  <b-form-group
                    :label="$t('Stream')"
                    ref="stream"
                  >
                    <div class="d-flex align-items-center">
                      <v-select
                        v-model="myObj.streamID"
                        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                        :options="streamOptions"
                        :reduce="(opt) => opt.id"
                        label="name"
                        :clearable="true"
                        :disabled="dataloading"
                        :placeholder="$t('Select stream')"
                        class="flex-grow-1"
                      />
                      <b-button
                        variant="flat-primary"
                        class="btn-icon ml-50"
                        @click="openStreamSidebar"
                        v-b-tooltip.hover
                        :title="$t('Manage Streams')"
                      >
                        <feather-icon icon="EditIcon" />
                      </b-button>
                    </div>
                  </b-form-group>
                </b-col>

                <b-col md="6">
                  <b-form-group
                    :label="$t('Biometric card no.')"
                    :invalid-feedback="$t('Card no. is required.')"
                  >
                    <b-form-input
                      :placeholder="$t('Enter biometric card')"
                      v-model="myObj.card_number"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group
                    :label="$t('Experience')"
                    :invalid-feedback="$t('Experience is required.')"
                  >
                    <b-form-input
                      :placeholder="$t('Enter experience')"
                      v-model="myObj.experience"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group
                    :label="$t('Note')"
                    :invalid-feedback="$t('Note is required.')"
                  >
                    <b-form-input
                      :placeholder="$t('Enter note')"
                      v-model="myObj.note"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6" v-if="rfObj && rfObj.valueBool">
                  <b-form-group
                    :label="$t('Biometric ID')"
                    :invalid-feedback="$t('Biometric ID is required.')"
                  >
                    <b-form-input
                      :placeholder="$t('Enter Biometric ID')"
                      v-model="myObj.rfID"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6" v-if="showUserLinkField">
                  <b-form-group :label="$t('User ID')">
                    <v-select
                      v-model="myObj.userID"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="iussUserOptions"
                      :reduce="(user) => user.id"
                      label="label"
                      :loading="iussUsersLoading"
                      :disabled="iussUsersLoading"
                      :placeholder="$t('Select user')"
                      :clearable="false"
                    />
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col md="6">
                  <b-form-group
                    :label="
                      $t(
                        reportDomain === 'myskooliuss'
                          ? 'Campus Email'
                          : 'Personal Email'
                      )
                    "
                  >
                    <b-form-input
                      :placeholder="
                        $t(
                          reportDomain === 'myskooliuss'
                            ? 'Enter campus email'
                            : 'Enter personal email'
                        )
                      "
                      v-model="myObj.personalEmail"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group
                    :label="
                      $t(
                        reportDomain === 'myskooliuss'
                          ? 'Head Office Email'
                          : 'Work Email'
                      )
                    "
                  >
                    <b-form-input
                      :placeholder="
                        $t(
                          reportDomain === 'myskooliuss'
                            ? 'Enter head office email'
                            : 'Enter work email'
                        )
                      "
                      v-model="myObj.workEmail"
                    />
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col md="6">
                  <b-form-group
                    :label="
                      $t(
                        reportDomain === 'myskooliuss' ||
                          reportDomain === 'myskool'
                          ? 'Last Salary Drawn'
                          : 'Previous Salary'
                      )
                    "
                  >
                    <b-form-input
                      type="number"
                      :placeholder="$t('Enter last salary drawn')"
                      v-model="myObj.previousSalary"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group
                    :label="$t('Sitting Campus')"
                    v-if="salaryCampusObj && salaryCampusObj.valueBool"
                  >
                    <v-select
                      v-model="myObj.salaryCampus"
                      :options="campuses"
                      label="branch"
                      :reduce="(val) => val.id"
                      :clearable="false"
                      :placeholder="$t('Select Campus')"
                    />
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col md="12">
                  <b-form-group
                    :label="$t('Current Address')"
                    :invalid-feedback="$t('Contact number is required')"
                  >
                    <b-form-input
                      :placeholder="$t('Enter current address')"
                      v-model="myObj.address_current"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="12">
                  <b-form-group
                    :label="$t('Permanent Address')"
                    :invalid-feedback="$t('Contact number is required')"
                  >
                    <b-form-input
                      :placeholder="$t('Enter permanent address')"
                      v-model="myObj.address_permanent"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row v-if="isMyskoolOrIuss">
                <b-col md="6">
                  <b-form-group :label="$t('Province')">
                    <v-select
                      v-model="myObj.province"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="pakistanProvinces"
                      :clearable="true"
                      :placeholder="$t('Select province')"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group :label="$t('City')">
                    <b-form-input
                      v-model.trim="myObj.city"
                      :placeholder="$t('Enter city')"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="12">
                  <b-form-group :label="$t('LinkedIn Profile')">
                    <b-form-input
                      v-model.trim="myObj.linkedinProfile"
                      placeholder="https://linkedin.com/in/username"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group :label="$t('Salary Slip')">
                    <b-button
                      variant="outline-primary"
                      block
                      :disabled="salarySlipLoading"
                      @click="$refs.staffSalarySlipInput.click()"
                    >
                      <b-spinner
                        v-if="salarySlipLoading"
                        type="grow"
                        small
                        label="Loading..."
                      />
                      <span v-else>
                        {{
                          myObj.salarySlip
                            ? $t("Replace Salary Slip")
                            : $t("Upload Salary Slip")
                        }}
                      </span>
                    </b-button>
                    <input
                      ref="staffSalarySlipInput"
                      type="file"
                      hidden
                      accept="image/*,.pdf"
                      :disabled="salarySlipLoading"
                      @change="uploadSalarySlip()"
                    />
                    <div
                      v-if="myObj.salarySlip"
                      class="d-flex align-items-center mt-50"
                    >
                      <a
                        :href="myObj.salarySlip"
                        target="_blank"
                        class="mr-1 text-primary"
                      >
                        {{ $t("View salary slip") }}
                      </a>
                      <feather-icon
                        icon="Trash2Icon"
                        size="18"
                        class="text-danger cursor-pointer"
                        @click="removeSalarySlip()"
                      />
                    </div>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group :label="$t('CV')">
                    <b-button
                      variant="outline-primary"
                      block
                      :disabled="cvLoading"
                      @click="$refs.staffCvFileInput.click()"
                    >
                      <b-spinner
                        v-if="cvLoading"
                        type="grow"
                        small
                        label="Loading..."
                      />
                      <span v-else>
                        {{ myObj.cv ? $t("Replace CV") : $t("Upload CV") }}
                      </span>
                    </b-button>
                    <input
                      ref="staffCvFileInput"
                      type="file"
                      hidden
                      accept="image/*,.pdf,.doc,.docx"
                      :disabled="cvLoading"
                      @change="uploadStaffCv()"
                    />
                    <div
                      v-if="myObj.cv"
                      class="d-flex align-items-center mt-50"
                    >
                      <a
                        :href="myObj.cv"
                        target="_blank"
                        class="mr-1 text-primary"
                      >
                        {{ $t("View CV") }}
                      </a>
                      <feather-icon
                        icon="Trash2Icon"
                        size="18"
                        class="text-danger cursor-pointer"
                        @click="removeStaffCv()"
                      />
                    </div>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="4">
                  <b-form-group
                    :label="$t('Appointed on')"
                    :invalid-feedback="$t('Appointment date is required')"
                    ref="appoint_date"
                  >
                    <flat-pickr
                      :config="config"
                      v-model="myObj.appointedOn"
                      class="form-control"
                      :disabled="localID > 0 ? formRights.hidePayroll : false"
                      :placeholder="$t('Select appointment date.')"
                      @on-change="setLeaveAllowed()"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group
                    :label="$t('Leaving Date')"
                    :invalid-feedback="$t('Date is required')"
                  >
                    <flat-pickr
                      :config="config"
                      v-model="myObj.leaving_date"
                      class="form-control"
                      :disabled="localID > 0 ? formRights.hidePayroll : false"
                      :placeholder="$t('Select Date')"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group
                    :label="$t('Rejoining Date')"
                    :invalid-feedback="$t('Date is required')"
                  >
                    <flat-pickr
                      :config="config"
                      v-model="myObj.rejoining_date"
                      class="form-control"
                      :disabled="localID > 0 ? formRights.hidePayroll : false"
                      :placeholder="$t('Select Date')"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group
                    :label="$t('Resignation Date')"
                    :invalid-feedback="$t('Date is required')"
                  >
                    <flat-pickr
                      :config="config"
                      v-model="myObj.resginationDate"
                      class="form-control"
                      :disabled="localID > 0 ? formRights.hidePayroll : false"
                      :placeholder="$t('Select Date')"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group
                    :label="$t('Availability')"
                    :invalid-feedback="$t('Availability is required.')"
                  >
                    <v-select
                      v-model="myObj.availibility"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="availibility"
                      :clearable="false"
                      input-id="categoryId"
                      ref="avail"
                      :placeholder="$t('Your availability')"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4" v-if="myObj.availibility == 'Part Time'">
                  <b-form-group
                    :label="$t('Part Timer Days')"
                    :invalid-feedback="$t('Part Timer Days is required.')"
                  >
                    <b-form-input
                      type="number"
                      :placeholder="$t('Enter working days.')"
                      v-model="myObj.partTimerDays"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <label
                    class="bv-no-focus-ring col-form-label d-flex align-items-center"
                    style="margin-top: 25px"
                    >{{ $t("Is Coordinator?") }}
                    <b-form-checkbox
                      v-model="myObj.isCoordinator"
                      switch
                      class="ml-1"
                    />
                  </label>
                </b-col>
              </b-row>

              <b-row v-if="reportDomain === 'myskooliuss'">
                <b-col md="12">
                  <b-divider class="my-2" />
                </b-col>
                <b-col md="12" class="mb-2">
                  <h5 class="mb-0 text-primary font-weight-bold">
                    {{ $t("Deduction Preferences") }}
                  </h5>
                </b-col>
                <b-col
                  class="d-flex align-items-center justify-content-between"
                  md="6"
                >
                  <label class="bv-no-focus-ring mb-0 p-0 mr-1 text-dark">
                    {{ $t("EOBI Deduct") }}
                  </label>
                  <b-form-checkbox v-model="myObj.eobiDeduct" switch />
                </b-col>
                <b-col
                  class="d-flex align-items-center justify-content-between"
                  md="6"
                >
                  <label class="bv-no-focus-ring mb-0 p-0 mr-1 text-dark">
                    {{ $t("PF Deduct") }}
                  </label>
                  <b-form-checkbox v-model="myObj.pfDeduct" switch />
                </b-col>
                <b-col
                  md="4"
                  v-if="
                    reportDomain == 'myskoolhpgs' &&
                    $store.state.userData.cId == 1
                  "
                >
                  <label
                    class="bv-no-focus-ring col-form-label pt-0 d-flex align-items-center"
                    >{{ $t("Hold Salary") }}
                    <b-form-checkbox
                      v-model="myObj.holdSalary"
                      switch
                      class="ml-1"
                    />
                  </label>
                </b-col>
              </b-row>
            </b-card>
          </b-col>

          <!-- Children Information Card -->
          <b-col
            md="8"
            v-if="reportDomain === 'myskooliuss' || reportDomain === 'myskool'"
          >
            <b-card class="">
              <div
                class="d-flex justify-content-between align-items-center mb-2"
              >
                <h3 class="mt-1 mb-0">{{ $t("Children Information") }}</h3>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  @click="openChildrenSelector"
                  class="d-flex align-items-center"
                >
                  <feather-icon icon="UsersIcon" size="16" class="mr-1" />
                  {{ $t("Select Children") }}
                </b-button>
              </div>
              <hr />

              <!-- Children List -->
              <b-row
                class="mb-1"
                v-for="(child, index) in myObj.children"
                :key="index"
              >
                <b-col md="2" class="">
                  <b-form-group :label="$t('Profile Img')">
                    <div class="child-avatar-container">
                      <div class="avatar-wrapper">
                        <b-avatar
                          :src="child.image"
                          :text="getInitials(child.name)"
                          size="60px"
                          class="child-avatar"
                        />
                        <b-button
                          variant="primary"
                          size="sm"
                          class="avatar-upload-btn"
                          @click="handleChildAvatarUpload(index)"
                        >
                          <feather-icon icon="CameraIcon" size="14" />
                        </b-button>
                      </div>
                    </div>
                  </b-form-group>
                </b-col>

                <b-col md="3" class="">
                  <b-form-group
                    :label="$t('Child ID')"
                    :invalid-feedback="$t('Child ID is required.')"
                  >
                    <b-form-input
                      v-model="child.sID"
                      :placeholder="$t('Enter child ID')"
                      type="number"
                    />
                  </b-form-group>
                </b-col>

                <b-col md="3" class="">
                  <b-form-group
                    :label="$t('Name')"
                    :invalid-feedback="$t('Name is required')"
                  >
                    <b-form-input
                      v-model="child.name"
                      :placeholder="$t('Enter child name')"
                    />
                  </b-form-group>
                </b-col>

                <b-col md="4" class="">
                  <b-form-group
                    :label="$t('Class')"
                    :invalid-feedback="$t('Class is required')"
                  >
                    <b-form-input
                      v-model="child.className"
                      :placeholder="$t('Enter class')"
                    />
                  </b-form-group>
                </b-col>

                <b-col md="12" class="text-center">
                  <b-button
                    v-if="index == myObj.children.length - 1"
                    @click="addChild"
                    variant="primary"
                    class="btn-icon rounded-circle mr-50"
                  >
                    <feather-icon icon="PlusIcon" />
                  </b-button>
                  <b-button
                    v-if="myObj.children.length > 1"
                    @click="removeChild(index)"
                    variant="outline-danger"
                    class="btn-icon rounded-circle"
                  >
                    <feather-icon icon="XIcon" />
                  </b-button>
                </b-col>
              </b-row>
            </b-card>
          </b-col>

          <b-col md="8">
            <b-card class="">
              <h3 class="mt-1">{{ $t("Emergency Information") }}</h3>
              <hr />
              <b-row>
                <b-col md="12">
                  <b-form-group
                    :label="$t('Name')"
                    :invalid-feedback="$t('Name is required')"
                  >
                    <b-form-input
                      ref="emerName"
                      :placeholder="$t('Enter name')"
                      v-model="myObj.emergency_contact_person"
                    />
                    <!-- @focusout="CheckEmerName()" -->
                  </b-form-group>
                </b-col>

                <b-col md="12">
                  <b-form-group
                    :label="$t('Relation')"
                    :invalid-feedback="$t('Relation is required.')"
                  >
                    <b-form-input
                      ref="emerRel"
                      :placeholder="$t('Enter relation.')"
                      v-model="myObj.emergency_contact_relation"
                    />
                    <!-- @focusout="CheckEmerRelation()" -->
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col md="12">
                  <b-form-group
                    :label="$t('Emergency contact')"
                    :invalid-feedback="$t('Emergency Contact is required.')"
                  >
                    <vue-tel-input
                      v-model="myObj.emergency_number"
                      :inputOptions="emerOpt"
                      :defaultCountry="$store.state.countryCode"
                    ></vue-tel-input>
                    <!-- @blur="CheckEmerContact()" -->
                    <small class="mr-1 text-danger" v-if="emerPhoneerror">{{
                      $t("Phone number is required.")
                    }}</small>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row> </b-row>
            </b-card>
          </b-col>
          <b-col md="8" v-if="localID > 0 ? !formRights.hidePayroll : true">
            <b-card class="">
              <div class="d-flex justify-content-between">
                <h3 class="">{{ $t("Timings") }}</h3>
                <div>
                  <b-button
                    v-if="localID > 0"
                    variant="outline-primary"
                    class="btn-icon mr-50"
                    size="sm"
                    @click="openGraceTimeModal()"
                  >
                    {{ $t("Grace Time") }}
                  </b-button>
                  <b-form-checkbox
                    v-model="sameDays"
                    switch
                    inline
                    v-b-tooltip.hover.bottomright
                    :title="$t('Everyday schedule same as monday')"
                    @input="setDays(timingInfo[0])"
                    class="mr-0"
                  />
                </div>
              </div>
              <hr />
              <b-row
                class="mb-1"
                v-for="(item, index) in timingInfo"
                :key="index"
              >
                <b-col md="3" class="">
                  <b-form-group
                    :invalid-feedback="$t('Day is required.')"
                    ref="day"
                  >
                    <div
                      class="d-flex align-items-center justify-content-between pb-50"
                    >
                      <label class="bv-no-focus-ring col-form-label p-0"
                        >{{ $t("Day") }}
                      </label>
                      <b-form-checkbox
                        v-model="item.dayOff"
                        switch
                        inline
                        v-b-tooltip.hover.bottomright
                        :title="$t('Day Off')"
                        class="custom-control-danger mr-0 ml-1"
                      />
                    </div>
                    <v-select
                      v-model="item.day"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="daysList"
                      :reduce="(op) => op.value"
                      :selectable="(opt) => !currDays.includes(opt.value)"
                      :clearable="false"
                      label="text"
                      @input="changeDay()"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="3" class="">
                  <b-form-group
                    :label="$t('Reporting Time')"
                    :invalid-feedback="$t('Reporting Time is required.')"
                    ref="rpTime"
                  >
                    <flat-pickr
                      v-model="item.reportingTime"
                      class="form-control"
                      :config="{
                        enableTime: true,
                        noCalendar: true,
                        altFormat: 'h:i K',
                        altInput: true,
                        dateFormat: 'Y-m-d\\TH:i:S',
                        defaultHour: 8,
                      }"
                      :disabled="item.dayOff"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="3" class="">
                  <b-form-group
                    :label="$t('Joins at')"
                    :invalid-feedback="$t('Joins at is required.')"
                    ref="join"
                  >
                    <flat-pickr
                      v-model="item.timeIn"
                      class="form-control"
                      :config="{
                        enableTime: true,
                        noCalendar: true,
                        altFormat: 'h:i K',
                        altInput: true,
                        dateFormat: 'Y-m-d\\TH:i:S',
                        defaultHour: 8,
                      }"
                      :disabled="item.dayOff"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="3" class="">
                  <b-form-group
                    :label="$t('Leaves at')"
                    :invalid-feedback="$t('Leaves at is required.')"
                    ref="leave"
                  >
                    <flat-pickr
                      v-model="item.timeOut"
                      class="form-control"
                      :config="{
                        enableTime: true,
                        noCalendar: true,
                        altFormat: 'h:i K',
                        altInput: true,
                        dateFormat: 'Y-m-d\\TH:i:S',
                        defaultHour: 13,
                      }"
                      :disabled="item.dayOff"
                      @on-close="setDays(item)"
                    />
                  </b-form-group>
                </b-col>

                <b-col md="12" class="text-center">
                  <b-button
                    v-if="
                      index == timingInfo.length - 1 &&
                      timingInfo.length < daysList.length
                    "
                    @click="AddVal()"
                    variant="primary"
                    class="btn-icon rounded-circle mr-50"
                  >
                    <feather-icon icon="PlusIcon" />
                  </b-button>
                  <b-button
                    v-if="timingInfo.length > 1"
                    @click="removeVal(item, index)"
                    variant="outline-danger"
                    class="btn-icon rounded-circle"
                  >
                    <feather-icon icon="XIcon" />
                  </b-button>
                </b-col>
              </b-row>
            </b-card>
          </b-col>
        </b-row>
      </tab-content>

      <tab-content
        :title="$t('Classes')"
        icon="feather icon-smartphone"
        v-if="localID > 0"
      >
        <b-row class="mb-1">
          <b-col md="12">
            <b-media vertical-align="center">
              <template #aside>
                <b-avatar
                  size="44"
                  :src="staffObjPicturePreview"
                  variant="light-primary"
                />
              </template>

              <b-link class="font-weight-bold d-block text-nowrap mb-25">
                <b-badge variant="light-primary">
                  {{ myObj.name }}
                </b-badge>
              </b-link>
              <small class="text-muted">
                <b-badge variant="light-primary">
                  {{ myObj.designation }}
                </b-badge>
              </small>
            </b-media>
          </b-col>
        </b-row>
        <b-row class="match-height">
          <!-- align-h="center"-->
          <b-col md="12">
            <b-card class="">
              <h3 class="mt-1">{{ $t("Teaching Classes") }}</h3>
              <hr />
              <b-row>
                <b-col md="3">
                  <b-form-group
                    :label="$t('Class')"
                    :invalid-feedback="$t('Class is required')"
                    ref="tcID"
                  >
                    <v-select
                      v-model="teachingClassObj.clsID"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="classesOptions"
                      :clearable="false"
                      :reduce="(val) => val.id"
                      label="name"
                      input-id="id"
                      ref="tcID"
                      @input="FillSections()"
                      @search:blur="CheckTcID()"
                      :disabled="dataloading"
                      :placeholder="$t('Select class')"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="3">
                  <b-form-group
                    :label="$t('Section')"
                    :invalid-feedback="$t('Section is required')"
                    ref="tSection"
                  >
                    <!-- v-model="teachingClassObj.sections" -->
                    <v-select
                      ref="tSection"
                      multiple
                      :closeOnSelect="false"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      v-model="selectedSections"
                      :options="admittedSections"
                      :reduce="(val) => val.id"
                      label="section"
                      :clearable="false"
                      :disabled="dataloading"
                      @input="setSections()"
                      @search:blur="CheckTSection()"
                      :placeholder="$t('Select section')"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group
                    :label="$t('Subject')"
                    :invalid-feedback="$t('Subject is required.')"
                    ref="tSubjects"
                  >
                    <v-select
                      ref="tSubjects"
                      multiple
                      :closeOnSelect="false"
                      v-model="selectedSubs"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="subjects"
                      :reduce="(val) => val.id"
                      label="subject"
                      :clearable="false"
                      :disabled="dataloading"
                      @input="setSubjects()"
                      @search:blur="CheckTSubject()"
                      :placeholder="$t('Select assigned subject.')"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4" v-if="false">
                  <b-form-group
                    :label="$t('Subject')"
                    :invalid-feedback="$t('Subject is required.')"
                    ref="tSubjects"
                  >
                    <v-select
                      ref="tSubjects"
                      multiple
                      :closeOnSelect="false"
                      v-model="teachingClassObj.subjects"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="subjects"
                      :reduce="(val) => val.id"
                      label="subject"
                      :clearable="false"
                      :disabled="dataloading"
                      @input="CheckTSubject()"
                      :placeholder="$t('Select assigned subject.')"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="1">
                  <b-form-group label="" class="mt-2">
                    <b-button
                      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                      class="btn-icon"
                      variant="success"
                      @click="assign()"
                      :disabled="assigning"
                      block
                    >
                      <b-spinner v-if="assigning" small />
                      <span v-else>{{ $t("Save") }}</span>
                    </b-button>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col>
                  <b-table
                    class="mt-1"
                    ref="selectableTable"
                    :items="gridData"
                    :fields="fields2"
                    responsive
                    hover
                    show-empty
                    small
                  >
                    <template #head()="scope">
                      <div>
                        <span>{{ $t(scope.label) }}</span>
                      </div>
                    </template>
                    <template #cell(subjects)="data">
                      <b-badge
                        variant="light-primary"
                        v-for="sub in data.item.subjects"
                        :key="sub.id"
                        class="m-25"
                      >
                        <span>{{ sub.subject }}</span>
                        <feather-icon
                          icon="XIcon"
                          class="ml-50"
                          @click="removeSingle(data.item, sub)"
                        />
                      </b-badge>
                    </template>
                    <template #cell(actions)="data">
                      <div class="d-flex">
                        <template v-if="ctObj && ctObj.valueBool">
                          <b-button
                            v-if="data.item.isClassTeacher"
                            variant="outline-danger"
                            class="btn-icon mr-1"
                            @click="setTeacher(data.item, false)"
                            v-b-tooltip.hover.left
                            :title="$t('Remove as Class Teacher')"
                          >
                            <feather-icon icon="XIcon" />
                          </b-button>
                          <b-button
                            v-else
                            variant="outline-success"
                            class="btn-icon mr-1"
                            @click="setTeacher(data.item, true)"
                            v-b-tooltip.hover.left
                            :title="$t('Mark as Class Teacher')"
                          >
                            <feather-icon icon="CheckIcon" />
                          </b-button>
                        </template>
                        <b-button
                          variant="outline-danger"
                          class="btn-icon"
                          @click="DeleteSub(data.item)"
                          v-b-tooltip.hover.left
                          :title="$t('Delete')"
                        >
                          <feather-icon icon="Trash2Icon" />
                        </b-button>
                      </div>
                    </template>
                  </b-table>
                </b-col>
              </b-row>
            </b-card>
          </b-col>
        </b-row>
      </tab-content>

      <tab-content
        :title="$t('App Settings')"
        icon="feather icon-settings"
        v-if="localID > 0"
      >
        <b-row class="mb-1">
          <b-col md="12">
            <b-media vertical-align="center">
              <template #aside>
                <b-avatar
                  size="44"
                  :src="staffObjPicturePreview"
                  variant="light-primary"
                />
              </template>

              <b-link class="font-weight-bold d-block text-nowrap mb-25">
                <b-badge variant="light-primary">
                  {{ myObj.name }}
                </b-badge>
              </b-link>
              <small class="text-muted">
                <b-badge variant="light-primary">
                  {{ myObj.designation }}
                </b-badge>
              </small>
            </b-media>
          </b-col>
        </b-row>
        <b-row class="match-height">
          <!-- align-h="center"-->
          <b-col md="12">
            <b-card class="">
              <h3 class="mt-1">{{ $t("Application Rights") }}</h3>
              <hr />
              <b-row>
                <b-col class="d-flex align-items-center flex-wrap" md="12">
                  <b-form-checkbox-group
                    v-model="selectedRights"
                    :options="appRights"
                  ></b-form-checkbox-group>
                </b-col>
                <b-col md="12">
                  <b-form-group label="" class="mt-2">
                    <b-button
                      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                      variant="success"
                      :disabled="savingRights"
                      @click="saveRights()"
                    >
                      <b-spinner v-if="savingRights" small />
                      <span v-else>{{ $t("Save") }}</span>
                    </b-button>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-card>
          </b-col>
        </b-row>
      </tab-content>

      <tab-content
        :title="$t('Increment History')"
        icon="feather icon-trending-up"
        v-if="localID > 0 ? !formRights.hidePayroll : true"
      >
        <b-row class="mb-1" v-if="localID > 0">
          <b-col md="12">
            <b-media vertical-align="center">
              <template #aside>
                <b-avatar
                  size="44"
                  :src="staffObjPicturePreview"
                  variant="light-primary"
                />
              </template>

              <b-link class="font-weight-bold d-block text-nowrap mb-25">
                <b-badge variant="light-primary">
                  {{ myObj.name }}
                </b-badge>
              </b-link>
              <small class="text-muted">
                <b-badge variant="light-primary">
                  {{ myObj.designation }}
                </b-badge>
              </small>
            </b-media>
          </b-col>
        </b-row>
        <!-- v-if="localID > 0" -->
        <b-row class="match-height">
          <b-col md="12">
            <b-card class="">
              <b-row>
                <b-col>
                  <b-button
                    @click="AddOpen()"
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="primary"
                  >
                    <feather-icon icon="PlusIcon" class="mr-50" />
                    <span class="align-middle">{{
                      $t("Increment History")
                    }}</span>
                  </b-button>
                </b-col>
              </b-row>
              <b-row>
                <b-col>
                  <b-table
                    class="mt-1"
                    ref="hisTable"
                    :items="history"
                    :fields="hisFields"
                    show-empty
                    responsive
                    hover
                  >
                    <template #head()="scope">
                      <div>
                        <span>{{ $t(scope.label) }}</span>
                      </div>
                    </template>

                    <template #cell(date)="data">
                      <b-badge variant="light-primary">
                        {{
                          new Date(data.value).toLocaleDateString("en-UK", {
                            year: "numeric",
                            day: "numeric",
                            month: "short",
                          })
                        }}
                      </b-badge>
                    </template>
                    <template #cell(amount)="data">
                      <b-badge variant="light-primary">
                        {{ data.value }}
                      </b-badge>
                    </template>
                    <template #cell(actions)="data">
                      <div class="">
                        <b-button
                          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                          variant="primary"
                          class="btn-icon mr-50"
                          @click="EditInc(data.item, data.index)"
                        >
                          <feather-icon icon="EditIcon" />
                        </b-button>

                        <b-button
                          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                          variant="outline-danger"
                          class="btn-icon mr-50"
                          @click="DeleteInc(data.item.id, data.index)"
                        >
                          <feather-icon icon="Trash2Icon" />
                        </b-button>
                      </div>
                    </template>
                  </b-table>
                </b-col>
              </b-row>
            </b-card>
          </b-col>
        </b-row>
      </tab-content>

      <tab-content
        :title="$t('Payroll')"
        icon="feather icon-dollar-sign"
        v-if="localID > 0 ? !formRights.hidePayroll : true"
      >
        <b-row class="mb-1" v-if="localID > 0">
          <b-col md="12" sm="6">
            <b-media vertical-align="center">
              <template #aside>
                <b-avatar
                  size="44"
                  :src="staffObjPicturePreview"
                  variant="light-primary"
                />
              </template>

              <b-link class="font-weight-bold d-block text-nowrap mb-25">
                <b-badge variant="light-primary">
                  {{ myObj.name }}
                </b-badge>
              </b-link>
              <small class="text-muted">
                <b-badge variant="light-primary">
                  {{ myObj.designation }}
                </b-badge>
              </small>
            </b-media>
          </b-col>
        </b-row>
        <!-- v-if="localID > 0" -->
        <b-row align-h="center" class="match-height">
          <b-col xl="6" lg="6" md="12">
            <b-card class="">
              <h3 class="mt-1">
                {{ $t("Allowances") }}
                <feather-icon
                  size="18"
                  icon="EditIcon"
                  class="ml-50 cursor-pointer"
                  @click="openAllow()"
                />
              </h3>
              <hr />
              <b-row
                class="align-items-center mb-1 mb-md-0"
                v-for="(item, index) in AddingInfo"
                :key="index"
              >
                <!-- <div
                  v-for="(item, index) in AddingInfo"
                  :key="index"
                  style="display: flex; align-items: center; width: 100%"
                >
                </div> -->
                <b-col md="7" class="pr-md-0">
                  <b-form-group
                    :label="$t('Allowance')"
                    :invalid-feedback="$t('detail is required.')"
                    ref="details"
                  >
                    <v-select
                      v-model="item.allowanceID"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="allowances"
                      :reduce="(op) => op.id"
                      :selectable="(opt) => !allowIDs.includes(opt.id)"
                      :clearable="false"
                      label="title"
                      @input="changeAllow()"
                    />
                    <!-- <b-form-input
                      ref="details"
                      placeholder="Enter details"
                      v-model="item.details"
                    /> -->
                  </b-form-group>
                </b-col>
                <b-col md="3" class="pr-md-0">
                  <b-form-group
                    :label="$t('Amount')"
                    :invalid-feedback="$t('Amount is required.')"
                    ref="sal_amount"
                  >
                    <b-form-input
                      ref="sal_amount"
                      type="number"
                      :placeholder="$t('Enter amount')"
                      v-model="item.amount"
                    />
                  </b-form-group>
                </b-col>

                <b-col md="2">
                  <b-button
                    v-if="index == AddingInfo.length - 1"
                    @click="AddDetail(item)"
                    variant="primary"
                    class="btn-icon rounded-circle mr-50"
                  >
                    <feather-icon icon="PlusIcon" />
                  </b-button>
                  <b-button
                    v-if="AddingInfo.length > 1"
                    @click="removeDetail(item, index)"
                    variant="outline-danger"
                    class="btn-icon rounded-circle"
                  >
                    <feather-icon icon="XIcon" />
                  </b-button>
                </b-col>
              </b-row>
            </b-card>
          </b-col>
          <b-col xl="6" lg="6" md="12">
            <b-card class="">
              <h3 class="mt-1">
                {{ $t("Salary Deduction") }}
                <feather-icon
                  size="18"
                  icon="EditIcon"
                  class="ml-50 cursor-pointer"
                  @click="openDeduct()"
                />
              </h3>
              <hr />
              <b-row
                class="align-items-center mb-1 mb-md-0"
                v-for="(item, index) in deductInfo"
                :key="index"
              >
                <b-col md="7" class="pr-md-0">
                  <b-form-group
                    :label="$t('Deduction')"
                    :invalid-feedback="$t('detail is required.')"
                    ref="ded_details"
                  >
                    <v-select
                      v-model="item.deductionID"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="deductions"
                      :reduce="(op) => op.id"
                      :selectable="(opt) => !dedIDs.includes(opt.id)"
                      :clearable="false"
                      label="title"
                      @input="changeDed()"
                    />
                    <!-- <b-form-input
                      ref="ded_details"
                      placeholder="Enter details"
                      v-model="item.details"
                    /> -->
                  </b-form-group>
                </b-col>
                <b-col md="3" class="pr-md-0">
                  <b-form-group
                    :label="$t('Amount')"
                    :invalid-feedback="$t('Amount is required.')"
                    ref="ded_amount"
                  >
                    <b-form-input
                      ref="ded_amount"
                      type="number"
                      :placeholder="$t('Enter amount')"
                      v-model="item.amount"
                    />
                  </b-form-group>
                </b-col>

                <b-col md="2">
                  <b-button
                    v-if="index == deductInfo.length - 1"
                    @click="AddDeduct(item)"
                    variant="primary"
                    class="btn-icon rounded-circle mr-50"
                  >
                    <feather-icon icon="PlusIcon" />
                  </b-button>
                  <b-button
                    v-if="deductInfo.length > 1"
                    @click="removeDeduct(item, index)"
                    variant="outline-danger"
                    class="btn-icon rounded-circle"
                  >
                    <feather-icon icon="XIcon" />
                  </b-button>
                </b-col>
                <!-- <div
                  v-for="(item, index) in deductInfo"
                  :key="index"
                  style="display: flex; align-items: center; width: 100%"
                >
                </div> -->
              </b-row>
            </b-card>
          </b-col>
        </b-row>
        <b-row align-h="center" class="">
          <b-col xl="12" lg="12" md="12">
            <b-card class="">
              <h3 class="d-flex align-items-center justify-content-between">
                {{ $t("Salary Information") }}
                <feather-icon
                  size="22"
                  icon="SettingsIcon"
                  class="cursor-pointer"
                  @click="openSettings()"
                  v-b-tooltip.hover.top
                  :title="$t('Breakup Policy')"
                />
              </h3>
              <hr />
              <b-row>
                <b-col md="3">
                  <b-form-group :invalid-feedback="$t('Salary is required.')">
                    <div class="">
                      <label class="bv-no-focus-ring col-form-label pt-0"
                        >{{ $t("Total Salary") }}
                        <feather-icon
                          class="ml-50 cursor-pointer"
                          icon="RefreshCwIcon"
                          size="18"
                          v-b-tooltip.hover.top
                          :title="$t('Calculate basic salary & allowances')"
                          @click="calculate()"
                        />
                      </label>
                    </div>
                    <b-form-input
                      ref="salary"
                      v-model="myObj.totalSalary"
                      type="number"
                      class="form-control"
                      :placeholder="$t('Enter salary')"
                      @keyup.enter="calculate()"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="3">
                  <b-form-group
                    :label="$t('Basic Salary')"
                    :invalid-feedback="$t('salary is required.')"
                  >
                    <b-form-input
                      ref="basic"
                      v-model="myObj.basicSalary"
                      type="number"
                      class="form-control"
                      :placeholder="$t('Enter basic salary')"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="3">
                  <b-form-group :label="$t('Basic + Allowances')">
                    <b-form-input
                      v-model="basicPlusAllowances"
                      type="number"
                      disabled
                      class="form-control"
                      :placeholder="$t('Basic + Allowances')"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="3">
                  <b-form-group
                    :label="$t('Leaves allowed from')"
                    :invalid-feedback="$t('date is required')"
                  >
                    <flat-pickr
                      :config="config"
                      v-model="myObj.leaves_allowed_from"
                      class="form-control"
                      :placeholder="$t('Select date.')"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row
                v-if="isMyskoolStJacobDomain"
                class="st-jacob-salary-toggles-row"
              >
                <b-col md="6" lg="5">
                  <div
                    class="d-flex align-items-center justify-content-between st-jacob-salary-toggle"
                  >
                    <label class="bv-no-focus-ring mb-0 text-dark font-weight-bold">
                      {{ $t("OFF leaves allowed") }}
                    </label>
                    <b-form-checkbox
                      v-model="myObj.offLeavesAllowed"
                      switch
                    />
                  </div>
                </b-col>
                <b-col md="6" lg="5">
                  <div
                    class="d-flex align-items-center justify-content-between st-jacob-salary-toggle"
                  >
                    <label class="bv-no-focus-ring mb-0 text-dark font-weight-bold">
                      {{ $t("OFF attendance allowances") }}
                    </label>
                    <b-form-checkbox
                      v-model="myObj.offAttendanceAllowances"
                      switch
                    />
                  </div>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="4" v-if="reportDomain == 'myskoolskh'">
                  <b-form-group
                    :label="$t('Share')"
                    :invalid-feedback="$t('Share is required.')"
                  >
                    <b-form-input
                      ref="share"
                      v-model="myObj.sharePerc"
                      type="number"
                      class="form-control"
                      :placeholder="$t('Enter share')"
                    />
                  </b-form-group>
                </b-col>
                <template v-if="reportDomain == 'myskoolhpgs'">
                  <b-col md="4">
                    <b-form-group
                      :label="$t('Pay Mode')"
                      :invalid-feedback="$t('Pay mode is required.')"
                    >
                      <v-select
                        v-model="myObj.payMode"
                        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                        :options="payOptions"
                        :clearable="false"
                        label="text"
                        :reduce="(val) => val.value"
                        :placeholder="$t('Select pay mode')"
                        @input="setAccount()"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col md="4">
                    <b-form-group
                      :label="$t('Account')"
                      :invalid-feedback="$t('Account is required.')"
                    >
                      <v-select
                        v-model="myObj.accountID"
                        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                        :options="accTypes"
                        :reduce="(opt) => opt.id"
                        label="account"
                        :clearable="false"
                        :placeholder="$t('Select account')"
                        @input="LoadBranches()"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col md="4">
                    <b-form-group
                      :label="$t('Branch')"
                      :invalid-feedback="$t('Branch is required.')"
                    >
                      <v-select
                        v-model="myObj.branchID"
                        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                        :options="branchOptions"
                        :reduce="(opt) => opt.id"
                        label="branch"
                        :clearable="false"
                        :disabled="branchLoading || disBranch"
                        :placeholder="$t('Select branch')"
                      />
                    </b-form-group>
                  </b-col>
                </template>
                <b-col v-else :md="reportDomain == 'myskoolskh' ? 8 : 12">
                  <b-form-group
                    :label="$t('Pay Mode')"
                    :invalid-feedback="$t('Pay mode is required.')"
                  >
                    <v-select
                      v-model="myObj.payMode"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="payOptions"
                      :clearable="false"
                      label="text"
                      :reduce="(val) => val.value"
                      :placeholder="$t('Select pay mode')"
                      @input="setAccount()"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="12">
                  <b-form-group
                    :label="$t('Account Title')"
                    :invalid-feedback="$t('Account title is required')"
                  >
                    <b-form-input
                      v-model="myObj.accountTitle"
                      :placeholder="$t('Enter account title')"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="12">
                  <b-form-group
                    :label="$t('Account #')"
                    :invalid-feedback="$t('Account number is required')"
                  >
                    <b-form-input
                      v-model="myObj.accountNumber"
                      :placeholder="$t('Enter A/C No.')"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </b-card>
          </b-col>
          <b-col xl="7" lg="8" md="12">
            <b-card>
              <h3 class="d-flex align-items-center justify-content-between">
                {{ $t("Custom Payroll Policy") }}
                <feather-icon
                  size="22"
                  icon="SettingsIcon"
                  class="cursor-pointer"
                  @click="openPSett()"
                  v-b-tooltip.hover.top
                  :title="$t('Settings')"
                />
                <!-- <b-form-checkbox v-model="customObj.show" switch /> -->
              </h3>

              <hr />
              <b-row v-if="false">
                <b-col md="6">
                  <b-form-group
                    :label="$t('Leaves By')"
                    :invalid-feedback="$t('Leaves By is required.')"
                    ref="leave_type"
                  >
                    <v-select
                      v-model="customObj.leaveType"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="leaveTypes"
                      :reduce="(op) => op.value"
                      :clearable="false"
                      label="text"
                      :placeholder="$t('Select')"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group
                    :label="$t('Starting From')"
                    :invalid-feedback="$t('Date is required')"
                    ref="dtFrom"
                  >
                    <flat-pickr
                      ref="dtFrom"
                      :config="config"
                      v-model="customObj.dateFrom"
                      class="form-control"
                      :placeholder="$t('Select Date')"
                    />
                  </b-form-group>
                </b-col>
              </b-row>

              <!-- settings -->
              <b-row class="mt-1">
                <!-- leaves -->
                <b-col md="12">
                  <b-row>
                    <b-col
                      class="d-flex align-items-center justify-content-between mb-1"
                      md="12"
                    >
                      <label
                        class="bv-no-focus-ring h4 mb-0 p-0 mr-1 text-dark"
                        >{{ $t("Allow Leaves") }}</label
                      >
                      <b-form-checkbox v-model="leavesObj.enabled" switch />
                    </b-col>
                    <b-col md="3" v-if="leavesObj.enabled" class="mb-1">
                      <b-form-group
                        :label="$t('Category')"
                        :invalid-feedback="$t('Category is required.')"
                        ref="all_cat"
                      >
                        <v-select
                          ref="all_cat"
                          v-model="leavesObj.option"
                          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                          :options="leaveTypes"
                          :reduce="(opt) => opt.text"
                          label="text"
                          :clearable="false"
                          :placeholder="$t('Select')"
                          @input="setMaxLeaves()"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col md="3" v-if="leavesObj.enabled">
                      <b-form-group
                        :invalid-feedback="$t('C.L Allowed is required.')"
                        ref="leave_allowed"
                      >
                        <template #label>
                          <span
                            v-b-tooltip.hover.top
                            :title="
                              $t(
                                'Casual Leave - Leave for personal reasons, vacations, or short absences'
                              )
                            "
                            class="cursor-pointer"
                          >
                            {{ $t("C.L Allowed") }}
                          </span>
                        </template>
                        <b-form-input
                          :placeholder="$t('Enter leave allowed')"
                          type="number"
                          ref="leave_allowed"
                          v-model="leavesObj.number"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col md="3" v-if="leavesObj.enabled">
                      <b-form-group
                        :invalid-feedback="
                          $t('Sick Leave Allowed is required.')
                        "
                        ref="sick_allowed"
                      >
                        <template #label>
                          <span
                            v-b-tooltip.hover.top
                            :title="
                              $t(
                                'Sick Leave - Leave specifically for health-related absences and medical appointments'
                              )
                            "
                            class="cursor-pointer"
                          >
                            {{ $t("S.L Allowed") }}
                          </span>
                        </template>
                        <b-form-input
                          :placeholder="$t('Enter sick leave allowed')"
                          type="number"
                          ref="sick_allowed"
                          v-model="sickObj.number"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col
                      md="3"
                      v-if="leavesObj.enabled && leavesObj.option == 'Yearly'"
                      class="mb-50"
                    >
                      <b-form-group
                        :invalid-feedback="$t('Max Allowed is required.')"
                        ref="max_allowed"
                      >
                        <template #label>
                          <span
                            v-b-tooltip.hover.top
                            :title="
                              $t('Maximum number of leaves allowed per month')
                            "
                            class="cursor-pointer"
                          >
                            {{ $t("M.L per Month") }}
                          </span>
                        </template>
                        <b-form-input
                          :placeholder="$t('Enter max leaves per month')"
                          type="number"
                          ref="max_allowed"
                          v-model="maxObj.number"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-col>

                <!-- late coming -->
                <b-col md="12">
                  <b-row>
                    <b-col
                      class="d-flex align-items-center justify-content-between mb-1"
                      md="12"
                    >
                      <label
                        class="bv-no-focus-ring h4 mb-0 p-0 mr-1 text-dark"
                        >{{ $t("Late Coming Deduction") }}</label
                      >
                      <b-form-checkbox v-model="lateObj.enabled" switch />
                    </b-col>
                    <b-col md="6" v-if="lateObj.enabled" class="mb-1">
                      <b-form-group
                        :label="$t('Late Coming Scale')"
                        :invalid-feedback="$t('Invalid value entered')"
                        ref="lateScale"
                      >
                        <b-form-input
                          :placeholder="$t('Enter late coming scale')"
                          ref="lateScale"
                          type="number"
                          v-model="lateObj.number"
                          @input="setLateHint()"
                        />
                        <p
                          v-if="showLateHint"
                          class="mb-0 mt-50"
                          style="font-size: 12px; line-height: 1rem"
                        >
                          ({{ lateObj.number ? lateObj.number : "n" }})
                          {{ $t("late comings equal to 1 absent") }}
                        </p>
                      </b-form-group>
                    </b-col>
                    <b-col md="6" v-if="lateObj.enabled" class="mb-1">
                      <b-form-group :label="$t('Deduct For Every Late:')">
                        <b-form-checkbox
                          v-model="lateObj.option"
                          switch
                          value="Yes"
                          unchecked-value="No"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-col>

                <!-- late coming per min -->
                <b-col md="12">
                  <b-row>
                    <b-col
                      class="d-flex align-items-center justify-content-between mb-1"
                      md="12"
                    >
                      <label
                        class="bv-no-focus-ring h4 mb-0 p-0 mr-1 text-dark"
                        >{{ $t("Late Coming Deduction (minutes)") }}</label
                      >
                      <b-form-checkbox v-model="minuteObj.enabled" switch />
                    </b-col>
                    <b-col md="6" v-if="minuteObj.enabled" class="mb-1">
                      <b-form-group
                        :label="$t('Rupees per minute')"
                        :invalid-feedback="$t('Invalid value entered')"
                        ref="lateAmount"
                      >
                        <b-form-input
                          :placeholder="$t('Enter rupees per minute')"
                          ref="lateAmount"
                          type="number"
                          v-model="minuteObj.number"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-col>

                <!-- short leave -->
                <b-col md="12">
                  <b-row>
                    <b-col
                      class="d-flex align-items-center justify-content-between mb-1"
                      md="12"
                    >
                      <label
                        class="bv-no-focus-ring h4 mb-0 p-0 mr-1 text-dark"
                        >{{ $t("Short Leave Deduction") }}</label
                      >
                      <b-form-checkbox v-model="shortObj.enabled" switch />
                    </b-col>
                    <b-col md="6" v-if="shortObj.enabled" class="mb-1">
                      <b-form-group
                        :label="$t('Short Leave Scale')"
                        :invalid-feedback="$t('Invalid value entered')"
                        ref="shortScale"
                      >
                        <b-form-input
                          :placeholder="$t('Enter short leave scale')"
                          type="number"
                          ref="shortScale"
                          v-model="shortObj.number"
                          @input="setHalfHint()"
                        />
                        <p
                          v-if="showHalfHint"
                          class="mb-0 mt-50"
                          style="font-size: 12px; line-height: 1rem"
                        >
                          ({{ shortObj.number ? shortObj.number : "n" }})
                          {{ $t("short leaves equal to 1 absent") }}
                        </p>
                      </b-form-group>
                    </b-col>
                    <b-col md="6" v-if="shortObj.enabled" class="mb-1">
                      <b-form-group
                        :label="$t('Deduct For Every Short Leave:')"
                      >
                        <b-form-checkbox
                          v-model="shortObj.option"
                          switch
                          value="Yes"
                          unchecked-value="No"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-col>

                <!-- early -->
                <b-col md="12">
                  <b-row>
                    <b-col
                      class="d-flex align-items-center justify-content-between mb-1"
                      md="12"
                    >
                      <label
                        class="bv-no-focus-ring h4 mb-0 p-0 mr-1 text-dark"
                        >{{ $t("Early Departure") }}</label
                      >
                      <b-form-checkbox v-model="earlyObj.enabled" switch />
                    </b-col>
                    <b-col md="6" v-if="earlyObj.enabled" class="mb-1">
                      <b-form-group
                        :label="$t('Early Departure Scale')"
                        :invalid-feedback="$t('Invalid value entered')"
                        ref="earlyScale"
                      >
                        <b-form-input
                          :placeholder="$t('Enter early departure scale')"
                          type="number"
                          ref="earlyScale"
                          v-model="earlyObj.number"
                          @input="setEarlyHint()"
                        />
                        <p
                          v-if="showEarly"
                          class="mb-0 mt-50"
                          style="font-size: 12px; line-height: 1rem"
                        >
                          ({{ earlyObj.number ? earlyObj.number : "n" }})
                          {{ $t("early departures equal to 1 absent") }}
                        </p>
                      </b-form-group>
                    </b-col>
                    <b-col md="6" v-if="earlyObj.enabled" class="mb-1">
                      <b-form-group
                        :label="$t('Deduct For Every Early Departure:')"
                      >
                        <b-form-checkbox
                          v-model="earlyObj.option"
                          switch
                          value="Yes"
                          unchecked-value="No"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-col>

                <!-- attendance -->
                <b-col md="12">
                  <b-row>
                    <b-col
                      class="d-flex align-items-center justify-content-between mb-1"
                      md="12"
                    >
                      <label
                        class="bv-no-focus-ring h4 mb-0 p-0 mr-1 text-dark"
                        >{{ $t("100% Attendance Allowance") }}</label
                      >
                      <b-form-checkbox v-model="attObj.enabled" switch />
                    </b-col>
                    <b-col md="6" v-if="attObj.enabled" class="mb-1">
                      <b-form-group
                        :label="$t('Based On')"
                        :invalid-feedback="
                          $t('Attendance Allowance is required.')
                        "
                        ref="att_allow"
                      >
                        <v-select
                          ref="att_allow"
                          v-model="attObj.option"
                          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                          :options="attTypes"
                          :reduce="(opt) => opt.text"
                          label="text"
                          :clearable="false"
                          :placeholder="$t('Select')"
                          @input="checkFields()"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col md="6" v-if="attObj.enabled" class="mb-1">
                      <b-form-group
                        v-if="attObj.option == 'Fixed amount'"
                        :label="$t('Amount')"
                        :invalid-feedback="$t('Invalid amount entered')"
                        ref="amount"
                      >
                        <b-form-input
                          :placeholder="$t('Enter amount')"
                          type="number"
                          ref="amount"
                          v-model="attObj.number"
                        />
                      </b-form-group>
                      <b-form-group
                        v-if="attObj.option == 'Percentage'"
                        :label="$t('Percentage')"
                        :invalid-feedback="$t('Invalid value entered')"
                        ref="percentage"
                      >
                        <b-form-input
                          :placeholder="$t('Enter percentage')"
                          type="number"
                          ref="percentage"
                          v-model="attObj.number"
                        />
                      </b-form-group>
                      <!-- <b-form-group
                        v-if="attObj.option == 'one'"
                        label="No. of Days"
                        invalid-feedback="Invalid value entered"
                        ref="days"
                      >
                        <b-form-input
                          placeholder="Enter no. of days"
                          type="number"
                          ref="days"
                          v-model="attObj.number"
                        />
                      </b-form-group> -->
                    </b-col>
                  </b-row>
                </b-col>

                <!-- encashment -->
                <b-col md="12">
                  <b-row>
                    <b-col
                      class="d-flex align-items-center justify-content-between mb-1"
                      md="12"
                    >
                      <label
                        class="bv-no-focus-ring h4 mb-0 p-0 mr-1 text-dark"
                        >{{ $t("Leave Encashment") }}</label
                      >
                      <b-form-checkbox v-model="encashObj.enabled" switch />
                    </b-col>
                    <b-col md="6" v-if="encashObj.enabled" class="mb-1">
                      <b-form-group
                        :label="$t('Maximum Encashment Leaves')"
                        :invalid-feedback="$t('Invalid value entered')"
                        ref="maxEn"
                      >
                        <b-form-input
                          :placeholder="$t('Enter max no.of leaves')"
                          type="number"
                          ref="maxEn"
                          v-model="encashObj.number"
                          @input="checkMax()"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-col>

                <!-- for iota -->
                <b-col
                  v-if="pObj && pObj.valueBool"
                  class="d-flex align-items-center justify-content-between mb-1"
                  md="12"
                >
                  <label class="bv-no-focus-ring h4 mb-0 p-0 mr-1 text-dark">{{
                    $t("Payroll by Share")
                  }}</label>
                  <b-form-checkbox
                    v-model="payrollByShare"
                    switch
                    @change="setShareData()"
                  />
                </b-col>
              </b-row>
            </b-card>
          </b-col>

          <b-col
            xl="7"
            lg="8"
            md="12"
            v-if="pObj && pObj.valueBool && payrollByShare"
          >
            <b-card>
              <h3 class="">{{ $t("Payroll Share Policy") }}</h3>
              <hr />

              <b-row v-if="pClassData.length == 0">
                <b-col md="12" class="text-center">
                  <b-button
                    @click="AddP()"
                    variant="primary"
                    class="btn-icon rounded-circle"
                  >
                    <feather-icon size="18" icon="PlusIcon" />
                  </b-button>
                  <p class="mb-0 mt-50">
                    {{ $t("Add payroll share details") }}
                  </p>
                </b-col>
              </b-row>
              <b-row v-for="(item, ind) in pClassData" :key="ind">
                <b-col md="4">
                  <b-form-group
                    :label="$t('Class')"
                    :invalid-feedback="$t('Class is required')"
                  >
                    <v-select
                      v-model="item.clsID"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="classesOptions"
                      :clearable="false"
                      :selectable="(opt) => !selectedCls.includes(opt.id)"
                      :reduce="(val) => val.id"
                      label="name"
                      @input="FillPSec(item, ind, '')"
                      :disabled="dataloading"
                      :placeholder="$t('Select class')"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group
                    :label="$t('Section')"
                    :invalid-feedback="$t('Section is required')"
                  >
                    <v-select
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      v-model="item.secID"
                      :options="pSections[ind]"
                      :reduce="(val) => val.id"
                      label="section"
                      :clearable="false"
                      :disabled="dataloading"
                      :placeholder="$t('Select section')"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group
                    :label="$t('Subject')"
                    :invalid-feedback="$t('Subject is required.')"
                  >
                    <v-select
                      v-model="item.subjectID"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="pSubjects"
                      :reduce="(val) => val.id"
                      label="subject"
                      :clearable="false"
                      :disabled="dataloading"
                      :placeholder="$t('Select subject')"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group
                    :label="$t('Base Amount')"
                    :invalid-feedback="$t('Base Amount is required')"
                  >
                    <b-form-input
                      type="number"
                      :placeholder="$t('Enter base amount')"
                      v-model="item.baseValue"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group
                    :label="$t('Share per student')"
                    :invalid-feedback="$t('Share is required')"
                  >
                    <b-form-input
                      type="number"
                      :placeholder="$t('Enter share amount')"
                      v-model="item.sharePerStudent"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group
                    :label="$t('Starting From')"
                    :invalid-feedback="$t('Date is required')"
                  >
                    <flat-pickr
                      :config="config"
                      v-model="item.startingFrom"
                      class="form-control"
                      :placeholder="$t('Select Date')"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="12" class="text-center">
                  <b-button
                    v-if="ind == pClassData.length - 1"
                    @click="AddP()"
                    variant="primary"
                    class="btn-icon rounded-circle mr-50"
                  >
                    <feather-icon size="18" icon="PlusIcon" />
                  </b-button>
                  <b-button
                    v-if="pClassData.length > 1"
                    @click="removeP(item, ind)"
                    variant="outline-danger"
                    class="btn-icon rounded-circle"
                  >
                    <feather-icon size="18" icon="XIcon" />
                  </b-button>
                </b-col>
              </b-row>
            </b-card>
          </b-col>
        </b-row>
      </tab-content>

      <tab-content
        :title="$t('Security Deposit')"
        icon="feather icon-lock"
        v-if="localID > 0 ? !formRights.hidePayroll : true"
      >
        <b-row class="mb-1" v-if="localID > 0">
          <b-col md="12">
            <b-media vertical-align="center">
              <template #aside>
                <b-avatar
                  size="44"
                  :src="staffObjPicturePreview"
                  variant="light-primary"
                />
              </template>

              <div class="d-flex align-items-center justify-content-between">
                <div>
                  <b-link class="font-weight-bold d-block text-nowrap mb-25">
                    <b-badge variant="light-primary">
                      {{ myObj.name }}
                    </b-badge>
                  </b-link>
                  <small class="text-muted">
                    <b-badge variant="light-primary">
                      {{ myObj.designation }}
                    </b-badge>
                  </small>
                </div>
                <!-- <div>
                  <b-button
                    variant="success"
                    class="btn-icon"
                    v-b-tooltip.hover.right
                    title="Return Deposits"
                    @click="openReturn()"
                  >
                    <feather-icon icon="RefreshCcwIcon" />
                  </b-button>
                </div> -->
              </div>
            </b-media>
          </b-col>
        </b-row>
        <!-- v-if="false" -->
        <b-row class="match-height">
          <b-col xl="3" lg="3" md="6" sm="6">
            <b-card>
              <h4 class="card-title">{{ $t("Salary") }}</h4>
              <p class="card-text font-small-6 font-weight-bolder mb-0">
                <span>Rs.</span
                ><span class="font-weight-bolder mb-0">
                  <!-- {{ totalDeposits }} -->
                  {{ myObj.totalSalary }}
                </span>
              </p>
            </b-card>
          </b-col>
          <b-col xl="3" lg="3" md="6" sm="6">
            <b-card>
              <h4 class="card-title">{{ $t("Deposited so far") }}</h4>
              <p class="card-text font-small-6 font-weight-bolder mb-0">
                <span>Rs.</span
                ><span class="font-weight-bolder mb-0">
                  {{ currentDeposits }}</span
                >
              </p>
            </b-card>
          </b-col>
          <b-col xl="3" lg="3" md="6" sm="6">
            <b-card>
              <h4 class="card-title">{{ $t("Remaining Deposits") }}</h4>
              <p
                class="card-text font-small-6 font-weight-bolder mb-0 d-flex justify-content-between"
              >
                <span class="font-weight-bolder mb-0">
                  Rs. {{ remainingDeposits }}</span
                >
                <b-badge
                  variant="light-danger"
                  v-if="myObj.totalSalary > totalDeposits"
                  >{{ myObj.totalSalary - totalDeposits }}</b-badge
                >
              </p>
            </b-card>
          </b-col>
          <b-col xl="3" lg="3" md="6" sm="6">
            <b-card>
              <h4 class="card-title">{{ $t("Returned Deposits") }}</h4>
              <p class="card-text font-small-6 font-weight-bolder mb-0">
                <span>Rs.</span
                ><span class="font-weight-bolder mb-0">
                  {{ returnDeposits }}</span
                >
              </p>
            </b-card>
          </b-col>

          <b-col md="12">
            <b-card class="">
              <b-row>
                <b-col md="12" class="d-flex justify-content-between flex-wrap">
                  <b-button
                    @click="depoOpen()"
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="primary"
                    class="mt-50 mt-sm-0"
                  >
                    <feather-icon icon="PlusIcon" class="mr-50" />
                    <span class="align-middle">{{
                      $t("Security Deposit")
                    }}</span>
                  </b-button>
                  <b-button
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="primary"
                    class="mt-50 mt-sm-0"
                    :disabled="delAll"
                    @click="depoDelAll()"
                  >
                    <span class="align-middle">{{
                      $t("Delete undeposited")
                    }}</span>
                  </b-button>
                </b-col>
              </b-row>
              <b-row>
                <b-col>
                  <b-table
                    class="mt-1"
                    ref="depoTable"
                    :items="deposits"
                    :fields="depoFields"
                    show-empty
                    responsive
                    hover
                  >
                    <template #head()="scope">
                      <div>
                        <span>{{ $t(scope.label) }}</span>
                      </div>
                    </template>
                    <template #cell(date)="data">
                      <b-badge variant="light-primary">
                        <!-- {{
                          new Date(data.value).toLocaleDateString("en-UK", {
                            year: "numeric",
                            day: "numeric",
                            month: "short",
                          })
                        }} -->
                        {{ data.item.month }} {{ data.item.year }}
                      </b-badge>
                    </template>
                    <template #cell(amount)="data">
                      <b-badge variant="light-primary">
                        {{ data.value }}
                      </b-badge>
                      <feather-icon
                        v-if="
                          sdObj &&
                          sdObj.valueBool &&
                          data.item.salaryID == null &&
                          data.item.isReturned == null
                        "
                        icon="EditIcon"
                        class="ml-50"
                        @click="openAmountEdit(data.item, data.index)"
                      />
                    </template>
                    <template #cell(loanType)="data">
                      <b-badge variant="light-primary">
                        {{ data.value }}
                      </b-badge>
                    </template>

                    <template #cell(actions)="data">
                      <b-button
                        v-if="data.item.salaryID != null"
                        variant="success"
                        class="btn-icon mr-1"
                        pill
                        size="sm"
                        @click="markUnDeposit(data.item)"
                      >
                        {{ $t("Deposited") }}
                      </b-button>
                      <b-button
                        v-else
                        variant="outline-success"
                        class="btn-icon mr-1"
                        pill
                        size="sm"
                        @click="markDeposit(data.item)"
                      >
                        {{ $t("Deposit") }}
                      </b-button>
                      <b-button
                        v-if="
                          data.item.isReturned != null &&
                          data.item.isReturned == true
                        "
                        variant="success"
                        class="btn-icon mr-1"
                        pill
                        size="sm"
                        @click="markUnReturned(data.item)"
                      >
                        {{ $t("Returned") }}
                      </b-button>
                      <b-button
                        v-else-if="
                          data.item.isReturned == null &&
                          data.item.salaryID != null
                        "
                        variant="outline-success"
                        class="btn-icon mr-1"
                        pill
                        size="sm"
                        @click="markReturned(data.item)"
                      >
                        {{ $t("Returned") }}
                      </b-button>

                      <!-- <b-button
                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                        variant="primary"
                        class="btn-icon mr-50"
                        @click="EditDepo(data.item, data.index)"
                      >
                        <feather-icon icon="EditIcon" />
                      </b-button> -->

                      <b-button
                        v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                        variant="outline-danger"
                        class="btn-icon mr-50"
                        @click="DeleteDepo(data.item.id, data.index)"
                        v-b-tooltip.hover.right
                        :title="$t('Delete')"
                      >
                        <feather-icon icon="Trash2Icon" />
                      </b-button>
                    </template>
                  </b-table>
                </b-col>
              </b-row>
            </b-card>
          </b-col>
        </b-row>
      </tab-content>

      <tab-content
        :title="$t('Loan / Advance')"
        icon="feather icon-info"
        v-if="localID > 0 ? !formRights.hidePayroll : true"
      >
        <b-row class="mb-1" v-if="localID > 0">
          <b-col md="12">
            <b-media vertical-align="center">
              <template #aside>
                <b-button
                  v-if="visibility2"
                  @click="visibility2 = false"
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="outline-secondary"
                  class="btn-icon rounded-circle mr-50"
                  v-b-tooltip.hover.top
                  :title="$t('Back')"
                >
                  <feather-icon icon="ArrowLeftIcon" size="20" />
                </b-button>
                <b-avatar
                  size="44"
                  :src="staffObjPicturePreview"
                  variant="light-primary"
                />
              </template>

              <b-link class="font-weight-bold d-block text-nowrap mb-25">
                <b-badge variant="light-primary">
                  {{ myObj.name }}
                </b-badge>
              </b-link>
              <small class="text-muted">
                <b-badge variant="light-primary">
                  {{ myObj.designation }}
                </b-badge>
              </small>
            </b-media>
          </b-col>
        </b-row>
        <!-- v-if="false" -->
        <b-row class="match-height">
          <b-col md="12">
            <b-card class="" v-if="visibility2">
              <b-row>
                <b-col md="12">
                  <b-button
                    v-for="t in typeOptions"
                    :key="t.value"
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    :variant="
                      t.value == loanObj.loanType
                        ? 'primary'
                        : 'outline-primary'
                    "
                    :disabled="loanObj.id > 0"
                    class="mr-1 mb-1"
                    @click="loanObj.loanType = t.value"
                  >
                    {{ $t(t.text) }}
                  </b-button>
                </b-col>
                <b-col lg="3" md="4">
                  <b-form-group
                    :label="$t('Amount')"
                    :invalid-feedback="$t('Amount is required')"
                    ref="loan_amount"
                  >
                    <b-form-input
                      :disabled="loanObj.id > 0"
                      ref="loan_amount"
                      type="number"
                      id="mc-first-name"
                      :placeholder="$t('Enter amount')"
                      v-model="loanObj.amount"
                      @input="checkLoanAmount()"
                      @focusout="setbyRetAmount()"
                    />
                  </b-form-group>
                </b-col>
                <b-col lg="3" md="4">
                  <b-form-group
                    :label="$t('Installment Amount')"
                    :invalid-feedback="$t('Invalid Amount')"
                    ref="return_amount"
                  >
                    <b-form-input
                      :disabled="loanObj.id > 0"
                      ref="return_amount"
                      type="number"
                      :placeholder="$t('Amount to return per month')"
                      v-model="loanObj.monthlyInstallments"
                      @input="checkRetAmount()"
                      @focusout="setbyRetAmount()"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group
                    :label="$t('Deduct From')"
                    :invalid-feedback="$t('Invalid Amount')"
                  >
                    <div class="d-flex">
                      <v-select
                        v-model="loanMonth"
                        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                        :options="months"
                        label="text"
                        :reduce="(opt) => opt.value"
                        :clearable="false"
                        :placeholder="$t('Select')"
                        :disabled="loanObj.id > 0"
                        @input="setbyRetAmount()"
                        style="flex: 1"
                        class="pr-1"
                      />
                      <v-select
                        v-model="loanYear"
                        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                        :options="years"
                        :clearable="false"
                        :placeholder="$t('Select')"
                        :disabled="loanObj.id > 0"
                        @input="setbyRetAmount()"
                        style="flex: 1"
                      />
                    </div>
                  </b-form-group>
                </b-col>
                <b-col lg="2" md="3" class="mt-lg-2" v-if="loanObj.id == 0">
                  <b-button
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="success"
                    @click="AddLoan()"
                    block
                    :disabled="request"
                  >
                    <b-spinner v-if="request" small type="grow" />
                    <span v-else> {{ $t(sidebarButton) }} </span>
                  </b-button>
                </b-col>
              </b-row>
              <b-row>
                <b-col>
                  <b-table
                    class="mt-1"
                    :items="installData"
                    :fields="installFields"
                    show-empty
                    responsive
                    hover
                  >
                    <template #head()="scope">
                      <div>
                        <span>{{ $t(scope.label) }}</span>
                      </div>
                    </template>
                    <template #cell(month)="data">
                      <b-badge variant="light-primary">
                        {{ data.item.month }} {{ data.item.year }}
                      </b-badge>
                    </template>
                    <template #cell(amount)="data">
                      <b-badge variant="light-primary">
                        Rs.{{ data.item.amount }}
                      </b-badge>
                      <b-button
                        v-if="data.item.isReturned"
                        variant="success"
                        class="btn-icon ml-1"
                        pill
                        size="sm"
                      >
                        {{ $t("Returned") }}
                      </b-button>
                      <b-button
                        v-else-if="!data.item.isReturned && data.item.id > 0"
                        variant="outline-success"
                        class="btn-icon ml-1"
                        pill
                        size="sm"
                        @click="openReturnModal(data.item)"
                      >
                        {{ $t("Returned") }}
                      </b-button>
                    </template>
                  </b-table>
                </b-col>
              </b-row>
            </b-card>
            <b-card class="" v-else>
              <b-row>
                <b-col>
                  <b-button
                    @click="LoanOpen()"
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="primary"
                  >
                    <feather-icon icon="PlusIcon" class="mr-50" />
                    <span class="align-middle">{{ $t("Loan / Advance") }}</span>
                  </b-button>
                </b-col>
              </b-row>
              <b-row>
                <b-col>
                  <b-table
                    class="mt-1"
                    ref="loanTable"
                    :items="loanData"
                    :fields="loanFields"
                    show-empty
                    responsive
                    hover
                  >
                    <template #head()="scope">
                      <div>
                        <span>{{ $t(scope.label) }}</span>
                      </div>
                    </template>
                    <template #cell(date)="data">
                      <b-badge variant="light-primary">
                        {{
                          new Date(data.value).toLocaleDateString("en-UK", {
                            year: "numeric",
                            month: "short",
                          })
                        }}
                      </b-badge>
                    </template>
                    <template #cell(amount)="data">
                      <b-badge variant="light-primary">
                        {{ data.value }}
                      </b-badge>
                    </template>
                    <template #cell(loanType)="data">
                      <b-badge variant="light-primary">
                        {{ data.value }}
                      </b-badge>
                    </template>
                    <template #cell(months)="data">
                      <b-badge variant="light-primary">
                        {{ data.value }}
                      </b-badge>
                    </template>
                    <template #cell(actions)="data">
                      <div class="">
                        <!-- v-if="data.item.id > 0" -->
                        <b-button
                          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                          variant="primary"
                          class="btn-icon mr-50"
                          @click="EditLoan(data.item, data.index)"
                        >
                          <feather-icon icon="EyeIcon" />
                        </b-button>

                        <b-button
                          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                          variant="outline-danger"
                          class="btn-icon mr-50"
                          @click="DeleteLoan(data.item, data.index)"
                        >
                          <feather-icon icon="Trash2Icon" />
                        </b-button>
                      </div>
                    </template>
                  </b-table>
                </b-col>
              </b-row>
            </b-card>
          </b-col>
        </b-row>
      </tab-content>

      <tab-content
        :title="$t('Documents')"
        icon="feather icon-link"
        v-if="localID > 0 ? !formRights.hideDocuments : true"
      >
        <b-row align-h="center">
          <b-col md="8">
            <!-- Staff name header (edit mode only) -->
            <b-row class="mb-1" v-if="localID > 0">
              <b-col md="12">
                <b-media vertical-align="center">
                  <template #aside>
                    <b-avatar
                      size="44"
                      :src="staffObjPicturePreview"
                      variant="light-primary"
                    />
                  </template>
                  <b-link class="font-weight-bold d-block text-nowrap mb-25">
                    <b-badge variant="light-primary">
                      {{ myObj.name }}
                    </b-badge>
                  </b-link>
                  <small class="text-muted">
                    <b-badge variant="light-primary">
                      {{ myObj.designation }}
                    </b-badge>
                  </small>
                </b-media>
              </b-col>
            </b-row>

            <!-- Only shown when doc_setting is enabled -->
            <template v-if="docSetting && docSetting.valueBool">
              <!-- Add Category row -->
              <b-row class="mb-1 align-items-center">
                <b-col md="11">
                  <b-input-group>
                    <b-form-input
                      v-model.trim="newDocType"
                      :placeholder="$t('e.g. CNIC, Degree, Experience Letter')"
                      @keyup.enter="saveDocType()"
                    />
                    <b-input-group-append>
                      <b-button
                        variant="primary"
                        :disabled="savingDocType || !newDocType"
                        @click="saveDocType()"
                      >
                        <b-spinner v-if="savingDocType" small />
                        <span v-else>
                          <feather-icon icon="PlusIcon" class="mr-25" />
                          {{ $t("Add Type") }}
                        </span>
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                </b-col>
              </b-row>

              <!-- Doc type list (edit / delete) -->
              <b-row
                v-for="(dd, ind) in docData"
                :key="'named-' + ind"
                class="mb-1 align-items-center"
              >
                <b-col md="11">
                  <b-card
                    body-class="p-75"
                    class="rounded cursor-pointer mb-0"
                    @click="
                      dd.url
                        ? openFile(dd.url)
                        : $refs['staffDoc' + ind][0].click()
                    "
                  >
                    <div
                      class="d-flex align-items-center justify-content-between"
                    >
                      <!-- Left: status icon + title -->
                      <div class="d-flex align-items-center" style="flex: 1">
                        <feather-icon
                          class="mr-50 flex-shrink-0"
                          :icon="dd.url ? 'CheckCircleIcon' : 'AlertCircleIcon'"
                          :class="dd.url ? 'text-success' : 'text-warning'"
                          size="18"
                        />
                        <span class="font-weight-bold">{{ dd.title }}</span>
                      </div>

                      <!-- Right: manage icons + upload/download -->
                      <div class="d-flex align-items-center" @click.stop>
                        <!-- Edit type name -->
                        <feather-icon
                          icon="EditIcon"
                          class="text-primary cursor-pointer mr-75"
                          size="16"
                          v-b-tooltip.hover.top="$t('Edit type name')"
                          @click="openEditDocModal(dd)"
                        />
                        <!-- Delete type -->
                        <feather-icon
                          icon="Trash2Icon"
                          class="text-danger cursor-pointer mr-75"
                          size="16"
                          v-b-tooltip.hover.top="$t('Delete type')"
                          @click="deleteDocType(dd)"
                        />
                        <!-- Upload / Download -->
                        <b-spinner
                          v-if="docLoading && currentIndex == ind"
                          small
                          variant="primary"
                        />
                        <feather-icon
                          v-else
                          :icon="dd.url ? 'DownloadIcon' : 'UploadIcon'"
                          size="18"
                          @click="
                            dd.url
                              ? openFile(dd.url)
                              : $refs['staffDoc' + ind][0].click()
                          "
                        />
                      </div>
                    </div>
                  </b-card>
                  <input
                    type="file"
                    hidden
                    :ref="'staffDoc' + ind"
                    @change="selectDoc(dd, ind)"
                    accept="image/*,.pdf"
                  />
                </b-col>
                <!-- Clear uploaded file -->
                <b-col md="1" class="pl-md-0 mt-50 mt-md-0">
                  <feather-icon
                    v-if="dd.url"
                    icon="XIcon"
                    class="text-secondary cursor-pointer"
                    size="18"
                    v-b-tooltip.hover.top="$t('Clear upload')"
                    @click="dd.url = ''"
                  />
                </b-col>
              </b-row>

              <!-- Additional Docs section -->
              <b-row class="mb-1 mt-2">
                <b-col
                  md="11"
                  class="d-flex flex-wrap align-items-center justify-content-between"
                >
                  <h4 class="mb-0">{{ $t("Additional Docs") }}</h4>
                  <b-button
                    @click="$refs.staffAdditionalDocs.click()"
                    variant="primary"
                    class="btn-icon"
                    :disabled="uploading"
                  >
                    <input
                      ref="staffAdditionalDocs"
                      type="file"
                      class="file_upload"
                      @change="uploadDocument()"
                      multiple="true"
                      accept="image/*,.pdf"
                      hidden
                    />
                    {{ $t("Upload More") }}
                  </b-button>
                </b-col>
              </b-row>
            </template>

            <!-- Simple upload fallback (doc_setting not enabled) -->
            <template v-else>
              <div class="file-uploader">
                <h5>
                  <b-spinner
                    variant="dark"
                    label="Spinning"
                    v-if="uploading"
                  ></b-spinner>
                  <span v-else> {{ $t("Upload Documents") }} </span>
                </h5>
                <input
                  ref="docs"
                  type="file"
                  class="file_upload"
                  @change="uploadDocument()"
                  multiple="true"
                />
              </div>
            </template>
          </b-col>
        </b-row>

        <!-- Additional / legacy docs list -->
        <b-row align-h="center">
          <b-col md="8">
            <b-row
              v-for="(file, index) in documents"
              :key="index"
              class="mb-1 align-items-center"
            >
              <b-col md="11">
                <b-card
                  body-class="p-75"
                  class="rounded cursor-pointer mb-0"
                  @click="openFile(file)"
                >
                  <div class="d-flex align-items-start justify-content-between">
                    <div>
                      <feather-icon
                        icon="CheckCircleIcon"
                        class="text-success mr-50"
                        size="20"
                      />
                      <span class="font-weight-bold">
                        {{ file.slice(0, 40) }}...</span
                      >
                    </div>
                    <feather-icon icon="DownloadIcon" size="20" />
                  </div>
                </b-card>
              </b-col>
              <b-col md="1" class="pl-md-0 mt-50 mt-md-0">
                <feather-icon
                  icon="TrashIcon"
                  class="text-danger cursor-pointer"
                  size="20"
                  @click="deleteDocument(index)"
                />
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </tab-content>

      <template slot="footer" slot-scope="props">
        <div class="wizard-footer-left d-none"></div>
        <div class="wizard-footer-right d-none"></div>
      </template>
    </form-wizard>

    <b-row align-h="center">
      <b-col md="6">
        <b-button
          block
          variant="success"
          :disabled="submitting"
          @click="formSubmitted()"
        >
          {{ $t("Save") }}
        </b-button>
      </b-col>
    </b-row>

    <!-- Children Selector Sidebar -->
    <b-sidebar
      id="children-selector-sidebar"
      ref="childrenSelectorSidebar"
      bg-variant="white"
      v-model="childrenSelectorVisible"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
    >
      <div class="children-sidebar-content">
        <div class="sidebar-header">
          <h5 class="mb-0">{{ $t("Select Children") }}</h5>
          <b-button
            variant="outline-secondary"
            size="sm"
            @click="childrenSelectorVisible = false"
            class="close-btn"
          >
            <feather-icon icon="XIcon" size="14" />
          </b-button>
        </div>

        <!-- Search Input -->
        <div class="search-section">
          <b-form-input
            v-model="childrenSearchQuery"
            :placeholder="$t('Search students') + '...'"
            @input="onChildrenSearchChange"
            class="search-input"
          />
        </div>

        <!-- Students List -->
        <div class="children-list-container">
          <div
            v-for="student in filteredChildrenOptions"
            :key="student.id"
            class="student-option-card"
            @click="selectChild(student)"
          >
            <div class="student-info">
              <b-avatar
                :src="student.picture"
                :text="getStudentInitials(student.name)"
                size="32px"
                class="student-avatar"
              />
              <div class="student-details">
                <div class="student-name">{{ student.name }}</div>
                <div class="student-class">
                  {{
                    student.cls_current ? student.cls_current.name : "No Class"
                  }}
                  <span v-if="student.grNo" class="gr-number"
                    >GR: {{ student.grNo }}</span
                  >
                </div>
              </div>
              <feather-icon icon="PlusIcon" size="14" class="add-icon" />
            </div>
          </div>

          <div
            v-if="
              filteredChildrenOptions.length === 0 && !childrenSearchLoading
            "
            class="empty-state"
          >
            <feather-icon icon="SearchIcon" size="32" />
            <p>{{ $t("No students found") }}</p>
          </div>

          <div v-if="childrenSearchLoading" class="loading-state">
            <b-spinner variant="primary" size="sm" />
            <span>{{ $t("Searching...") }}</span>
          </div>
        </div>
      </div>
    </b-sidebar>
    <b-sidebar
      id="sidebar-streams"
      bg-variant="white"
      v-model="visibilityStream"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
    >
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0">{{ $t('Manage Streams') }}</h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="visibilityStream = false"
        />
      </div>

      <div class="p-2">
        <!-- Add/Edit Form -->
        <b-form @submit.prevent="saveStream">
          <b-form-group :label="$t('Stream Name')">
            <b-form-input
              v-model.trim="streamObj.name"
              :placeholder="$t('Enter stream name')"
              required
            />
          </b-form-group>
          <div class="d-flex mb-2">
            <b-button
              variant="primary"
              type="submit"
              :disabled="savingStream"
              class="flex-grow-1"
            >
              <b-spinner v-if="savingStream" small />
              <span v-else>{{ streamObj.id > 0 ? $t('Update') : $t('Add New') }}</span>
            </b-button>
            <b-button
              v-if="streamObj.id > 0"
              variant="outline-secondary"
              class="ml-1"
              @click="streamObj = { id: 0, name: '', campusID: $store.state.userData.cId, status: 'active' }"
            >
              {{ $t('Cancel') }}
            </b-button>
          </div>
        </b-form>

        <hr />

        <!-- List -->
        <div v-if="loadingStreams" class="text-center my-2">
          <b-spinner variant="primary" />
        </div>
        <div v-else>
          <div v-for="stream in streams" :key="stream.id" class="d-flex align-items-center justify-content-between py-1 border-bottom">
            <div>
              <h6 class="mb-0">{{ stream.name }}</h6>
            </div>
            <div class="d-flex">
              <b-button variant="flat-primary" class="btn-icon p-0 mr-50" @click="editStream(stream)">
                <feather-icon icon="EditIcon" size="14" />
              </b-button>
              <b-button variant="flat-danger" class="btn-icon p-0" @click="deleteStream(stream.id)">
                <feather-icon icon="Trash2Icon" size="14" />
              </b-button>
            </div>
          </div>
          <b-pagination
            v-if="streamTotalCount > streamPerPage"
            v-model="streamCurrentPage"
            :total-rows="streamTotalCount"
            :per-page="streamPerPage"
            class="mt-2"
            align="center"
            @input="LoadStreams"
          />
        </div>
      </div>
    </b-sidebar>

    <b-sidebar
      id="sidebar-qualifications"
      bg-variant="white"
      v-model="visibilityQualification"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">{{ $t("Manage qualifications") }}</h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="visibilityQualification = false"
        />
      </div>

      <div class="p-2">
        <b-form @submit.prevent="saveQualificationSidebar">
          <b-form-group :label="$t('Qualification name')">
            <b-form-input
              v-model.trim="qualificationInput"
              :placeholder="$t('Enter qualification')"
              required
            />
          </b-form-group>
          <div class="d-flex mb-2">
            <b-button
              variant="primary"
              type="submit"
              :disabled="savingQualification || !qualificationInput"
              class="flex-grow-1"
            >
              <b-spinner v-if="savingQualification" small />
              <span v-else>{{
                qualificationEditingOld ? $t("Update") : $t("Add New")
              }}</span>
            </b-button>
            <b-button
              v-if="qualificationEditingOld"
              variant="outline-secondary"
              class="ml-1"
              @click="resetQualificationSidebarForm"
            >
              {{ $t("Cancel") }}
            </b-button>
          </div>
        </b-form>

        <hr />

        <div v-if="loadingQualifications" class="text-center my-2">
          <b-spinner variant="primary" />
        </div>
        <div v-else-if="!qualificationsServerList.length" class="text-muted small">
          {{ $t("No custom qualifications yet. Defaults appear in the dropdown.") }}
        </div>
        <div v-else>
          <div
            v-for="name in qualificationsServerList"
            :key="'qual-' + name"
            class="d-flex align-items-center justify-content-between py-1 border-bottom"
          >
            <div>
              <h6 class="mb-0">{{ name }}</h6>
            </div>
            <div class="d-flex">
              <b-button
                variant="flat-primary"
                class="btn-icon p-0 mr-50"
                @click="editQualificationSidebar(name)"
              >
                <feather-icon icon="EditIcon" size="14" />
              </b-button>
              <b-button
                variant="flat-danger"
                class="btn-icon p-0"
                @click="removeQualificationSidebar(name)"
              >
                <feather-icon icon="Trash2Icon" size="14" />
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </b-sidebar>
  </div>
</template>

<script>
import heic2any from "heic2any";

import { FormWizard, TabContent } from "vue-form-wizard";
import vSelect from "vue-select";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import "vue-form-wizard/dist/vue-form-wizard.min.css";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import Ripple from "vue-ripple-directive";
import { mapActions, mapGetters } from "vuex";
import { mask } from "vue-the-mask";
import {
  getMediaPreviewUrl,
  getUploadPath,
  getUploadedPaths,
  handleDrivePreviewError,
  uploadToMySkool,
} from "@/utils/gdriveUpload";
// import DropZone from 'dropzone-vue';
import vue2Dropzone from "vue2-dropzone";

import "vue2-dropzone/dist/vue2Dropzone.min.css";
// // optionally import default styles
// import 'dropzone-vue/dist/dropzone-vue.common.css';

// createApp(App)
//   .use(DropZone)
//   .mount('#app');
// import 'dropzone-vue/dist/dropzone-vue.common.css';

// optionally import default styles

import {
  VBTooltip,
  BRow,
  BFormCheckboxGroup,
  BFormCheckbox,
  BCol,
  BFormGroup,
  BFormInput,
  BCard,
  BButton,
  BMediaAside,
  BImg,
  BMedia,
  BLink,
  BSpinner,
  BBadge,
  BFormFile,
  BFormTextarea,
  BTable,
  BForm,
  BSidebar,
  BAvatar,
  BInputGroup,
  BInputGroupAppend,
  BPagination,
} from "bootstrap-vue";
import { resolve } from "path";
import { reject } from "q";
import axios from "axios";
import {
  buildStreamOptions,
  normalizeStreamIdForUi,
  streamIdForApi,
} from "./streamUtils";

/** Default qualification picks shown in the dropdown; merged with campus list from API. */
const DEFAULT_QUALIFICATION_OPTIONS = [
  "Under Matric",
  "Matric",
  "Intermediate",
  "Bachelors",
  "Masters",
  "M.Phil",
  "PhD",
  "Diploma",
  "Certification",
  "Other",
];

export default {
  components: {
    vueDropzone: vue2Dropzone,
    // DropZone,
    BAvatar,
    BForm,
    BFormTextarea,
    BSidebar,
    FormWizard,
    TabContent,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    vSelect,
    flatPickr,
    BCard,
    BButton,
    BMediaAside,
    BImg,
    BMedia,
    BLink,
    BSpinner,
    BBadge,
    BFormFile,
    BTable,
    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
    BFormCheckbox,
    BFormCheckboxGroup,
    BInputGroup,
    BInputGroupAppend,
    BPagination,
  },
  directives: {
    "b-tooltip": VBTooltip,
    Ripple,
    mask,
  },
  data() {
    let yList = [];
    for (let i = 2020; i <= 2040; i++) {
      yList.push(i);
    }
    return {
      skipped: [],
      delAll: false,
      years: yList,
      months: [
        { text: "January", value: 1 },
        { text: "February", value: 2 },
        { text: "March", value: 3 },
        { text: "April", value: 4 },
        { text: "May", value: 5 },
        { text: "June", value: 6 },
        { text: "July", value: 7 },
        { text: "August", value: 8 },
        { text: "September", value: 9 },
        { text: "October", value: 10 },
        { text: "November", value: 11 },
        { text: "December", value: 12 },
      ],
      deductMonth: new Date().getMonth() + 1,
      deductYear: new Date().getFullYear(),
      loanMonth: new Date().getMonth() + 1,
      loanYear: new Date().getFullYear(),
      localID: 0,
      file: [],
      mydocs: [],
      moreloading: false,
      plus: "",
      logoloading: false,
      netAmount: 0,
      dataloading: false,
      deptOptions: [],
      admittedSections: [],
      currentSections: [],
      classes: [],
      genders: ["Male", "Female"],
      groups: ["A", "B", "O", "AB"],
      religions: ["Islam", "Christianity", "Hinduism", "Buddhists"],
      config: {
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
        // disableMobile: true,
      },
      myObj: {
        id: 0,
        rfID: "",
        name: "",
        father_name: "",
        surname: "",
        designation: "",
        gender: "",
        picture: "",
        date_of_birth: null,
        address_current: "",
        address_permanent: "",
        province: "",
        city: "",
        linkedinProfile: "",
        salarySlip: "",
        cv: "",
        contact_number: "",
        emergency_contact_person: "",
        emergency_contact_relation: "",
        emergency_number: "",
        qualification: "",
        certificate: "",
        experience: "",
        campusID: this.$store.state.userData.cId,
        streamID: null,
        departmentID: 0,
        documents: "",
        appointedOn: null,
        totalSalary: 0,
        payMode: null,
        accountTitle: null,
        accountNumber: null,
        cnic: "",
        reportTo: [],
        reportsTo: "",
        spouse: "",
        isCoordinator: false,
        leaves_allowed_from: null,
        card_number: null,
        holdSalary: false,
        offLeavesAllowed: false,
        offAttendanceAllowances: false,
        accountID: 0,
        empCode: 0,
        registrationDate: new Date().toJSON(),
        resginationDate: null,
        branchID: 0,
        personalEmail: "",
        workEmail: "",
        previousSalary: 0,
        salaryCampus: 0,
        availibility: "",
        partTimerDays: "",
        reportTo: null,
        userID: null,
        eobiDeduct: false,
        pfDeduct: false,
        children: [
          {
            id: 0, // Children information record ID (set by backend)
            tID: 0, // Staff/Teacher ID (will be set to myObj.id)
            sID: 0, // Student ID (child's student ID)
            name: "", // Child's name
            className: "", // Child's class/grade
            image: "", // Child's profile image
            campusID: this.$store.state.userData.cId, // Campus ID
          },
        ],
      },
      pClassData: [],
      pSubjects: [],
      pSections: [],
      selectedCls: [],
      leaveTypes: [
        { text: "Monthly", value: "monthly" },
        { text: "Yearly", value: "yearly" },
      ],
      availibility: [
        "Part Time",
        "Full Time",
        "Probation",
        "Permanent",
        "Contractual",
        "Visiting",
        "Intern",
      ],
      // Report To functionality
      reportToOptions: [],
      reportToLoading: false,
      reportToKey: 0,
      reportDomain: this.$store.state.report.replace(/_/g, ""),
      // Children Selector
      childrenSelectorVisible: false,
      childrenSearchQuery: "",
      childrenSearchLoading: false,
      filteredChildrenOptions: [],
      customObj: {
        show: false,
        leaveType: "",
        dateFrom: null,
      },
      settObj: {
        isAllowed: false,
        leaveAllowed: 0,
        category: "monthly",
        dateFrom: null,
        dateTo: null,
        onLate: false,
        lateScale: null,
        everyLate: false,
        onLeave: false,
        shortScale: null,
        everyLeave: false,
        onAttendance: false,
        attendanceType: "fixed",
        days: 0,
        amount: 0,
        percentage: 0,
        onEncashment: false,
        maxEncashment: 0,
      },
      leavesObj: {
        id: 0,
        tID: this.$route.params.id,
        categoy: "Leave Allowed",
        enabled: false,
        number: 0,
        option: "Monthly",
        campusID: this.$store.state.userData.cId,
      },
      sickObj: {
        id: 0,
        tID: this.$route.params.id,
        categoy: "Sick Leave",
        enabled: false,
        number: 0,
        option: "Monthly",
        campusID: this.$store.state.userData.cId,
      },
      maxObj: {
        id: 0,
        tID: this.$route.params.id,
        categoy: "Max Leave per Month",
        enabled: false,
        number: 0,
        option: "",
        campusID: this.$store.state.userData.cId,
      },
      minuteObj: {
        id: 0,
        tID: this.$route.params.id,
        categoy: "Late Coming Deduction per Minute",
        enabled: false,
        number: 0,
        option: "",
        campusID: this.$store.state.userData.cId,
      },
      earlyObj: {
        id: 0,
        tID: this.$route.params.id,
        categoy: "Early Departure",
        enabled: false,
        number: 0,
        option: "No",
        campusID: this.$store.state.userData.cId,
      },
      showEarly: true,
      lateObj: {
        id: 0,
        tID: this.$route.params.id,
        categoy: "Late Coming Scale",
        enabled: false,
        number: 0,
        option: "No",
        campusID: this.$store.state.userData.cId,
      },
      shortObj: {
        id: 0,
        tID: this.$route.params.id,
        categoy: "Short Leave Scale",
        enabled: false,
        number: 0,
        option: "No",
        campusID: this.$store.state.userData.cId,
      },
      attObj: {
        id: 0,
        tID: this.$route.params.id,
        categoy: "Attendance Allowance",
        enabled: false,
        number: 0,
        option: "Fixed amount",
        campusID: this.$store.state.userData.cId,
      },
      encashObj: {
        id: 0,
        tID: this.$route.params.id,
        categoy: "Leave Encashment",
        enabled: false,
        number: 0,
        option: "",
        campusID: this.$store.state.userData.cId,
      },
      settingData: [],
      showHalfHint: true,
      showLateHint: true,
      attTypes: [
        { text: "Fixed amount", value: "fixed" },
        { text: "Percentage", value: "percentage" },
        { text: "One day salary", value: "one" },
      ],
      classObj: {
        class_admitted: null,
        section_admitted: null,
        subject_admitted: null,
      },
      allowObj: {
        id: 0,
        title: "",
        amount: 0,
        campusID: this.$store.state.userData.cId,
      },
      allowances: [],
      allowFields: [
        { label: "title", key: "title" },
        { key: "actions", label: "actions" },
      ],
      deductObj: {
        id: 0,
        title: "",
        amount: 0,
        campusID: this.$store.state.userData.cId,
      },
      deductions: [],
      saving: false,
      accIcon: "",
      fields: [
        { label: "Class", key: "class_admitted" },
        { label: "section", key: "section_admitted" },
        { label: "subject", key: "subject_admitted" },
      ],
      // for newTeachingList
      // fields2: [
      //   { label: "Class", key: "className" },
      //   { label: "section", key: "sectionName" },
      //   { label: "subject", key: "subjects" },
      //   "actions",
      // ],
      fields2: [
        { label: "Class", key: "cls" },
        { label: "section", key: "sec" },
        { label: "subject", key: "subjects" },
        "actions",
      ],
      hisFields: [
        { label: "date", key: "date" },
        { label: "amount", key: "amount" },
        { label: "note", key: "note" },
        "actions",
      ],
      history: [],
      deposits: [],
      depoSaveList: [],
      depoObj: {},
      newDepo: true,
      depoIndex: null,
      deductOption: [
        { text: "Same Month", value: true },
        { text: "Next Month", value: false },
      ],
      sameMonth: true,
      loanFields: [
        { label: "date", key: "date" },
        { label: "type", key: "loanType" },
        { label: "amount", key: "amount" },
        { label: "months", key: "months" },
        // { label: "return", key: "monthlyInstallments" },
        "actions",
      ],
      depoFields: [
        { label: "date", key: "date" },
        { label: "amount", key: "amount" },
        // { label: "months", key: "months" },
        "actions",
      ],
      installFields: [
        { label: "month", key: "month" },
        { label: "amount", key: "amount" },
        // "actions",
      ],
      installData: [],
      loanData: [],
      loanDetails: [],
      typeOptions: [
        { text: "Loan", value: "loan" },
        { text: "Advance", value: "advance" },
      ],
      updateOptions: [
        { text: "Yes", value: "yes" },
        { text: "No", value: "no" },
      ],
      incAmountTypeOptions: [
        { text: "Amount", value: "amount" },
        { text: "Percentage", value: "percent" },
      ],
      loanObj: {},
      newLoan: true,
      loanIndex: null,
      visibility2: false,
      loanHide: false,
      visibility3: false,
      visibility4: false,
      visibility5: false,
      allowIDs: [],
      dedIDs: [],
      AddingInfo: [
        {
          id: 0,
          tID: this.$route.params.id,
          amount: 0,
          allowanceID: 0,
          campusID: this.$store.state.userData.cId,
        },
      ],
      deductInfo: [
        {
          id: 0,
          tID: this.$route.params.id,
          amount: 0,
          deductionID: 0,
          campusID: this.$store.state.userData.cId,
        },
      ],

      classesOptions: [],
      qualificationOptions: [...DEFAULT_QUALIFICATION_OPTIONS],
      qualificationsServerList: [],
      teachingList: [],
      teachingClasses: [],
      newTeachingList: [],
      gridData: [],
      // teachingClassObj: {
      //   cID: 0,
      //   sectionName: "",
      //   subjects: 0,
      // },
      teachingClassObj: {
        tID: this.$route.params.id,
        clsID: 0,
        sections: [],
        subjects: [],
        campusID: this.$store.state.userData.cId,
      },
      subjects: [],
      selectedSubs: [],
      selectedSections: [],
      fphoneerror: false,
      mphoneerror: false,
      phoneerror: false,
      emerPhoneerror: false,
      sPhoneerror: false,
      fileProfile: "",
      vueTelOptions: { placeholder: "Enter contact number" },
      emerOpt: { placeholder: "Enter emergency contact" },
      documents: [],
      uploading: false,
      docData: [],
      docLoading: false,
      salarySlipLoading: false,
      cvLoading: false,
      currentIndex: 0,
      docSetting: null,
      newDocType: "",
      savingDocType: false,
      editDocObj: null,
      incObj: {},
      newInc: true,
      incIndex: null,
      sidebarTitle: "",
      sidebarButton: "",
      visibility: false,
      request: false,
      currDays: [],
      sameDays: this.$store.state.sameTimings,
      timingInfo: [
        {
          id: 0,
          tID: this.$route.params.id,
          day: "monday",
          timeIn: "2024-05-05T08:00:00",
          timeOut: "2024-05-05T13:00:00",
          dayOff: false,
          reportingTime: "2024-05-05T08:00:00",
          campusID: this.$store.state.userData.cId,
        },
      ],
      daysList: [
        { text: "Monday", value: "monday" },
        { text: "Tuesday", value: "tuesday" },
        { text: "Wednesday", value: "wednesday" },
        { text: "Thursday", value: "thursday" },
        { text: "Friday", value: "friday" },
        { text: "Saturday", value: "saturday" },
        { text: "Sunday", value: "sunday" },
      ],
      assigning: false,
      submitting: false,
      appRights: [
        { text: "Homework", value: "Homework" },
        { text: "Attendance", value: "Attendance" },
        { text: "Examination", value: "Examination" },
        { text: "Resources", value: "Resources" },
        { text: "My Profile", value: "My Profile" },
        { text: "Feedback", value: "Feedback" },
        { text: "Parent Consent", value: "Parent Consent" },
        { text: "Requisition", value: "Requisition" },
        { text: "Planner", value: "Planner" },
        { text: "Events", value: "Events" },
      ],
      selectedRights: [
        "Homework",
        "Attendance",
        "Examination",
        "Resources",
        "My Profile",
        "Feedback",
        "Parent Consent",
        "Requisition",
        "Planner",
        "Events",
      ],
      savingRights: false,
      rightObj: {},
      visibility6: false,
      policySett: [],
      breakupData: [],
      SettIDs: [],
      // Deduction Policy
      deductionPolicySett: [
        {
          id: 0,
          allowanceTypeID: null,
          allowanceName: "",
          perc: 0,
          campusID: 1,
        },
      ],
      deductionPolicyData: [],
      deductionPolicySettIDs: [],
      deductionTypes: [],
      savingDeductionPolicySett: false,

      // Contribution Policy
      contributionPolicySett: [
        {
          id: 0,
          allowanceTypeID: null,
          allowanceName: "",
          perc: 0,
          campusID: 1,
          eobiContribution: 0,
          PFcontribution: 0,
        },
      ],
      contributionPolicyData: [],
      contributionPolicySettIDs: [],
      contributionTypes: [],
      savingContributionPolicySett: false,
      salaryText: "Basic Salary",
      savingSett: false,
      subObj: null,
      pObj: null,
      payrollByShare: false,
      pCopy: {},
      visibility7: false,
      campuses: [],
      formRights: {},
      payOptions: [
        { value: "Cash", text: "Cash" },
        { value: "Bank", text: "Bank" },
        { value: "Cheque", text: "Cheque" },
      ],
      accTypes: [],
      rfObj: null,
      ctObj: null,
      isHeadOffice: null,
      branchOptions: [],
      branchLoading: false,
      disBranch: false,
      visibility8: false,
      returnObj: {
        tID: 0,
        amount: 0,
        date: null,
        cheque: "",
        userID: this.$store.state.userData.userID,
        campusID: this.$store.state.userData.cId,
      },
      savingReturn: false,
      graceTimeInput: 0,
      applying: false,
      returningLoan: false,
      returnLoanObj: null,
      sdObj: null,
      isUpdatingAmount: false,
      editAmountObj: null,
      depositIndex: 0,
      iussUserOptions: [],
      iussUsersLoading: false,
      salaryCampusObj: null,
      incrementTotalObj: null, // Store increment-total setting
      designations: [], // Array to store designation options from settings
      designationSetting: null, // Store designation settings
      showDesignationField: false, // Track if staff-designations setting exists
      salaryLedgerObj: null,
      coaAccounts: [],
      linkedAccounts: [],
      linkObj: null,
      streamOptions: [],
      streams: [],
      streamObj: {
        id: 0,
        name: "",
        campusID: this.$store.state.userData.cId,
        status: "active",
      },
      visibilityStream: false,
      loadingStreams: false,
      savingStream: false,
      deletingStream: false,
      streamTotalCount: 0,
      streamPerPage: 20,
      streamCurrentPage: 1,
      streamSearchQuery: "",
      visibilityQualification: false,
      loadingQualifications: false,
      savingQualification: false,
      qualificationInput: "",
      qualificationEditingOld: null,
    };
  },
  mounted() {
    this.$refs.wizard.activateAll();
    // window.addEventListener("keydown", async (event) => {
    // });
    window.addEventListener("keydown", this.handleEvent);

    // Load staff for report to dropdown
    this.loadReportToStaff();
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.handleEvent);
  },
  async created() {
    // console.log("----------", this.$route.params.id);
    // console.log(this.$store.state.rights["manage-staffgrid"]);
    if (this.showUserLinkField) {
      this.loadIussUsers();
    }
    let right = this.$store.state.rights["manage-staffgrid"];
    if (!right) {
      this.$router.replace({
        name: "misc-not-authorized",
      });
    } else {
      this.formRights = right;
      this.$route.params.id = parseInt(this.$route.params.id);

      if (this.$route.params.id > 0 && right.edit) {
        this.localID = this.$route.params.id;
        // Load stream options first so the All Streams sentinel binds correctly
        await this.LoadStreamSelectedData();
        await this.LoadData(this.$route.params.id);
        // will be called after staff load
        // this.LoadInfo();
        // this.LoadSettings();
        this.LoadSettingsAll();
        this.LoadTeachingClasses();
        this.loadAllowances();
        this.loadDeductions();
        this.loadAppRights();
        await this.loadBreakup();
        this.LoadCampuses(); // Load campuses for salary campus dropdown
        this.loadDesignationSettings(); // Load designation settings for edit mode
        this.loadReportToStaff(); // Load staff for report to dropdown
        this.LoadQualificationsForDropdown();
      } else if (this.$route.params.id == 0 && right.add) {
        await this.LoadDep();
        this.LoadSettingsAll();
        this.LoadSettings();
        this.LoadInfo();
        this.loadAllowances();
        this.loadDeductions();
        await this.loadBreakup();
        this.policyOnAdd();
        this.loadDesignationSettings();
        this.LoadCampuses(); // Load campuses for salary campus dropdown
        await this.LoadStreamSelectedData(); // Load streams for dropdown
        this.LoadQualificationsForDropdown();
        if (this.reportDomain == "myskoolhpgs") {
          this.loadAccounts();
          // if (this.$store.state.userData.cId == 1) {
          //   this.LoadCampuses();
          // }
        }
        //this.loadAllowances("set");
        //this.loadDeductions("set");
        // this.AddingInfo = [];
        // this.deductInfo = [];
        this.loanData = [];
        this.history = [];
        this.deposits = [];
        this.changeDay();
        // console.log(this.myObj);
      } else {
        this.$router.replace({
          name: "misc-not-authorized",
        });
      }
    }
    // console.log(this.$store.state.campusID);
  },
  computed: {
    showHappinessFoundation() {
      return this.reportDomain === "myskoolhappinessfoundation";
    },
    staffPicturePreview() {
      if (!this.plus) return "";
      if (String(this.plus).startsWith("blob:")) return this.plus;
      return getMediaPreviewUrl(this.plus, "Image");
    },
    staffObjPicturePreview() {
      if (!this.myObj || !this.myObj.picture) return "";
      return getMediaPreviewUrl(this.myObj.picture, "Image");
    },
    showUserLinkField() {
      return [
        "myskooliuss",
        "myskool",
        "myskoolcrestviewacademy",
        "myskoolfgs",
        "myskoolconceptthatta",
        "myskoolperfectthatta",
      ].includes(this.reportDomain);
    },
    isMyskoolOrIuss() {
      return (
        this.reportDomain === "myskool" || this.reportDomain === "myskooliuss"
      );
    },
    isMyskoolStJacobDomain() {
      return String(this.reportDomain || "").toLowerCase() === "myskoolstjacob";
    },
    pakistanProvinces() {
      return [
        "Punjab",
        "Sindh",
        "Khyber Pakhtunkhwa",
        "Balochistan",
        "Islamabad Capital Territory",
        "Gilgit-Baltistan",
        "Azad Jammu & Kashmir",
      ];
    },
    designationOptions() {
      const depId = Number(this.myObj && this.myObj.departmentID) || 0;
      if (!depId) return [];

      const all = (this.designations || []).map((item) => ({
        ...item,
        name: String(item.name || "").trim(),
        departmentID: Number(
          item.departmentID != null
            ? item.departmentID
            : item.departmentId != null
            ? item.departmentId
            : 0
        ),
      }));

      // Exact department match
      const matched = all.filter((item) => item.departmentID === depId);
      if (matched.length) return matched;

      // If nothing is department-linked yet (legacy data), show all
      const hasLinked = all.some((item) => item.departmentID > 0);
      if (!hasLinked) return all.filter((item) => item.name);

      // No designations saved for this department
      return [];
    },
    basicPlusAllowances() {
      let total = this.AddingInfo.reduce(
        (sum, el) =>
          el.allowanceID > 0 && el.amount ? sum + parseInt(el.amount) : sum + 0,
        0
      );
      let basic = this.myObj.basicSalary ? parseInt(this.myObj.basicSalary) : 0;
      return total + basic;
    },

    totalDeposits() {
      return this.calculateFilteredDeposits(() => true);
    },
    currentDeposits() {
      return this.calculateFilteredDeposits((el) => el.salaryID != null);
    },

    remainingDeposits() {
      return this.calculateFilteredDeposits((el) => el.salaryID == null);
    },

    returnDeposits() {
      return this.calculateFilteredDeposits(
        (el) => el.isReturned != null && el.isReturned == true
      );
    },
  },
  methods: {
    ...mapActions({
      get: "get",
      post: "post",
      postObj: "postObj",
      put: "put",
      delete: "delete",
    }),

    onDrivePhotoPreviewError(event, url) {
      if (url && String(url).startsWith("blob:")) return;
      handleDrivePreviewError(event, url, "image");
    },

    qualificationsApiQuery() {
      const db = encodeURIComponent(this.$store.state.userData.db);
      const cID = this.$store.state.userData.cId;
      return `?db=${db}&cID=${cID}`;
    },

    /** Settings qualification endpoints (see api/Settings/GetQualifications, etc.). */
    qualificationsSettingsUrl(action) {
      return `${this.$store.state.domain}Settings/${action}${this.qualificationsApiQuery()}`;
    },

    parseQualificationsApiPayload(payload) {
      if (payload == null || payload === "") return [];
      if (typeof payload === "string") {
        return [
          ...new Set(
            payload
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          ),
        ];
      }
      if (Array.isArray(payload)) {
        return [
          ...new Set(
            payload.map((s) => String(s).trim()).filter(Boolean)
          ),
        ];
      }
      if (typeof payload === "object") {
        if (Array.isArray(payload.items)) {
          return [
            ...new Set(
              payload.items.map((s) => String(s).trim()).filter(Boolean)
            ),
          ];
        }
        if (payload.value != null) {
          return this.parseQualificationsApiPayload(payload.value);
        }
        if (payload.data != null) {
          return this.parseQualificationsApiPayload(payload.data);
        }
        if (payload.status === "success" && payload.data != null) {
          return this.parseQualificationsApiPayload(payload.data);
        }
      }
      return [];
    },

    rebuildQualificationDropdownOptions() {
      const presetSet = new Set(DEFAULT_QUALIFICATION_OPTIONS);
      const extras = this.qualificationsServerList.filter(
        (x) => x && !presetSet.has(x)
      );
      extras.sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: "base" })
      );
      this.qualificationOptions = [...DEFAULT_QUALIFICATION_OPTIONS, ...extras];
    },

    async fetchQualificationsFromApi() {
      const url = this.qualificationsSettingsUrl("GetQualifications");
      const { data } = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${this.$store.state.userData.token}`,
          Accept: "application/json",
        },
      });
      return this.parseQualificationsApiPayload(data);
    },

    async LoadQualificationsForDropdown() {
      try {
        const list = await this.fetchQualificationsFromApi();
        this.qualificationsServerList = list;
        this.rebuildQualificationDropdownOptions();
      } catch (error) {
        console.error("Error loading qualifications:", error);
        this.qualificationsServerList = [];
        this.rebuildQualificationDropdownOptions();
      }
    },

    resetQualificationSidebarForm() {
      this.qualificationInput = "";
      this.qualificationEditingOld = null;
    },

    async openQualificationSidebar() {
      this.visibilityQualification = true;
      this.resetQualificationSidebarForm();
      this.loadingQualifications = true;
      try {
        const list = await this.fetchQualificationsFromApi();
        this.qualificationsServerList = list;
        this.rebuildQualificationDropdownOptions();
      } catch (error) {
        console.error("Error loading qualifications sidebar:", error);
      } finally {
        this.loadingQualifications = false;
      }
    },

    editQualificationSidebar(name) {
      this.qualificationEditingOld = name;
      this.qualificationInput = name;
    },

    async saveQualificationSidebar() {
      const name = (this.qualificationInput || "").trim();
      if (!name) return;

      this.savingQualification = true;
      try {
        const q = this.qualificationsApiQuery();
        const token = this.$store.state.userData.token;
        const headers = {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        };

        if (this.qualificationEditingOld) {
          const oldValue = this.qualificationEditingOld.trim();
          if (oldValue === name) {
            return;
          }
          const { data } = await axios.put(
            `${this.$store.state.domain}Settings/UpdateQualification${q}`,
            { oldValue, newValue: name },
            { headers }
          );
          const ok =
            data?.status === "success" ||
            data === true ||
            data?.data === true ||
            data?.data === "updated";
          if (!ok) throw new Error("update failed");
          if (this.myObj.qualification === oldValue) {
            this.myObj.qualification = name;
          }
        } else {
          const { data } = await axios.post(
            `${this.$store.state.domain}Settings/AddQualification${q}`,
            { value: name },
            { headers }
          );
          const ok =
            data?.status === "success" ||
            data === true ||
            data?.data === true;
          if (!ok) throw new Error("add failed");
        }

        this.$bvToast.toast(this.$t("Saved successfully"), {
          title: this.$t("Success"),
          variant: "success",
          toaster: "b-toaster-top-center",
        });
        this.resetQualificationSidebarForm();
        await this.LoadQualificationsForDropdown();
      } catch (error) {
        console.error("Error saving qualification:", error);
        this.$bvToast.toast(this.$t("Request failed."), {
          title: this.$t("Error"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      } finally {
        this.savingQualification = false;
      }
    },

    async removeQualificationSidebar(name) {
      const value = (name || "").trim();
      if (!value) return;

      const result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("Remove this qualification from the list?"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("Yes, remove it"),
        cancelButtonText: this.$t("Cancel"),
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      });

      if (!result.isConfirmed) return;

      this.loadingQualifications = true;
      try {
        const q = this.qualificationsApiQuery();
        const { data } = await axios.post(
          `${this.$store.state.domain}Settings/RemoveQualification${q}`,
          { value },
          {
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const ok =
          data?.status === "success" ||
          data === true ||
          data?.data === true;
        if (!ok) throw new Error("remove failed");

        if (this.myObj.qualification === value) {
          this.myObj.qualification = "";
        }

        this.$bvToast.toast(this.$t("Removed successfully"), {
          title: this.$t("Success"),
          variant: "success",
          toaster: "b-toaster-top-center",
        });
        await this.LoadQualificationsForDropdown();
      } catch (error) {
        console.error("Error removing qualification:", error);
        this.$bvToast.toast(this.$t("Request failed."), {
          title: this.$t("Error"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      } finally {
        this.loadingQualifications = false;
      }
    },

    normalizeStaffStreamID() {
      if (!this.myObj) return;
      this.$set(
        this.myObj,
        "streamID",
        normalizeStreamIdForUi(this.myObj.streamID)
      );
    },
    async LoadStreamSelectedData() {
      try {
        const url = `${this.$store.state.domain}Stream/LoadSelectedData?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}`;
        const obj = {
          url: url,
          token: this.$store.state.userData.token,
          context: this,
        };
        const response = await this.get(obj);
        let list = [];
        if (Array.isArray(response)) {
          list = response;
        } else if (response && Array.isArray(response.data)) {
          list = response.data;
        }
        // Prepend All Streams (sentinel id); drop any duplicate from API
        this.streamOptions = buildStreamOptions(
          list,
          this.$t("All Streams")
        );
        // Re-apply after options load so the sentinel option binds correctly
        this.normalizeStaffStreamID();
      } catch (error) {
        console.error("Error loading stream selected data:", error);
        this.streamOptions = buildStreamOptions([], this.$t("All Streams"));
        this.normalizeStaffStreamID();
      }
    },
    async LoadStreams() {
      this.loadingStreams = true;
      try {
        const url = `${this.$store.state.domain}Stream/GetByPagination?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}&pageNumber=${this.streamCurrentPage}&pageSize=${this.streamPerPage}&search=${this.streamSearchQuery}&status=active&sortBy=addedAt&sortOrder=desc`;
        const obj = {
          url: url,
          token: this.$store.state.userData.token,
          context: this,
        };
        const response = await this.get(obj);
        if (response && response.data) {
          this.streams = response.data;
          this.streamTotalCount = response.totalCount || this.streams.length;
        } else if (Array.isArray(response)) {
          this.streams = response;
          this.streamTotalCount = this.streams.length;
        }
      } catch (error) {
        console.error("Error loading streams:", error);
      }
      this.loadingStreams = false;
    },
    openStreamSidebar() {
      this.visibilityStream = true;
      this.streamObj = { id: 0, name: "", campusID: this.$store.state.userData.cId, status: "active" };
      this.LoadStreams();
    },
    editStream(stream) {
      this.streamObj = { ...stream };
    },
    async saveStream() {
      if (!this.streamObj.name) {
        this.$bvToast.toast("Please enter stream name", {
          title: "Error",
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        return;
      }
      this.savingStream = true;
      try {
        const url = `${this.$store.state.domain}Stream/Save?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}&userID=${this.$store.state.userData.userID}`;
        const obj = {
          url: url,
          body: this.streamObj,
          token: this.$store.state.userData.token,
          context: this,
        };
        const response = await this.post(obj);
        if (response && (response.status === "success" || response === true || response.id > 0)) {
          this.$bvToast.toast("Stream saved successfully", {
            title: "Success",
            variant: "success",
            toaster: "b-toaster-top-center",
          });
          this.streamObj = { id: 0, name: "", campusID: this.$store.state.userData.cId, status: "active" };
          this.LoadStreams();
          this.LoadStreamSelectedData();
        } else {
          throw new Error("Failed to save stream");
        }
      } catch (error) {
        console.error("Error saving stream:", error);
        this.$bvToast.toast("Error saving stream", {
          title: "Error",
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      }
      this.savingStream = false;
    },
    async deleteStream(id) {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You want to delete this stream!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        customClass: { confirmButton: "btn btn-primary", cancelButton: "btn btn-outline-danger ml-1" },
        buttonsStyling: false,
      });

      if (!result.isConfirmed) return;

      this.deletingStream = true;
      try {
        const url = `${this.$store.state.domain}Stream/Delete/${id}?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}&userID=${this.$store.state.userData.userID}`;
        const obj = {
          url: url,
          token: this.$store.state.userData.token,
          context: this,
        };
        const response = await this.delete(obj);
        if (response && (response.status === "success" || response === true)) {
          this.$bvToast.toast("Stream deleted successfully", {
            title: "Success",
            variant: "success",
            toaster: "b-toaster-top-center",
          });
          this.LoadStreams();
          this.LoadStreamSelectedData();
        }
      } catch (error) {
        console.error("Error deleting stream:", error);
      }
      this.deletingStream = false;
    },

    setCOA(el) {
      if (el == null || el == "") this.linkObj.valueNumber = 0;
    },

    // Helper method to calculate filtered deposits (respecting rejoining date)
    calculateFilteredDeposits(filterFn) {
      const filtered = this.deposits.filter(filterFn);
      if (!this.myObj.rejoining_date) {
        return filtered.reduce((sum, el) => sum + el.amount, 0);
      }

      const rejoiningDate = new Date(this.myObj.rejoining_date);
      const rejoiningYear = rejoiningDate.getFullYear();
      const rejoiningMonth = rejoiningDate.getMonth() + 1;
      const monthMap = {
        jan: 1,
        feb: 2,
        mar: 3,
        apr: 4,
        may: 5,
        jun: 6,
        jul: 7,
        aug: 8,
        sep: 9,
        oct: 10,
        nov: 11,
        dec: 12,
      };
      const getMonthNumber = (m) =>
        monthMap[m?.toString().trim().substring(0, 3).toLowerCase()] ||
        parseInt(m) ||
        0;

      return filtered.reduce((sum, el) => {
        const depositYear = parseInt(el.year);
        const depositMonth = getMonthNumber(el.month);
        const isOnOrAfter =
          depositYear > rejoiningYear ||
          (depositYear === rejoiningYear && depositMonth >= rejoiningMonth);
        return isOnOrAfter ? sum + el.amount : sum;
      }, 0);
    },

    // Load staff for Report To dropdown using SearchStaff API
    async loadReportToStaff() {
      this.reportToLoading = true;
      try {
        const obj = {
          url: `${this.$store.state.domain}Staff/SearchStaff?db=${this.$store.state.userData.db}&name=`,
          token: this.$store.state.userData.token,
        };

        const response = await this.get(obj);

        // Handle different response structures and add unique keys
        let staffData = [];
        if (response && response.data) {
          staffData = response.data;
        } else if (response && Array.isArray(response)) {
          staffData = response;
        }

        // Add unique keys and ensure proper structure
        this.reportToOptions = staffData.map((staff, index) => ({
          ...staff,
          _uniqueKey: staff.id || `staff_${index}`, // Ensure unique key
          name: staff.name || "Unknown Staff",
          id: staff.id || index,
        }));
      } catch (error) {
        console.error("Error loading staff for report to:", error);
        this.reportToOptions = [];
      } finally {
        this.reportToLoading = false;
      }
    },

    // Search staff for Report To dropdown using SearchStaff API
    async searchReportToStaff(search) {
      if (search.length < 2) {
        // Load all staff if search is too short
        this.loadReportToStaff();
        return;
      }

      this.reportToLoading = true;
      try {
        const obj = {
          url: `${this.$store.state.domain}Staff/SearchStaff?db=${
            this.$store.state.userData.db
          }&name=${encodeURIComponent(search)}`,
          token: this.$store.state.userData.token,
        };

        const response = await this.get(obj);

        // Handle different response structures and add unique keys
        let staffData = [];
        if (response && response.data) {
          staffData = response.data;
        } else if (response && Array.isArray(response)) {
          staffData = response;
        }

        // Add unique keys and ensure proper structure
        this.reportToOptions = staffData.map((staff, index) => ({
          ...staff,
          _uniqueKey: staff.id || `staff_${index}`, // Ensure unique key
          name: staff.name || "Unknown Staff",
          id: staff.id || index,
        }));
      } catch (error) {
        console.error("Error searching staff for report to:", error);
        this.reportToOptions = [];
      } finally {
        this.reportToLoading = false;
      }
    },
    async loadIussUsers() {
      if (!this.showUserLinkField) return;
      this.iussUsersLoading = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Users/LoadUsers?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        const rawList = (response && response.data) || response || [];
        this.iussUserOptions = Array.isArray(rawList)
          ? rawList.map((user, index) => {
              const userId = user.id != null ? Number(user.id) : index;
              return {
                id: userId,
                label:
                  user.name && user.meta_role
                    ? `${user.name} (${user.meta_role})`
                    : user.name ||
                      user.meta_role ||
                      user.username ||
                      `User ${userId}`,
              };
            })
          : [];
      } catch (error) {
        console.error("Error loading IUSS users:", error);
        this.iussUserOptions = [];
      } finally {
        this.iussUsersLoading = false;
      }
    },

    // Handle Report To selection change
    onReportToChange(selectedStaff) {
      if (selectedStaff && selectedStaff.length > 0) {
        // Extract staff IDs and join with commas
        const staffIDs = selectedStaff.map((staff) => staff.id).join(",");
        this.$set(this.myObj, "reportsTo", staffIDs);
        this.$set(this.myObj, "reportTo", selectedStaff);
      } else {
        this.$set(this.myObj, "reportsTo", "");
        this.$set(this.myObj, "reportTo", []);
      }

      // Force re-render to ensure UI reflects changes
      this.reportToKey++;
      this.$nextTick(() => {
        if (this.$refs.reportToSelect) {
          this.$refs.reportToSelect.$forceUpdate();
        }
      });
    },

    // Load selected staff for edit mode using SearchStaff API with IDs from reportsTo
    async loadSelectedStaffForEdit() {
      if (
        this.myObj.reportsTo &&
        this.myObj.reportsTo.toString().trim() !== ""
      ) {
        // Get the IDs from reportsTo (can be single ID or comma-separated)
        const reportsToIds = this.myObj.reportsTo.toString().trim();
        const staffIDs = reportsToIds
          .split(",")
          .filter((id) => id.trim() !== "");

        if (staffIDs.length === 0) {
          return;
        }

        this.reportToLoading = true;

        try {
          // Pass ID to SearchStaff API in the name parameter
          // API format: Staff/SearchStaff?db=...&name=254
          const idsParam = staffIDs.join(",");

          // Call API with ID in name parameter
          const obj = {
            url: `${this.$store.state.domain}Staff/SearchStaff?db=${this.$store.state.userData.db}&name=${idsParam}`,
            token: this.$store.state.userData.token,
          };

          let response = await this.get(obj);

          // Handle different response structures
          let staffData = [];
          if (response && response.data) {
            if (Array.isArray(response.data)) {
              staffData = response.data;
            } else if (response.data.id) {
              // Single staff object
              staffData = [response.data];
            }
          } else if (response && Array.isArray(response)) {
            staffData = response;
          } else if (
            response &&
            response.status === "success" &&
            response.data === null
          ) {
            // API returned success but empty data - fallback: load all and filter
            const fallbackObj = {
              url: `${this.$store.state.domain}Staff/SearchStaff?db=${this.$store.state.userData.db}&name=`,
              token: this.$store.state.userData.token,
            };
            const fallbackResponse = await this.get(fallbackObj);
            if (fallbackResponse && fallbackResponse.data) {
              staffData = Array.isArray(fallbackResponse.data)
                ? fallbackResponse.data
                : [];
            } else if (Array.isArray(fallbackResponse)) {
              staffData = fallbackResponse;
            }
          }

          // Filter to only include staff with matching IDs
          const matchingStaff = staffData.filter((staff) => {
            const staffId = staff.id ? staff.id.toString() : "";
            return staffIDs.includes(staffId);
          });

          // Add unique keys and ensure proper structure
          const selectedStaff = matchingStaff.map((staff, index) => ({
            ...staff,
            _uniqueKey: staff.id || `staff_${index}`,
            name: staff.name || "Unknown Staff",
            id: staff.id || index,
          }));

          // Also add to reportToOptions for future reference
          selectedStaff.forEach((staff) => {
            const exists = this.reportToOptions.find(
              (opt) => opt.id === staff.id
            );
            if (!exists) {
              this.reportToOptions.push(staff);
            }
          });

          // Set the selected staff to myObj.reportTo for the dropdown
          this.$set(this.myObj, "reportTo", selectedStaff);

          // Force re-render after loading
          this.reportToKey++;
        } catch (error) {
          console.error("Error loading selected staff for edit:", error);
          // Fallback: try loading all staff and filtering
          try {
            await this.loadReportToStaff();
            const selectedStaff = this.reportToOptions.filter((staff) => {
              const staffId = staff.id ? staff.id.toString() : "";
              return staffIDs.includes(staffId);
            });
            if (selectedStaff.length > 0) {
              this.$set(this.myObj, "reportTo", selectedStaff);
              this.reportToKey++;
            }
          } catch (fallbackError) {
            console.error("Fallback loading also failed:", fallbackError);
          }
        } finally {
          this.reportToLoading = false;
        }
      }
    },

    setDepTimings() {
      if (
        this.myObj.id == 0 &&
        this.myObj.departmentID > 0 &&
        ["myskoolstanmore", "myskooliuss", "myskool", "myskoolhpgs"].includes(
          this.reportDomain
        )
      ) {
        this.LoadTimings();
      }
      // Keep designation only if still valid for selected department
      if (this.myObj.designation) {
        const stillValid = this.designationOptions.some(
          (d) => d.name === this.myObj.designation
        );
        if (!stillValid) this.myObj.designation = "";
      }
      this.checkDep();
    },

    setDesignation() {
      // Guard: never keep settings JSON / array blobs in staff.designation
      this.sanitizeStaffDesignation();
    },

    isCorruptDesignationValue(value) {
      if (value == null) return false;
      if (Array.isArray(value) || typeof value === "object") return true;
      const s = String(value).trim();
      if (!s) return false;
      // Accidentally saved staff-designations setting JSON into staff.designation
      if (s.startsWith("[") || s.startsWith("{")) return true;
      if (s.includes('"departmentID"') || s.includes('"departmentId"'))
        return true;
      if (s.length > 80) return true;
      return false;
    },

    sanitizeStaffDesignation() {
      if (!this.myObj) return;
      const raw = this.myObj.designation;
      if (!this.isCorruptDesignationValue(raw)) {
        if (typeof raw === "string") this.myObj.designation = raw.trim();
        return;
      }
      console.warn(
        "Corrupt staff designation value cleared (looked like settings JSON):",
        typeof raw === "string" ? raw.slice(0, 120) : raw
      );
      this.myObj.designation = "";
    },

    async LoadTimings() {
      var obj = {
        url:
          this.$store.state.domain +
          "StaffDepartmentTimings/LoadData?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.myObj.campusID +
          "&depID=" +
          this.myObj.departmentID,
        token: this.$store.state.userData.token,
      };
      let res = await this.get(obj);
      // console.log(res);
      if (res.length > 0) {
        this.timingInfo = res.map((el) => ({
          id: 0,
          tID: this.myObj.id,
          reportingTime: el.reportingTime,
          day: el.day,
          timeIn: el.timeIn,
          timeOut: el.timeOut,
          dayOff: el.dayOff,
          campusID: this.myObj.campusID,
        }));
        this.sortTimingInfoByDay();
      } else {
        this.timingInfo = [
          {
            id: 0,
            tID: this.myObj.id,
            day: "monday",
            timeIn: "2024-05-05T08:00:00",
            timeOut: "2024-05-05T13:00:00",
            dayOff: false,
            reportingTime: "2024-05-05T08:00:00",
            campusID: this.myObj.campusID,
          },
        ];
      }
      // console.log(this.timingInfo);
    },

    stepChanged(prev, next) {
      console.log(prev, next);
    },

    setLeaveAllowed() {
      if (
        this.reportDomain == "myskoolhpgs" &&
        this.myObj.appointedOn &&
        !this.myObj.leaves_allowed_from
      ) {
        let appointedDate = new Date(this.myObj.appointedOn);
        let leaveAllowedDate = new Date(
          appointedDate.setMonth(appointedDate.getMonth() + 3)
        );
        this.myObj.leaves_allowed_from = leaveAllowedDate
          .toJSON()
          .split("T")[0];
        // console.log(this.myObj.leaves_allowed_from);
      }
    },

    async loadAccounts() {
      var obj2 = {
        url:
          this.$store.state.domain +
          "CollectionAccounts?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      let result = await this.get(obj2);
      // console.log("acc", result);
      if (result !== "NotFound") this.accTypes = result;
      else this.accTypes = [];
      if (this.reportDomain == "myskoolhpgs" && this.myObj.id > 0) {
        this.setBranches();
      }
    },
    async setBranches() {
      if (this.myObj.accountID) {
        let item = this.accTypes.find((el) => el.id == this.myObj.accountID);
        if (
          item &&
          !["cash", "cheque", "paypro", "online"].some((word) =>
            item.account.toLowerCase().includes(word)
          )
        ) {
          this.disBranch = false;
          this.branchLoading = true;
          var obj = {
            url:
              this.$store.state.domain +
              "CollectionAccountBranch/LoadData?db=" +
              this.$store.state.userData.db +
              "&accID=" +
              item.id +
              "&campID=" +
              this.myObj.campusID,
            token: this.$store.state.userData.token,
          };
          this.branchOptions = await this.get(obj);

          this.branchLoading = false;
        } else this.disBranch = true;
      }
    },
    setAccount() {
      let item = null;
      if (this.myObj.payMode == "Cash Salary") {
        item = this.accTypes.find(
          (el) => el.account.toLowerCase().trim() == "cash"
        );
      } else if (this.myObj.payMode == "Cheque Salary") {
        item = this.accTypes.find(
          (el) => el.account.toLowerCase().trim() == "cheque"
        );
      }

      if (item) {
        this.myObj.accountID = item.id;
        this.myObj.branchID = 0;
        this.branchOptions = [];
        this.disBranch = true;
      } else this.disBranch = false;
    },
    async LoadBranches() {
      this.myObj.branchID = 0;
      this.branchOptions = [];
      let item = this.accTypes.find((el) => el.id == this.myObj.accountID);
      if (
        item &&
        !["cash", "cheque", "paypro", "online"].some((word) =>
          item.account.toLowerCase().includes(word)
        )
      ) {
        this.disBranch = false;
        this.branchLoading = true;
        var obj = {
          url:
            this.$store.state.domain +
            "CollectionAccountBranch/LoadData?db=" +
            this.$store.state.userData.db +
            "&accID=" +
            item.id +
            "&campID=" +
            this.myObj.campusID,
          token: this.$store.state.userData.token,
          dashboard: true,
        };
        let res = await this.get(obj);
        if (res.status == "success") {
          this.branchOptions = res.data;
          this.myObj.branchID = res.selectedID;
        }
        this.branchLoading = false;
      } else this.disBranch = true;
    },
    openPSett() {
      if (this.pObj) {
        this.pCopy = { ...this.pObj };
      } else {
        this.pCopy = {
          id: 0,
          campusID: this.$store.state.userData.cId,
          key: "payroll_share",
          value: "payroll_share",
          valueBool: false,
          valueNumber: null,
        };
      }
      this.visibility7 = true;
      // console.log(this.pCopy);
    },
    async savePSett() {
      this.savingSett = true;
      var status = await this.post({
        url:
          this.$store.state.domain +
          "Settings/SaveNew?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        body: [this.pCopy],
        message: "Settings saved successfully.",
        context: this,
        token: this.$store.state.userData.token,
      });
      this.savingSett = false;
      if (status) {
        this.LoadSettings("check");
        this.visibility7 = false;
      }
    },

    async LoadPSubs() {
      var obj = {
        url:
          this.$store.state.domain +
          "Subjects?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.pSubjects = await this.get(obj);
    },
    FillPSec(item, ind, text) {
      if (text != "filled") item.secID = 0;
      let cls = this.classes.find((el) => el.cls.id == item.clsID);
      if (cls) this.pSections.splice(ind, 1, cls.sections);
      else this.pSections.splice(ind, 1, []);
      // console.log(this.pSections);
      // this.allowCls()
    },
    allowCls() {
      let data = [];
      this.pClassData.forEach((elem) => {
        let cls = this.classes.find((el) => el.cls.id == elem.clsID);
        if (cls && cls.section.length > 0) {
        }
      });
      this.selectedCls = data;
    },
    AddP() {
      this.pClassData.push({
        id: 0,
        tID: this.$route.params.id,
        clsID: 0,
        secID: 0,
        subjectID: 0,
        baseValue: 0,
        startingFrom: null,
        sharePerStudent: 0,
        campusID: this.$store.state.userData.cId,
      });
      this.pSections.push([]);
    },
    async removeP(item, ind) {
      if (item.id == 0) {
        this.pClassData.splice(ind, 1);
        this.pSections.splice(ind, 1);
      } else {
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "staffClassShare/" +
            item.id +
            "?db=" +
            this.$store.state.userData.db,
          body: null,
          grading: true,
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) {
          this.pClassData.splice(ind, 1);
          this.pSections.splice(ind, 1);
        }
      }
      // console.log(this.pSections);
    },
    setShareData() {
      // console.log(this.payrollByShare);
      if (this.payrollByShare && this.pClassData.length == 0) {
        this.AddP();
      }
    },

    async loadBreakup() {
      var obj = {
        url:
          this.$store.state.domain +
          "SalaryBreakupPolicy?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.breakupData = await this.get(obj);
      // console.log(this.breakupData);

      // Load separate policy data for myskooliuss and myskool domains
      if (
        this.reportDomain === "myskooliuss" ||
        this.reportDomain === "myskool"
      ) {
        await this.loadDeductionTypes();
        await this.loadDeductionPolicyData();
        this.initializeDeductionPolicy();

        await this.loadContributionTypes();
        await this.loadContributionPolicyData();
        this.initializeContributionPolicy();
      }
    },
    async LoadCampuses() {
      var obj = {
        url:
          this.$store.state.domain +
          "campuses?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.campuses = await this.get(obj);
    },
    openSettings() {
      this.visibility6 = true;
      if (this.breakupData.length == 0) {
        this.policySett = [
          {
            id: 0,
            allowanceTypeID: 0,
            perc: 100,
            campusID: this.$store.state.userData.cId,
          },
        ];
      } else this.policySett = this.breakupData.map((el) => ({ ...el }));
      // console.log(this.breakupData);
      this.changeSettAllow();
    },
    changeSettAllow() {
      this.SettIDs = this.policySett.reduce((acc, el) => {
        acc.push(el.allowanceTypeID);
        return acc;
      }, []);
    },
    addSett(item) {
      this.policySett.push({
        id: 0,
        allowanceTypeID: null,
        perc: 0,
        campusID: this.$store.state.userData.cId,
      });
      // console.log(this.policySett);
    },
    async removeSett(item, ind) {
      if (item.id == 0) {
        this.policySett.splice(ind, 1);
        this.changeSettAllow();
      } else {
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "SalaryBreakupPolicy/" +
            item.id +
            "?db=" +
            this.$store.state.userData.db,
          body: null,
          grading: true,
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) {
          this.policySett.splice(ind, 1);
          this.breakupData.splice(ind, 1);
          this.changeSettAllow();
        }
      }
    },
    checkSett() {
      let state = true;
      this.policySett.forEach((el) => {
        el.perc = parseFloat(el.perc);
        if (el.allowanceTypeID == null || isNaN(el.perc) || el.perc < 0) {
          state = false;
        }
      });
      return state;
    },
    checkSum() {
      let total = this.policySett.reduce((sum, el) => sum + el.perc, 0);
      return total == 100;
    },
    async saveSett() {
      if (this.checkSett() == false) {
        this.$bvToast.toast(this.$t("Please enter the details correctly!"), {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      } else if (this.checkSum() == false) {
        this.$bvToast.toast(this.$t("The total % should be equal to 100."), {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      } else {
        // console.log(this.policySett);
        this.savingSett = true;
        var status = await this.post({
          url:
            this.$store.state.domain +
            "SalaryBreakupPolicy/saveData?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          body: this.policySett,
          message: "Settings saved successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });
        this.savingSett = false;
        if (status) {
          await this.loadBreakup();
          this.visibility6 = false;
        }
      }
    },

    // Combined Policy Methods
    async loadAllPolicyTypes() {
      try {
        // Try different API endpoints based on domain
        let apiEndpoint = "Deductions";
        if (this.reportDomain === "myskool") {
          // For myskool domain, try both Deductions and Contributions endpoints
          apiEndpoint = "Deductions";
        }

        var obj = {
          url:
            this.$store.state.domain +
            apiEndpoint +
            "?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        };

        const response = await this.get(obj);
        console.log(
          `Policy types response for domain ${this.reportDomain} from ${apiEndpoint}:`,
          response
        );

        let rawData = [];
        if (response && Array.isArray(response)) {
          rawData = response;
        } else if (response && response.data && Array.isArray(response.data)) {
          rawData = response.data;
        } else {
          console.log("No policy types found in response");
        }

        // If no contribution types found and we're on myskool domain, try Contributions endpoint
        if (this.reportDomain === "myskool" && rawData.length > 0) {
          const hasContributions = rawData.some(
            (item) =>
              item.title === "PF Contribution" ||
              item.title === "EOBI Contribution"
          );

          if (!hasContributions) {
            console.log(
              "No contribution types found in Deductions, trying Contributions endpoint..."
            );
            try {
              const contribObj = {
                url:
                  this.$store.state.domain +
                  "Contributions?db=" +
                  this.$store.state.userData.db +
                  "&cID=" +
                  this.$store.state.userData.cId,
                token: this.$store.state.userData.token,
              };

              const contribResponse = await this.get(contribObj);
              console.log("Contributions API response:", contribResponse);

              let contribData = [];
              if (contribResponse && Array.isArray(contribResponse)) {
                contribData = contribResponse;
              } else if (
                contribResponse &&
                contribResponse.data &&
                Array.isArray(contribResponse.data)
              ) {
                contribData = contribResponse.data;
              }

              // Merge contribution data with deduction data
              rawData = [...rawData, ...contribData];
            } catch (contribError) {
              console.log("Contributions API not available:", contribError);
            }
          }
        }

        // Format data for v-select component
        this.allPolicyTypes = rawData.map((item) => ({
          id: item.id,
          title: item.title,
          name: item.title,
        }));

        // Filter and log contribution types specifically
        const contributionTypes = this.allPolicyTypes.filter(
          (type) =>
            type.title === "PF Contribution" ||
            type.title === "EOBI Contribution"
        );
        console.log("Contribution types found:", contributionTypes);

        // If no contribution types found, add them manually for myskool domain
        if (contributionTypes.length === 0 && this.reportDomain === "myskool") {
          console.log(
            "No contribution types found, adding them manually for myskool domain"
          );
          const manualContributions = [
            { id: 1001, title: "PF Contribution", name: "PF Contribution" },
            { id: 1002, title: "EOBI Contribution", name: "EOBI Contribution" },
          ];
          this.allPolicyTypes = [
            ...this.allPolicyTypes,
            ...manualContributions,
          ];
          console.log("Added manual contribution types:", manualContributions);
        }

        console.log("All policy types loaded:", this.allPolicyTypes);
      } catch (error) {
        console.error("Error loading policy types:", error);
        this.allPolicyTypes = [];
      }
    },
    async loadAllPolicyData() {
      try {
        var obj = {
          url:
            this.$store.state.domain +
            "DeductionPolicy?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        };

        const response = await this.get(obj);
        console.log(
          `Policy data response for domain ${this.reportDomain}:`,
          response
        );

        let rawData = [];
        if (response && Array.isArray(response)) {
          rawData = response;
        } else if (response && response.data && Array.isArray(response.data)) {
          rawData = response.data;
        } else {
          console.log("No policy data found in response");
        }

        // Load all data and separate by type
        this.allPolicyData = rawData
          .filter(
            (item) => item && typeof item === "object" && item.id !== undefined
          ) // Filter out invalid entries
          .map((item) => {
            const policyType = this.allPolicyTypes.find(
              (type) => type.id === item.allowanceTypeID
            );
            return {
              ...item,
              policyType: policyType ? policyType.title : "Unknown",
              isContribution:
                policyType &&
                (policyType.title === "PF Contribution" ||
                  policyType.title === "EOBI Contribution"),
            };
          });

        console.log("Loaded all policy data:", this.allPolicyData);
      } catch (error) {
        console.error("Error loading policy data:", error);
        this.allPolicyData = [];
      }
    },
    initializeAllPolicy() {
      console.log("Initializing policy with data:", this.allPolicyData);

      if (!this.allPolicyData || this.allPolicyData.length == 0) {
        console.log("No policy data, creating default entry");
        this.allPolicySett = [
          {
            id: 0,
            allowanceTypeID: null,
            perc: 0,
            campusID: this.$store.state.userData.cId,
            eobiContribution: 0,
            PFcontribution: 0,
          },
        ];
      } else {
        console.log("Mapping existing policy data");
        // Map existing data to form structure
        this.allPolicySett = this.allPolicyData
          .filter((el) => el && typeof el === "object") // Filter out invalid entries
          .map((el) => ({
            id: el.id || 0,
            allowanceTypeID: el.allowanceTypeID || null,
            perc: el.perc || 0,
            campusID: el.campusID || this.$store.state.userData.cId,
            eobiContribution: el.eobiContribution || 0,
            PFcontribution: el.PFcontribution || 0,
          }));
      }

      // Ensure we always have at least one entry
      if (this.allPolicySett.length === 0) {
        console.log("No entries after mapping, creating default");
        this.allPolicySett = [
          {
            id: 0,
            allowanceTypeID: null,
            perc: 0,
            campusID: this.$store.state.userData.cId,
            eobiContribution: 0,
            PFcontribution: 0,
          },
        ];
      }

      console.log("Final policy sett:", this.allPolicySett);
    },
    getPolicyLabel(item) {
      const policyType = this.allPolicyTypes.find(
        (type) => type.id === item.allowanceTypeID
      );
      if (policyType) {
        if (
          policyType.title === "PF Contribution" ||
          policyType.title === "EOBI Contribution"
        ) {
          return "Contribution";
        } else {
          return "Deduction";
        }
      }
      return "Policy Type";
    },
    changeAllPolicySettAllow() {
      this.allPolicySettIDs = this.allPolicySett.reduce((acc, el) => {
        if (el.allowanceTypeID) {
          acc.push(el.allowanceTypeID);
        }
        return acc;
      }, []);
    },
    addAllPolicySett(item) {
      this.allPolicySett = [
        ...this.allPolicySett,
        {
          id: 0,
          allowanceTypeID: null,
          perc: 0,
          campusID: this.$store.state.userData.cId,
          eobiContribution: 0,
          PFcontribution: 0,
        },
      ];
    },
    async removeAllPolicySett(item, ind) {
      if (item.id == 0) {
        // New item - just remove from form
        this.allPolicySett = this.allPolicySett.filter(
          (_, index) => index !== ind
        );
        this.changeAllPolicySettAllow();
      } else {
        // Existing item - delete from server
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "DeductionPolicy/" +
            item.id +
            "?db=" +
            this.$store.state.userData.db,
          body: null,
          grading: true,
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) {
          this.allPolicySett = this.allPolicySett.filter(
            (_, index) => index !== ind
          );
          // Update local data
          this.allPolicyData = this.allPolicyData.filter(
            (el) => el.id !== item.id
          );
          this.changeAllPolicySettAllow();
        }
      }
    },
    checkAllPolicySett() {
      let state = true;
      this.allPolicySett.forEach((el) => {
        el.perc = parseFloat(el.perc);
        if (el.allowanceTypeID == null || isNaN(el.perc) || el.perc < 0) {
          state = false;
        }
      });
      return state;
    },
    async saveAllPolicySett() {
      if (this.checkAllPolicySett() == false) {
        this.$bvToast.toast(this.$t("Please enter the details correctly!"), {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      } else {
        this.savingAllPolicySett = true;

        // Prepare data for API - map contribution types to correct fields
        const saveData = this.allPolicySett.map((item) => {
          const baseData = {
            id: item.id,
            allowanceTypeID: item.allowanceTypeID,
            perc: parseFloat(item.perc),
            campusID: item.campusID,
            eobiContribution: 0,
            PFcontribution: 0,
          };

          // Find the selected policy type
          const selectedType = this.allPolicyTypes.find(
            (type) => type.id === item.allowanceTypeID
          );
          if (selectedType) {
            if (selectedType.title === "PF Contribution") {
              baseData.PFcontribution = parseFloat(item.perc);
            } else if (selectedType.title === "EOBI Contribution") {
              baseData.eobiContribution = parseFloat(item.perc);
            }
          }

          return baseData;
        });

        console.log("Saving all policy data:", saveData);

        try {
          // Use the same API endpoint as deduction policy
          const response = await this.post({
            url:
              this.$store.state.domain +
              "DeductionPolicy/SaveData?db=" +
              this.$store.state.userData.db +
              "&cID=" +
              this.$store.state.userData.cId,
            body: saveData,
            message: "Policy saved successfully.",
            context: this,
            token: this.$store.state.userData.token,
          });

          if (response) {
            this.$bvToast.toast(this.$t("Policy saved successfully!"), {
              title: this.$t("Success!"),
              variant: "success",
              solid: true,
            });

            // Reload the data
            await this.loadAllPolicyData();
            this.initializeAllPolicy();
            this.changeAllPolicySettAllow();
          }
        } catch (error) {
          console.error("Error saving policy:", error);
          this.$bvToast.toast(this.$t("Error saving policy!"), {
            title: this.$t("Error!"),
            variant: "danger",
            solid: true,
          });
        } finally {
          this.savingAllPolicySett = false;
        }
      }
    },
    calculate() {
      if (this.breakupData.length > 0) {
        if (this.checkSalary() == true) {
          // console.log(this.breakupData);
          this.myObj.totalSalary = parseInt(this.myObj.totalSalary);

          this.breakupData.forEach((el) => {
            if (el.allowanceTypeID == 0) {
              this.myObj.basicSalary = parseInt(
                ((this.myObj.totalSalary * el.perc) / 100).toFixed(2)
              );
            } else {
              let obj = this.AddingInfo.find(
                (elem) => elem.allowanceID == el.allowanceTypeID
              );
              // console.log(obj);

              if (obj) {
                let ind = this.AddingInfo.findIndex(
                  (elem) => elem.allowanceID == el.allowanceTypeID
                );
                obj.amount = parseInt(
                  ((this.myObj.totalSalary * el.perc) / 100).toFixed(2)
                );

                this.AddingInfo.splice(ind, 1, obj);
              } else {
                let amount = parseInt(
                  ((this.myObj.totalSalary * el.perc) / 100).toFixed(2)
                );

                this.AddingInfo.push({
                  id: 0,
                  tID: this.$route.params.id,
                  amount: amount,
                  allowanceID: el.allowanceTypeID,
                  campusID: this.$store.state.userData.cId,
                });
                this.changeAllow();
              }
            }
          });
        }
      } else {
        this.$bvToast.toast(this.$t("Breakup Policy is not defined."), {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      }
    },

    async saveRights() {
      this.rightObj.rights = this.selectedRights.filter((el) => el).join(",");
      // console.log(this.selectedRights, this.rightObj);
      if (this.rightObj.id == 0) {
        this.savingRights = true;
        var status = await this.post({
          url:
            this.$store.state.domain +
            "TeachersAppRights?db=" +
            this.$store.state.userData.db,
          body: this.rightObj,
          message: "App rights saved successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });
        this.savingRights = false;
        if (status) this.loadAppRights();
      } else {
        this.savingRights = true;
        var status = await this.put({
          url:
            this.$store.state.domain +
            "TeachersAppRights/" +
            this.rightObj.id +
            "?db=" +
            this.$store.state.userData.db,
          message: "App rights saved successfully.",
          context: this,
          body: this.rightObj,
          token: this.$store.state.userData.token,
        });
        this.savingRights = false;
        if (status) this.loadAppRights();
      }
    },
    async loadAppRights() {
      var obj = {
        url:
          this.$store.state.domain +
          "TeachersAppRights/LoadData?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&tID=" +
          this.$route.params.id,
        token: this.$store.state.userData.token,
      };
      this.rightObj = await this.get(obj);
      // console.log(this.rightObj);
      if (!this.rightObj) {
        this.rightObj = {
          id: 0,
          tID: this.$route.params.id,
          rights: "",
          campusID: this.$store.state.userData.cId,
        };
        this.selectedRights = [
          "Homework",
          "Attendance",
          "Examination",
          "Resources",
          "Online Class",
          "Feedback",
          "Parent Consent",
        ];
      } else {
        this.selectedRights = this.rightObj.rights
          ? this.rightObj.rights.replace("Complaint", "Feedback").split(",")
          : [];
      }
    },
    async loadDesignationSettings() {
      try {
        const campusID = Number(
          this.myObj.campusID || this.$store.state.userData.cId
        );
        const response = await this.post({
          url: `${this.$store.state.domain}Settings/getSettingsAll?db=${this.$store.state.userData.db}`,
          body: ["staff-designations"],
          context: this,
          token: this.$store.state.userData.token,
          subjects: true,
        });

        let settingsArray = [];
        if (
          response &&
          response.status === "success" &&
          response.data &&
          Array.isArray(response.data)
        ) {
          settingsArray = response.data;
        } else if (Array.isArray(response)) {
          settingsArray = response;
        }

        this.designationSetting =
          settingsArray.find(
            (setting) =>
              setting.key === "staff-designations" &&
              Number(setting.campusID) === campusID
          ) ||
          settingsArray.find((setting) => setting.key === "staff-designations");

        if (this.designationSetting && this.designationSetting.value) {
          this.designations = this.normalizeDesignations(
            this.designationSetting.value
          );
        } else {
          this.designations = [];
        }

        this.showDesignationField = this.designations.length > 0;
      } catch (error) {
        console.error("Error loading designation settings:", error);
        this.designations = [];
        this.showDesignationField = false;
      }
    },
    normalizeDesignations(rawValue) {
      const readDepartmentID = (item) =>
        Number(
          item.departmentID != null
            ? item.departmentID
            : item.departmentId != null
            ? item.departmentId
            : item.DepartmentID != null
            ? item.DepartmentID
            : 0
        ) || 0;

      const toOption = (item, index) => {
        if (typeof item === "string") {
          const name = item.trim();
          // Support legacy encoded "name|departmentID"
          if (name.includes("|")) {
            const [n, d] = name.split("|");
            return {
              id: index + 1,
              name: (n || "").trim(),
              departmentID: Number(d) || 0,
            };
          }
          return {
            id: index + 1,
            name,
            departmentID: 0,
          };
        }
        return {
          id: index + 1,
          name: String(item.name || item.Name || "").trim(),
          departmentID: readDepartmentID(item),
        };
      };

      if (!rawValue) return [];

      let value = rawValue;
      if (typeof value === "string") {
        value = value.replace(/^\uFEFF/, "").trim();
      }

      // Already an array
      if (Array.isArray(value)) {
        return value
          .map((item, index) => toOption(item, index))
          .filter((item) => item.name);
      }

      if (typeof value === "string") {
        // Try JSON first (new department-wise format)
        if (value.startsWith("[") || value.startsWith("{")) {
          try {
            let parsed = JSON.parse(value);
            // Handle double-encoded JSON string
            if (typeof parsed === "string") {
              parsed = JSON.parse(parsed);
            }
            if (Array.isArray(parsed)) {
              return parsed
                .map((item, index) => toOption(item, index))
                .filter((item) => item.name);
            }
          } catch (e) {
            // fall through to legacy parse
          }
        }

        // Legacy format: "Teacher, Principal, Manager"
        return value
          .split(",")
          .map((name) => name.trim())
          .filter((name) => name)
          .map((name, index) => toOption(name, index));
      }

      return [];
    },
    handleEvent(event) {
      // console.log(event.key);
      if (event.ctrlKey && event.keyCode === 83) {
        event.preventDefault();
        if (!this.submitting) {
          this.submitting = true;
          this.formSubmitted();
        }
      }
    },
    changeDay() {
      this.currDays = this.timingInfo.reduce((acc, el) => {
        acc.push(el.day.toLowerCase());
        return acc;
      }, []);
      this.sortTimingInfoByDay();
      // console.log(this.currDays);
    },
    sortTimingInfoByDay() {
      // Define day order
      const dayOrder = [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ];

      // Sort timingInfo by day order
      this.timingInfo.sort((a, b) => {
        const dayA = a.day ? a.day.toLowerCase() : "";
        const dayB = b.day ? b.day.toLowerCase() : "";
        const indexA = dayOrder.indexOf(dayA);
        const indexB = dayOrder.indexOf(dayB);

        // If day not found in order, put it at the end
        if (indexA === -1 && indexB === -1) return 0;
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;

        return indexA - indexB;
      });
    },
    setDays(item) {
      this.$store.commit("setSameTimings", this.sameDays);

      if (
        this.timingInfo.length == 1 &&
        this.sameDays &&
        item.day == "monday"
      ) {
        // console.log(item);
        let days = ["tuesday", "wednesday", "thursday", "friday", "saturday"];
        let data = [item];
        days.forEach((el) => {
          data.push({
            id: 0,
            tID: this.$route.params.id,
            day: el,
            timeIn: item.timeIn,
            timeOut: item.timeOut,
            dayOff: false,
            reportingTime: item.timeIn,
            campusID: this.$store.state.userData.cId,
          });
        });
        this.timingInfo = data;
        this.sortTimingInfoByDay();
        this.changeDay();
      }
    },
    CheckTimings() {
      this.timingInfo = this.timingInfo.filter(
        (el) => el.day != "" && el.timeIn != "" && el.timeOut != ""
      );
      // console.log(this.timingInfo);
      if (this.localID > 0 && this.formRights.hidePayroll) {
        return true;
      } else {
        let state = true;
        // this.timingInfo = this.timingInfo.filter(
        //   (el) => el.day != "" && el.timeIn != "" && el.timeOut != ""
        // );
        this.timingInfo.forEach((el) => {
          el.tID = parseInt(this.$route.params.id);
          if (el.timeIn == "" || el.timeOut == "") {
            state = false;
          }
          el.reportingTime = !el.reportingTime ? el.timeIn : el.reportingTime;
        });
        // console.log(state);
        return state;
      }
    },
    AddVal() {
      let t1 = "2024-05-05T08:00:00";
      let t2 = "2024-05-05T13:00:00";
      let first = this.timingInfo[0];
      if (first) {
        t1 = first.timeIn ? first.timeIn : "2024-05-05T08:00:00";
        t2 = first.timeOut ? first.timeOut : "2024-05-05T13:00:00";
      }
      this.timingInfo.push({
        id: 0,
        tID: this.$route.params.id,
        day: "",
        timeIn: t1,
        timeOut: t2,
        dayOff: false,
        reportingTime: t1,
        campusID: this.$store.state.userData.cId,
      });
      this.sortTimingInfoByDay();
      // console.log(this.timingInfo);
    },
    async removeVal(item, ind) {
      if (item.id == 0) {
        this.timingInfo.splice(ind, 1);
        // console.log(this.timingInfo);
        this.currDays = this.currDays.filter((el) => el != item.day);
      } else {
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "StaffTimings/" +
            item.id +
            "?db=" +
            this.$store.state.userData.db,
          body: null,
          grading: true,
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) {
          this.timingInfo.splice(ind, 1);
          this.currDays = this.currDays.filter((el) => el != item.day);
        }
        // console.log(this.timingInfo);
      }
    },
    changeAllow() {
      this.allowIDs = this.AddingInfo.reduce((acc, el) => {
        acc.push(el.allowanceID);
        return acc;
      }, []);
      // console.log(this.allowIDs);
    },
    changeDed() {
      this.dedIDs = this.deductInfo.reduce((acc, el) => {
        acc.push(el.deductionID);
        return acc;
      }, []);
      // console.log(this.dedIDs);
    },
    async loadAllowances(text) {
      var obj = {
        url:
          this.$store.state.domain +
          "Allowances?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.allowances = await this.get(obj);
      if (text == "set") {
        let data1 = [];
        this.allowances.forEach((el) => {
          data1.push({
            id: 0,
            tID: this.$route.params.id,
            amount: !el.amount ? 0 : el.amount,
            allowanceID: el.id,
            campusID: this.$store.state.userData.cId,
          });
        });
        this.AddingInfo = data1;
        this.changeAllow();
      }
      //  else if (text == "new") {
      //   this.AddingInfo.push({
      //     id: 0,
      //     tID: this.$route.params.id,
      //     amount: this.allowances.at(-1).amount,
      //     allowanceID: this.allowances.at(-1).id,
      //     campusID: this.$store.state.userData.cId,
      //   });
      // }
    },

    openAllow() {
      this.accIcon = "PlusIcon";
      this.allowObj = {
        id: 0,
        title: "",
        amount: 0,
        campusID: this.$store.state.userData.cId,
      };

      this.visibility3 = true;
      var elem = this.$refs["title_allow"];
      elem.state = undefined;
      var elem = this.$refs["amount_allow"];
      elem.state = undefined;
    },
    editAllow(item) {
      this.allowObj = { ...item };
      this.accIcon = "Edit2Icon";
      var elem = this.$refs["title_allow"];
      elem.state = undefined;
      var elem = this.$refs["amount_allow"];
      elem.state = undefined;
      // console.log(this.allowObj);
    },

    async deleteAllow(id) {
      // console.log(acc);
      let result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("Yes, Delete it!"),
      });

      if (result.isConfirmed) {
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "Allowances/" +
            id +
            "?db=" +
            this.$store.state.userData.db,
          message: "Allowance deleted successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) this.loadAllowances();
      }
    },
    checkTitle() {
      var elem = this.$refs["title_allow"];
      if (this.allowObj.title.trim() == "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    checkAllAmount() {
      var elem = this.$refs["amount_allow"];
      if (
        parseInt(this.allowObj.amount) < 0 ||
        isNaN(parseInt(this.allowObj.amount))
      ) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },

    async SaveAllow() {
      // console.log("obj:", this.allowObj);
      if (this.checkTitle() == true && this.checkAllAmount() == true) {
        this.saving = true;
        this.allowObj.amount = parseInt(this.allowObj.amount);

        if (this.allowObj.id == 0) {
          var status = await this.post({
            url:
              this.$store.state.domain +
              "Allowances?db=" +
              this.$store.state.userData.db,
            body: this.allowObj,
            message: "Allowance added successfully.",
            context: this,
            token: this.$store.state.userData.token,
          });
          this.saving = false;
        } else {
          var status = await this.put({
            url:
              this.$store.state.domain +
              "Allowances/" +
              this.allowObj.id +
              "?db=" +
              this.$store.state.userData.db,
            message: "Allowance updated successfully.",
            context: this,
            body: this.allowObj,
            token: this.$store.state.userData.token,
          });
          this.saving = false;
        }
        if (status) {
          // let newval = "";
          // if (this.allowObj.id == 0) newval = "new";
          this.loadAllowances();
          var elem = this.$refs["title_allow"];
          elem.state = undefined;
          var elem = this.$refs["amount_allow"];
          elem.state = undefined;
          this.allowObj = {
            id: 0,
            title: "",
            campusID: this.$store.state.userData.cId,
          };
          this.accIcon = "PlusIcon";
        }
      }
    },

    async loadDeductions(text) {
      var obj = {
        url:
          this.$store.state.domain +
          "Deductions?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.deductions = await this.get(obj);
      this.deductions = this.deductions.filter((el) => !el.isHidden);

      if (text == "set") {
        let data2 = [];
        this.deductions.forEach((el) => {
          data2.push({
            id: 0,
            tID: this.$route.params.id,
            amount: !el.amount ? 0 : el.amount,
            deductionID: el.id,
            campusID: this.$store.state.userData.cId,
          });
        });
        this.deductInfo = data2;
        this.changeDed();
      }
    },

    openDeduct() {
      this.accIcon = "PlusIcon";
      this.deductObj = {
        id: 0,
        title: "",
        amount: 0,
        campusID: this.$store.state.userData.cId,
      };

      this.visibility4 = true;
      var elem = this.$refs["title_ded"];
      elem.state = undefined;
      var elem = this.$refs["amount_ded"];
      elem.state = undefined;

      this.linkObj = null;
      if (this.salaryLedgerObj?.valueBool) {
        this.linkObj = {
          id: 0,
          campusID: this.$store.state.userData.cId,
          key: "deduction_links",
          value: "",
          valueBool: true,
          valueNumber: 0,
        };
      }
    },
    editDeduct(item) {
      this.deductObj = { ...item };
      this.accIcon = "Edit2Icon";
      var elem = this.$refs["title_ded"];
      elem.state = undefined;
      var elem = this.$refs["amount_ded"];
      elem.state = undefined;
      // console.log(this.deductObj);

      this.linkObj = null;
      if (this.salaryLedgerObj?.valueBool) {
        const linkedAccount = this.linkedAccounts.find(
          (el) => el.value == item.id
        );
        console.log("linkedAccount", linkedAccount);
        if (linkedAccount) {
          this.linkObj = { ...linkedAccount };
        } else {
          this.linkObj = {
            id: 0,
            campusID: this.$store.state.userData.cId,
            key: "deduction_links",
            value: `${item.id}`,
            valueBool: true,
            valueNumber: 0,
          };
        }
      }
    },

    async deleteDeduct(id) {
      // console.log(acc);
      let result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("Yes, Delete it!"),
      });

      if (result.isConfirmed) {
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "Deductions/" +
            id +
            "?db=" +
            this.$store.state.userData.db,
          message: "Deduction deleted successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) this.loadDeductions();
      }
    },
    checkDTitle() {
      var elem = this.$refs["title_ded"];
      if (this.deductObj.title.trim() == "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    checkDedAmount() {
      var elem = this.$refs["amount_ded"];
      if (
        parseInt(this.deductObj.amount) < 0 ||
        isNaN(parseInt(this.deductObj.amount))
      ) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    async SaveDeduct() {
      // console.log("obj:", this.deductObj);
      if (this.checkDTitle() == true && this.checkDedAmount() == true) {
        this.deductObj.amount = parseInt(this.deductObj.amount);

        if (this.salaryLedgerObj?.valueBool) this.saveWithLink();
        else {
          this.saving = true;
          if (this.deductObj.id == 0) {
            var status = await this.post({
              url:
                this.$store.state.domain +
                "Deductions?db=" +
                this.$store.state.userData.db,
              body: this.deductObj,
              message: "Deduction added successfully.",
              context: this,
              token: this.$store.state.userData.token,
            });
            this.saving = false;
          } else {
            var status = await this.put({
              url:
                this.$store.state.domain +
                "Deductions/" +
                this.deductObj.id +
                "?db=" +
                this.$store.state.userData.db,
              message: "Deduction updated successfully.",
              context: this,
              body: this.deductObj,
              token: this.$store.state.userData.token,
            });
            this.saving = false;
          }
          if (status) {
            this.loadDeductions();
            var elem = this.$refs["title_ded"];
            elem.state = undefined;
            var elem = this.$refs["amount_ded"];
            elem.state = undefined;
            this.deductObj = {
              id: 0,
              title: "",
              campusID: this.$store.state.userData.cId,
            };
            this.accIcon = "PlusIcon";
          }
        }
      }
    },
    async saveWithLink() {
      this.saving = true;

      let setting = null;
      if (this.linkObj) {
        if (this.linkObj.id == 0 && this.linkObj.valueNumber == 0) {
          setting = null;
        } else setting = this.linkObj;
      }

      var status = await this.post({
        url:
          this.$store.state.domain +
          "Deductions/SaveWithLinks?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        body: {
          deduction: this.deductObj,
          setting: setting,
        },
        message: "Deduction saved successfully.",
        context: this,
        token: this.$store.state.userData.token,
      });
      this.saving = false;

      if (status) {
        this.loadDeductions();
        this.LoadDeductionLinks();

        var elem = this.$refs["title_ded"];
        elem.state = undefined;
        var elem = this.$refs["amount_ded"];
        elem.state = undefined;
        this.deductObj = {
          id: 0,
          title: "",
          campusID: this.$store.state.userData.cId,
        };
        this.accIcon = "PlusIcon";

        this.linkObj = {
          id: 0,
          campusID: this.$store.state.userData.cId,
          key: "deduction_links",
          value: "",
          valueBool: true,
          valueNumber: 0,
        };
      }
    },
    CheckValues() {
      this.AddingInfo = this.AddingInfo.filter((el) => el.allowanceID != 0);
      this.deductInfo = this.deductInfo.filter((el) => el.deductionID != 0);

      if (this.localID > 0 && this.formRights.hidePayroll) {
        return true;
      } else {
        let state = true;
        // this.AddingInfo = this.AddingInfo.filter((el) => el.allowanceID != 0);
        this.AddingInfo.forEach((el) => {
          el.tID = parseInt(this.$route.params.id);
          el.amount = parseInt(el.amount);
          if (isNaN(el.amount) || el.amount < 0) {
            state = false;
          }
        });

        // this.deductInfo = this.deductInfo.filter((el) => el.deductionID != 0);
        this.deductInfo.forEach((el) => {
          el.tID = parseInt(this.$route.params.id);
          el.amount = parseInt(el.amount);
          if (isNaN(el.amount) || el.amount < 0) {
            state = false;
          }
        });

        return state;
      }
    },

    AddDetail(item) {
      if (item.allowanceID != 0 && item.amount !== "") {
        this.AddingInfo.push({
          id: 0,
          tID: this.$route.params.id,
          amount: 0,
          allowanceID: 0,
          campusID: this.$store.state.userData.cId,
        });
      }
      // console.log(this.AddingInfo);
    },
    async removeDetail(item, ind) {
      if (item.id == 0) {
        this.AddingInfo.splice(ind, 1);
        console.log(this.AddingInfo);
        this.changeAllow();
        // this.allowIDs = this.allowIDs.filter((el) => el != item.allowanceID);
      } else {
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "StaffAllowances/" +
            item.id +
            "?db=" +
            this.$store.state.userData.db,
          body: null,
          grading: true,
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) {
          this.AddingInfo.splice(ind, 1);
          // this.allowIDs = this.allowIDs.filter((el) => el != item.allowanceID);
          this.changeAllow();
        }
        // console.log(this.AddingInfo);
      }
    },
    AddDeduct(item) {
      if (item.deductionID != 0 && item.amount !== "") {
        this.deductInfo.push({
          id: 0,
          tID: this.$route.params.id,
          amount: 0,
          deductionID: 0,
          campusID: this.$store.state.userData.cId,
        });
      }
      // console.log(this.deductInfo);
    },
    async removeDeduct(item, ind) {
      if (item.id == 0) {
        this.deductInfo.splice(ind, 1);
        console.log(this.deductInfo);
        this.changeDed();
        // this.dedIDs = this.dedIDs.filter((el) => el != item.deductionID);
      } else {
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "StaffDeductions/" +
            item.id +
            "?db=" +
            this.$store.state.userData.db,
          body: null,
          grading: true,
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) {
          this.deductInfo.splice(ind, 1);
          // this.dedIDs = this.dedIDs.filter((el) => el != item.deductionID);
          this.changeDed();
        }
        // console.log(this.deductInfo);
      }
    },
    AddOpen() {
      this.incObj = {
        id: 0,
        tID: this.$route.params.id,
        amount: 0,
        amountType: "amount",
        percentage: "",
        note: "",
        date: new Date().toJSON(),
        updateSalary: "no",
        campusID: this.$store.state.userData.cId,
        userID: null,
      };
      this.newInc = true;
      this.visibility = true;
      this.sidebarTitle = "Add Increment";
      this.sidebarButton = "Save";
      var elem = this.$refs["amount"];
      elem.state = undefined;
      var elem = this.$refs["note"];
      elem.state = undefined;
      var elem = this.$refs["inc_date"];
      elem.state = undefined;
    },
    EditInc(item, ind) {
      console.log(ind);
      this.incObj = { ...item };
      if (!this.incObj.amountType) {
        this.incObj.amountType = "amount";
      }
      if (this.incObj.percentage == null || this.incObj.percentage === undefined) {
        this.incObj.percentage = "";
      }
      if (this.incObj.amountType === "percent") {
        this.recalcIncrementFromPercent();
      }
      if (this.showUserLinkField && !this.incObj.userID) {
        this.incObj.userID = null;
      }
      this.newInc = false;
      this.incIndex = ind;
      this.visibility = true;
      this.sidebarTitle = "Edit Increment";
      this.sidebarButton = "Update";
      var elem = this.$refs["amount"];
      elem.state = undefined;
      var elem = this.$refs["note"];
      elem.state = undefined;
      var elem = this.$refs["inc_date"];
      elem.state = undefined;
    },

    async DeleteInc(id, ind) {
      if (id == 0) {
        this.history.splice(ind, 1);
        console.log(this.history);
      } else {
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "StaffIncrementHistory/" +
            id +
            "?db=" +
            this.$store.state.userData.db,
          message: "Increment removed successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) this.history.splice(ind, 1);
        console.log(this.history);

        // let result = await Swal.fire({
        //   title: this.$t("Are you sure?"),
        //   text: this.$t("You won't be able to revert this!"),
        //   icon: "warning",
        //   showCancelButton: true,
        //   confirmButtonColor: "#d33",
        //   cancelButtonColor: "#3085d6",
        //   confirmButtonText: this.$t("Yes, Delete it!"),
        // });

        // if (result.isConfirmed) {
        //   // var status = await this.delete({
        //   //   url:
        //   //     this.$store.state.domain +
        //   //     "?db=" +
        //   //     this.$store.state.userData.db,
        //   //   body: item,
        //   //   message: "Increment removed successfully.",
        //   //   context: this,
        //   //   token: this.$store.state.userData.token,
        //   // });
        //   // if (status) this.LoadInc();
        // }
      }
    },
    depoOpen() {
      this.depoObj = {
        id: 0,
        tID: this.$route.params.id,
        amount: this.myObj.totalSalary,
        loanType: "security deposit",
        date: new Date(),
        months: 12,
        monthlyInstallments: null,
        campusID: this.$store.state.userData.cId,
      };
      this.depoObj.monthlyInstallments = parseInt(
        this.depoObj.amount / this.depoObj.months
      );
      this.deductMonth = new Date().getMonth() + 1;
      this.deductYear = new Date().getFullYear();

      this.skipped = [];

      if (this.reportDomain == "myskoolhpgs") {
        this.skipped = [6, 7]; // skip june july
        this.depoObj.months = 10;
        this.depoObj.monthlyInstallments = parseInt(
          this.depoObj.amount / this.depoObj.months
        );

        // After 25, deposit month after 2 months else next month
        if (this.myObj.appointedOn) {
          let appointedDate = new Date(this.myObj.appointedOn);

          let day = appointedDate.getDate();
          appointedDate.setDate(20); // to avoid feb conflict
          appointedDate.setMonth(appointedDate.getMonth() + 1);

          let nextDate = new Date(appointedDate);
          if (day > 25) {
            nextDate = new Date(nextDate.setMonth(nextDate.getMonth() + 1));
          }
          // console.log(nextDate);
          this.deductMonth = nextDate.getMonth() + 1;
          this.deductYear = nextDate.getFullYear();
        }
      }

      this.visibility5 = true;
      this.newDepo = true;
      this.sidebarTitle = "Add Deposit";
      this.sidebarButton = "Save";
      var elem = this.$refs["depo_amount"];
      elem.state = undefined;
      var elem = this.$refs["depo_month"];
      elem.state = undefined;
      var elem = this.$refs["depo_return"];
      elem.state = undefined;
    },
    EditDepo(item, ind) {
      this.depoObj = { ...item };
      this.depoIndex = ind;
      this.newDepo = false;
      this.visibility5 = true;
      this.sidebarTitle = "Edit Deposit";
      this.sidebarButton = "Update";
      var elem = this.$refs["depo_amount"];
      elem.state = undefined;
      var elem = this.$refs["depo_month"];
      elem.state = undefined;
      var elem = this.$refs["depo_return"];
      elem.state = undefined;
    },
    async DeleteDepo(id, ind) {
      if (id == 0) {
        this.deposits.splice(ind, 1);
        // console.log(this.deposits);
      } else {
        let result = await Swal.fire({
          title: this.$t("Are you sure?"),
          text: this.$t("You won't be able to revert this!"),
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          confirmButtonText: this.$t("Yes, Delete it!"),
        });

        if (result.isConfirmed) {
          var status = await this.delete({
            url:
              this.$store.state.domain +
              "StaffAdvaceAndLoansInstallmentPlan/" +
              id +
              "?db=" +
              this.$store.state.userData.db,
            message: "Deposit removed successfully.",
            context: this,
            token: this.$store.state.userData.token,
          });
          if (status) this.deposits.splice(ind, 1);
          // console.log(this.deposits);
        }
      }
    },
    async depoDelAll() {
      if (this.deposits.length > 0) {
        let result = await Swal.fire({
          title: this.$t("Are you sure?"),
          text: "This will delete all Undeposited amount!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          confirmButtonText: this.$t("Yes, Delete it!"),
        });

        if (result.isConfirmed) {
          this.delAll = true;

          if (this.$route.params.id > 0) {
            var status = await this.post({
              url:
                this.$store.state.domain +
                "Staff/ClearDeposits?db=" +
                this.$store.state.userData.db +
                "&cID=" +
                this.$store.state.userData.cId +
                "&tID=" +
                this.$route.params.id,
              message: "Deposits removed successfully.",
              context: this,
              savingMarks: true,
              token: this.$store.state.userData.token,
            });
            this.delAll = false;

            this.deposits = status;
          } else {
            this.deposits = this.deposits.filter(
              (el) => el.isReturned != null || el.salaryID != null
            );
            this.delAll = false;
          }
        }
      }
    },

    LoanOpen() {
      this.loanObj = {
        id: 0,
        tID: this.$route.params.id,
        amount: null,
        loanType: "loan",
        date: new Date(),
        months: null,
        monthlyInstallments: null,
        campusID: this.$store.state.userData.cId,
        records: [],
      };
      this.installData = [];
      this.loanMonth = new Date().getMonth() + 1;
      this.loanYear = new Date().getFullYear();
      this.visibility2 = true;
      this.newLoan = true;
      this.sidebarTitle = "Add Loan / Advance";
      this.sidebarButton = "Save";
      var elem = this.$refs["loan_amount"];
      elem.state = undefined;
      var elem = this.$refs["depo_return"];
      elem.state = undefined;
      var elem = this.$refs["loan_month"];
      elem.state = undefined;
      var elem = this.$refs["return_amount"];
      elem.state = undefined;
    },
    EditLoan(item, ind) {
      // console.log(item);
      this.loanObj = { ...item };
      let date = new Date(this.loanObj.date);
      this.loanMonth = date.getMonth() + 1;
      this.loanYear = date.getFullYear();
      if (item.id == 0) this.installData = item.records;
      else
        this.installData = this.loanDetails.filter(
          (el) => el.advanceAndLoanID == item.id
        );
      this.loanIndex = ind;
      this.newLoan = false;
      this.visibility2 = true;
      this.sidebarTitle = "Edit Loan / Advance";
      this.sidebarButton = "Update";
      var elem = this.$refs["loan_amount"];
      elem.state = undefined;
      var elem = this.$refs["loan_date"];
      elem.state = undefined;
      var elem = this.$refs["loan_month"];
      elem.state = undefined;
      var elem = this.$refs["return_amount"];
      elem.state = undefined;
    },

    async DeleteLoan(item, ind) {
      if (item.id == 0) {
        this.loanData.splice(ind, 1);
        // console.log(this.loanData);
      } else {
        let result = await Swal.fire({
          title: this.$t("Are you sure?"),
          text: this.$t("You won't be able to revert this!"),
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          confirmButtonText: this.$t("Yes, Delete it!"),
        });

        if (result.isConfirmed) {
          var status = await this.delete({
            url:
              this.$store.state.domain +
              "StaffAdvaceAndLoans/" +
              item.id +
              "?db=" +
              this.$store.state.userData.db,
            message: item.loanType + " removed successfully.",
            context: this,
            token: this.$store.state.userData.token,
          });
          if (status) this.loanData.splice(ind, 1);
          // console.log(this.loanData);
        }
      }
    },
    checkLoanDate() {
      var elem = this.$refs["loan_date"];
      if (this.loanObj.date == "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    checkDAmount() {
      var elem = this.$refs["depo_amount"];
      if (this.depoObj.amount <= 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    checkDMonth() {
      var elem = this.$refs["depo_month"];
      if (this.depoObj.months <= 0) {
        // || this.depoObj.months > 12
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    checkDRetAmount() {
      var elem = this.$refs["depo_return"];
      if (
        this.depoObj.monthlyInstallments <= 0 ||
        parseInt(this.depoObj.monthlyInstallments) >
          parseInt(this.depoObj.amount)
      ) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    setDInstallment() {
      if (this.checkDAmount() == true && this.checkDMonth() == true) {
        this.depoObj.monthlyInstallments = parseInt(
          this.depoObj.amount / this.depoObj.months
        );

        // console.log(this.depoObj.monthlyInstallments);
      }
    },
    setbyDReturn() {
      if (this.checkDAmount() == true && this.checkDRetAmount() == true) {
        this.depoObj.amount = parseInt(this.depoObj.amount);
        this.depoObj.monthlyInstallments = parseInt(
          this.depoObj.monthlyInstallments
        );

        this.depoObj.months = Math.ceil(
          this.depoObj.amount / this.depoObj.monthlyInstallments
        );
      }
    },
    setSkip(val) {
      if (this.skipped.includes(val)) {
        this.skipped = this.skipped.filter((el) => el != val);
      } else this.skipped.push(val);
      // console.log(this.skipped);
    },
    async AddDeposit() {
      // this.checkLoanDate() == true &&
      if (
        this.checkDAmount() == true &&
        this.checkDMonth() == true &&
        this.checkDRetAmount() == true
      ) {
        this.request = true;
        this.depoObj.amount = parseInt(this.depoObj.amount);
        this.depoObj.months = parseInt(this.depoObj.months);
        this.depoObj.monthlyInstallments = parseInt(
          this.depoObj.monthlyInstallments
        );

        let status = false;
        this.depoObj.date = new Date(
          `${this.deductYear}-${this.deductMonth}-05`
        ).toJSON();
        // console.log(this.deductYear, this.deductMonth);
        //alert(this.depoObj.date);

        if (this.$route.params.id == 0) {
          status = await this.post({
            url:
              this.$store.state.domain +
              "staff/InstallmentsDep?db=" +
              this.$store.state.userData.db +
              "&sameMonth=true&skip=" +
              this.skipped,
            body: this.depoObj,
            subjects: true,
            context: this,
            token: this.$store.state.userData.token,
          });
        } else {
          status = await this.post({
            url:
              this.$store.state.domain +
              "Staff/DepositInstallments?db=" +
              this.$store.state.userData.db +
              "&month=" +
              this.deductMonth +
              "&year=" +
              this.deductYear +
              "&skip=" +
              this.skipped,
            body: this.depoObj,
            subjects: true,
            context: this,
            token: this.$store.state.userData.token,
          });
        }
        // console.log(status);
        if (status) {
          this.deposits = [...this.deposits, ...status];
          this.depoSaveList.push(this.depoObj);
        }
        this.request = false;
        this.visibility5 = false;

        // if (this.newDepo) this.deposits.push(this.depoObj);
        // else this.deposits[this.depoIndex] = this.depoObj;

        // this.visibility5 = false;
        // this.$refs.depoTable.refresh();
        // console.log(this.deposits);
      }
    },
    checkLoanAmount() {
      var elem = this.$refs["loan_amount"];
      if (this.loanObj.amount <= 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    checkMonth() {
      var elem = this.$refs["loan_month"];
      if (this.loanObj.months <= 0) {
        //  || this.loanObj.months > 12
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    async GetDeductMonth() {
      console.log("MONTH---------", this.deductMonth);
    },
    async setInstallment() {
      if (this.checkLoanAmount() == true && this.checkMonth() == true) {
        this.loanObj.monthlyInstallments = parseInt(
          this.loanObj.amount / this.loanObj.months
        );

        // console.log(this.loanObj.monthlyInstallments);
        this.checkRetAmount();

        this.loanObj.amount = parseInt(this.loanObj.amount);
        this.loanObj.months = parseInt(this.loanObj.months);
        this.loanObj.monthlyInstallments = parseInt(
          this.loanObj.monthlyInstallments
        );

        this.loanObj.date = new Date(
          `${this.loanYear}-${this.loanMonth}-05`
        ).toJSON();
        // console.log(this.loanObj);
        var status = await this.post({
          url:
            this.$store.state.domain +
            "staff/Installments?db=" +
            this.$store.state.userData.db +
            "&sameMonth=" +
            this.sameMonth,
          body: this.loanObj,
          subjects: true,
          context: this,
          token: this.$store.state.userData.token,
        });
        // console.log(status);
        if (status) {
          this.installData = status;
          this.loanObj.records = status;
        }
      }
    },
    async setbyRetAmount() {
      if (this.checkLoanAmount() == true && this.checkRetAmount() == true) {
        this.loanObj.amount = parseInt(this.loanObj.amount);
        // this.loanObj.months = parseInt(this.loanObj.months);

        this.loanObj.months = Math.ceil(
          this.loanObj.amount / this.loanObj.monthlyInstallments
        );

        // console.log(this.loanObj.months);
        // this.checkMonth(); // ====

        this.loanObj.monthlyInstallments = parseInt(
          this.loanObj.monthlyInstallments
        );

        this.loanObj.date = new Date(
          `${this.loanYear}-${this.loanMonth}-05`
        ).toJSON();
        // console.log(this.loanObj);
        var status = await this.post({
          url:
            this.$store.state.domain +
            "staff/Installments?db=" +
            this.$store.state.userData.db +
            "&sameMonth=" +
            this.sameMonth,
          body: this.loanObj,
          subjects: true,
          context: this,
          token: this.$store.state.userData.token,
        });
        // console.log(status);
        if (status) {
          this.installData = status;
          this.loanObj.records = status;
        }
      }
    },
    checkRetAmount() {
      var elem = this.$refs["return_amount"];
      if (
        this.loanObj.monthlyInstallments <= 0 ||
        parseInt(this.loanObj.monthlyInstallments) >
          parseInt(this.loanObj.amount)
      ) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    openAmountEdit(item, index) {
      this.editAmountObj = { ...item };
      this.depositIndex = index;
      this.$bvModal.show("deposit-amount-edit-modal");
    },
    async saveEditAmount() {
      // Validate amount
      let x = this.editAmountObj.amount;
      if (isNaN(x) || x <= 0) {
        this.$refs.edit_amount.state = false;
        return;
      }

      if (this.editAmountObj.id == 0) {
        this.editAmountObj.amount = parseInt(this.editAmountObj.amount);
        this.deposits[this.depositIndex].amount = this.editAmountObj.amount;
        this.$bvModal.hide("deposit-amount-edit-modal");

        this.$bvToast.toast(this.$t("Amount updated successfully!"), {
          title: this.$t("Success"),
          variant: "success",
          solid: true,
        });
        return;
      }

      // Show confirmation dialog before proceeding
      const result = await Swal.fire({
        title: "Confirm",
        text: "Are you sure you want to update this amount?",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#7367F0",
        cancelButtonColor: "#EA5455",
        confirmButtonText: "Yes, update",
        cancelButtonText: this.$t("Cancel"),
      });

      // If user cancels, exit the function
      if (!result.isConfirmed) {
        return;
      }

      this.isUpdatingAmount = true;
      this.editAmountObj.amount = parseInt(this.editAmountObj.amount);

      try {
        const status = await this.put({
          url:
            this.$store.state.domain +
            "StaffAdvaceAndLoansInstallmentPlan/" +
            this.editAmountObj.id +
            "?db=" +
            this.$store.state.userData.db,
          body: this.editAmountObj,
          message: "Amount updated successfully!",
          context: this,
          token: this.$store.state.userData.token,
        });

        if (status) {
          this.$bvModal.hide("deposit-amount-edit-modal");

          this.deposits[this.depositIndex].amount = this.editAmountObj.amount;
        }
      } catch (error) {
        console.error("Error updating amount:", error);
      } finally {
        this.isUpdatingAmount = false;
      }
    },
    async markReturned(item) {
      if (item.id == 0) {
        item.isReturned = true;
      } else {
        item.isReturned = true;
        // console.log(item);

        var status = await this.put({
          url:
            this.$store.state.domain +
            "StaffAdvaceAndLoansInstallmentPlan/" +
            item.id +
            "?db=" +
            this.$store.state.userData.db,
          token: this.$store.state.userData.token,
          message: "Marked as Returned",
          context: this,
          body: item,
          token: this.$store.state.userData.token,
        });
        if (status) {
          console.log("done");
        }
      }
    },
    async markDeposit(item) {
      if (item.id == 0) {
        item.salaryID = 0;
      } else {
        item.salaryID = 0;

        var status = await this.put({
          url:
            this.$store.state.domain +
            "StaffAdvaceAndLoansInstallmentPlan/" +
            item.id +
            "?db=" +
            this.$store.state.userData.db,
          token: this.$store.state.userData.token,
          message: "Marked as Deposited",
          context: this,
          body: item,
          token: this.$store.state.userData.token,
        });
        if (status) {
          console.log("done");
        }
      }
    },
    async markUnDeposit(item) {
      if (item.id == 0) {
        item.salaryID = null;
        item.isReturned = null;
      } else {
        let result = await Swal.fire({
          title: this.$t("Are you sure?"),
          text: "Do you want to undeposit this amount?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          confirmButtonText: "Yes!",
        });

        if (result.isConfirmed) {
          item.salaryID = null;
          item.isReturned = null;
          // console.log(item);

          var status = await this.put({
            url:
              this.$store.state.domain +
              "StaffAdvaceAndLoansInstallmentPlan/" +
              item.id +
              "?db=" +
              this.$store.state.userData.db,
            token: this.$store.state.userData.token,
            message: "Marked as Undeposit",
            context: this,
            body: item,
            token: this.$store.state.userData.token,
          });
          if (status) {
            console.log("done");
          }
        }
      }
    },
    async markUnReturned(item) {
      if (item.id == 0) {
        item.isReturned = null;
      } else {
        let result = await Swal.fire({
          title: this.$t("Are you sure?"),
          text: "Do you want to unreturn this deposit?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          confirmButtonText: "Yes!",
        });

        if (result.isConfirmed) {
          item.isReturned = null;
          // console.log(item);

          var status = await this.put({
            url:
              this.$store.state.domain +
              "StaffAdvaceAndLoansInstallmentPlan/" +
              item.id +
              "?db=" +
              this.$store.state.userData.db,
            token: this.$store.state.userData.token,
            message: "Marked as unreturned",
            context: this,
            body: item,
            token: this.$store.state.userData.token,
          });
          if (status) {
            console.log("done");
          }
        }
      }
    },
    openReturn() {
      this.returnObj = {
        tID: this.myObj.id,
        amount: this.currentDeposits,
        date: new Date().toJSON(),
        cheque: "",
        userID: this.$store.state.userData.userID,
        campusID: this.myObj.campusID,
      };
      this.visibility8 = true;
    },
    checkRetDate() {
      var elem = this.$refs["ret_date"];
      if (!this.returnObj.date) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    checkRetCheq() {
      var elem = this.$refs["ret_cheq"];
      if (this.returnObj.cheque == "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    async saveReturn() {
      this.checkRetDate();
      this.checkRetCheq();
      if (this.checkRetDate() == false || this.checkRetCheq() == false) {
        return this.$bvToast.toast(this.$t("Please enter the details correctly!"), {
          title: this.$t("Error!"),
          variant: "danger",
        });
      } else {
        console.log(this.returnObj);
        // this.savingReturn = true;
        // var status = await this.post({
        //   url:
        //     this.$store.state.domain +
        //     "staff/returnDeposits?db=" +
        //     this.$store.state.userData.db,
        //   body: this.loanObj,
        //   message: "Deposit returned successfully!",
        //   context: this,
        //   token: this.$store.state.userData.token,
        // });
        // this.savingReturn = false;
        // if (status) {
        //   this.visibility8 = false;
        // }
      }
    },

    async AddLoan() {
      // this.checkLoanDate() == true &&
      if (
        this.checkLoanAmount() == true &&
        this.checkMonth() == true &&
        this.checkRetAmount() == true
      ) {
        this.loanObj.amount = parseInt(this.loanObj.amount);
        this.loanObj.months = parseInt(this.loanObj.months);
        this.loanObj.monthlyInstallments = parseInt(
          this.loanObj.monthlyInstallments
        );
        if (this.newLoan) this.loanData.push(this.loanObj);
        else this.loanData[this.loanIndex] = this.loanObj;

        this.visibility2 = false;
        this.$refs.loanTable.refresh();
        // console.log(this.loanData);

        // console.log(this.loanObj);
        // var status = await this.post({
        //   url:
        //     this.$store.state.domain +
        //     "staff/Installments?db=" +
        //     this.$store.state.userData.db,
        //   body: this.loanObj,
        //   subjects: true,
        //   context: this,
        //   token: this.$store.state.userData.token,
        // });
        // this.request = false;
        // console.log(status);
        // if (status) {
        //   this.installData = status;
        // }

        // ------------------

        // if (this.loanObj.id == 0) {
        //   // var status = await this.post({
        //   //   url:
        //   //     this.$store.state.domain +
        //   //     " ?db=" +
        //   //     this.$store.state.userData.db,
        //   //   body: this.loanObj,
        //   //   message: "loan added successfully!",
        //   //   context: this,
        //   //   token: this.$store.state.userData.token,
        //   // });
        //   // this.request = false
        //   // if (status) {
        //   //   this.visibility2 = false,
        //   //   this.LoadLoan()
        //   // }
        // } else {
        //   console.log("ed", this.loanObj);

        //   //   var status = await this.put({
        //   //   url:
        //   //     this.$store.state.domain +
        //   //     " /" +
        //   //     item.id +
        //   //     "?db=" +
        //   //     this.$store.state.userData.db,
        //   //   body: this.loanObj,
        //   //   message: "Loan updated successfully!",
        //   //   context: this,
        //   //   token: this.$store.state.userData.token,
        //   // });
        //   // this.request = false
        //   // if (status) {
        //   //     this.visibility2 = false
        //   //     this.LoadLoan()
        //   //   }
        // }
        // this.request = false;
      }
    },

    hideSideBar() {
      this.visibility = false;
    },
    checkDate() {
      var elem = this.$refs["inc_date"];
      if (!this.incObj.date) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    formatIncrementBasicSalary() {
      const basic = Number(this.myObj.basicSalary);
      if (!Number.isFinite(basic) || basic <= 0) return "—";
      return basic.toLocaleString();
    },

    getIncrementBasicSalary() {
      const basic = parseFloat(this.myObj.basicSalary);
      return Number.isFinite(basic) && basic > 0 ? basic : 0;
    },

    onIncAmountTypeChange() {
      if (this.incObj.amountType === "amount") {
        this.incObj.percentage = "";
        if (this.$refs.inc_percentage) {
          this.$refs.inc_percentage.state = undefined;
        }
      } else {
        this.recalcIncrementFromPercent();
      }
      this.checkAmount();
    },

    onIncPercentageInput() {
      this.recalcIncrementFromPercent();
      this.checkAmount();
    },

    recalcIncrementFromPercent() {
      if (this.incObj.amountType !== "percent") return;
      const basic = this.getIncrementBasicSalary();
      const pct = parseFloat(this.incObj.percentage);
      if (!basic || !Number.isFinite(pct) || pct <= 0) {
        this.incObj.amount = 0;
        return;
      }
      this.incObj.amount = Math.round((basic * pct) / 100);
    },

    checkIncPercentage() {
      const elem = this.$refs.inc_percentage;
      if (!elem) return true;
      const pct = parseFloat(this.incObj.percentage);
      if (!Number.isFinite(pct) || pct <= 0) {
        elem.state = false;
        return false;
      }
      elem.state = true;
      return true;
    },

    checkAmount() {
      var elem = this.$refs["amount"];
      if (this.incObj.amountType === "percent") {
        const pctOk = this.checkIncPercentage();
        if (!pctOk) {
          if (elem) elem.state = false;
          return false;
        }
        this.recalcIncrementFromPercent();
      }
      if (this.incObj.amount <= 0) {
        if (elem) elem.state = false;
        return false;
      }
      if (elem) elem.state = true;
      return true;
    },
    async LoadInc() {
      var obj = {
        url:
          this.$store.state.domain +
          " ?db=" +
          this.$store.state.userData.db +
          "&tID=" +
          this.$route.params.id,
        token: this.$store.state.userData.token,
      };
      this.history = await this.get(obj);
    },
    async Add() {
      if (this.incObj.amountType === "percent") {
        this.recalcIncrementFromPercent();
      }
      if (this.checkAmount() == true && this.checkDate() == true) {
        // this.request = true;
        this.incObj.amount = parseInt(this.incObj.amount, 10);
        if (this.incObj.amountType !== "percent") {
          this.incObj.percentage = "";
        } else if (this.incObj.percentage !== "") {
          this.incObj.percentage = parseFloat(this.incObj.percentage);
        }
        // console.log(this.incObj);
        if (this.newInc) {
          this.history.push(this.incObj);
          if (this.incObj.updateSalary == "yes") {
            let x = parseInt(this.myObj.basicSalary);
            if (isNaN(x) || x <= 0) this.myObj.basicSalary = this.incObj.amount;
            else this.myObj.basicSalary = x + this.incObj.amount;

            let y = parseInt(this.myObj.totalSalary);
            if (isNaN(y) || y <= 0) this.myObj.totalSalary = this.incObj.amount;
            else this.myObj.totalSalary = y + this.incObj.amount;
          }
        } else this.history[this.incIndex] = this.incObj;
        // this.request = false;
        this.visibility = false;
        this.$refs.hisTable.refresh();
        // console.log(this.history);
        // if (this.incObj.id == 0) {
        //   // var status = await this.post({
        //   //   url:
        //   //     this.$store.state.domain +
        //   //     " ?db=" +
        //   //     this.$store.state.userData.db,
        //   //   body: this.incObj,
        //   //   message: "Increment added successfully!",
        //   //   context: this,
        //   //   token: this.$store.state.userData.token,
        //   // });
        //   // this.request = false
        //   // if (status) {
        //   //   this.visibility = false,
        //   //   this.LoadInc()
        //   // }
        // } else {
        //   console.log("ed", this.incObj);
        //   //   var status = await this.put({
        //   //   url:
        //   //     this.$store.state.domain +
        //   //     " /" +
        //   //     item.id +
        //   //     "?db=" +
        //   //     this.$store.state.userData.db,
        //   //   body: this.incObj,
        //   //   message: "Increment updated successfully!",
        //   //   context: this,
        //   //   token: this.$store.state.userData.token,
        //   // });
        //   // this.request = false
        //   // if (status) {
        //   //     this.visibility = false
        //   //     this.LoadInc()
        //   //   }
        // }
      }
    },

    deleteDocument(index) {
      this.documents.splice(index, 1);
      this.myObj.documents = this.documents.join(",");
    },
    openFile(url) {
      window.open(url, "_blank");
    },
    async LoadStaffDocs() {
      var obj = {
        url:
          this.$store.state.domain +
          "DropdownList/LoadData?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&category=staff_doc",
        token: this.$store.state.userData.token,
      };
      let res = await this.get(obj);
      console.log("LoadStaffDocs response:", res);
      if (res && Array.isArray(res)) {
        this.docData = res.map((item) => ({
          id: item.id,
          title: item.title,
          url: "",
        }));

        // Merge previously saved URLs (from edit load) back into cards
        if (this._savedDocDetails && this._savedDocDetails.length > 0) {
          this.docData.forEach((dd) => {
            const saved = this._savedDocDetails.find((s) => s.docID === dd.id);
            if (saved) dd.url = saved.url || "";
          });
          this._savedDocDetails = null; // clear after merge
        }
      }
    },
    async saveDocType() {
      if (!this.newDocType.trim()) return;
      this.savingDocType = true;
      try {
        const payload = {
          id: 0,
          title: this.newDocType.trim(),
          typee: "staff_doc",
          campusID: this.$store.state.userData.cId,
        };
        const res = await this.post({
          url: `${this.$store.state.domain}DropdownList/SaveData?db=${this.$store.state.userData.db}`,
          body: payload,
          context: this,
          token: this.$store.state.userData.token,
          message: "Document type added!",
        });
        if (res) {
          this.newDocType = "";
          await this.LoadStaffDocs();
        }
      } catch (e) {
        console.error("saveDocType error:", e);
      }
      this.savingDocType = false;
    },
    openEditDocModal(dd) {
      // deep-clone so cancel doesn't mutate the original
      this.editDocObj = { ...dd };
      this.$bvModal.show("edit-doc-type-modal");
    },
    async saveEditDocType() {
      if (!this.editDocObj || !this.editDocObj.title.trim()) return;
      this.savingDocType = true;
      try {
        const payload = {
          id: this.editDocObj.id,
          title: this.editDocObj.title.trim(),
          typee: "staff_doc",
          campusID: this.$store.state.userData.cId,
        };
        const res = await this.post({
          url: `${this.$store.state.domain}DropdownList/SaveData?db=${this.$store.state.userData.db}`,
          body: payload,
          context: this,
          token: this.$store.state.userData.token,
          message: "Document type updated!",
        });
        if (res) {
          this.$bvModal.hide("edit-doc-type-modal");
          await this.LoadStaffDocs();
        }
      } catch (e) {
        console.error("saveEditDocType error:", e);
      }
      this.savingDocType = false;
    },
    async deleteDocType(dd) {
      const ok = await this.$bvModal.msgBoxConfirm(
        `Delete "${dd.title}" document type?`,
        {
          title: "Confirm Delete",
          okVariant: "danger",
          okTitle: "Delete",
          cancelTitle: "Cancel",
          centered: true,
        }
      );
      if (!ok) return;
      try {
        await this.post({
          url: `${this.$store.state.domain}DropdownList/DeleteData?db=${this.$store.state.userData.db}&id=${dd.id}`,
          body: {},
          context: this,
          token: this.$store.state.userData.token,
          message: "Document type deleted!",
        });
        await this.LoadStaffDocs();
      } catch (e) {
        console.error("deleteDocType error:", e);
      }
    },
    async selectDoc(item, ind) {
      let ref = this.$refs["staffDoc" + ind];
      let fileList = Array.isArray(ref) ? ref[0].files : ref.files;
      if (fileList && fileList.length > 0) {
        this.currentIndex = ind;
        this.docLoading = true;
        try {
          const uploaded = await uploadToMySkool([fileList[0]]);
          item.url = getUploadPath(uploaded[0]);
        } catch (err) {
          console.error(err);
        } finally {
          this.docLoading = false;
        }
      }
    },
    async uploadDocument() {
      let docsRef = this.$refs.staffAdditionalDocs || this.$refs.docs;
      let docs = docsRef ? docsRef.files : [];
      if (docs.length != 0) {
        this.uploading = true;
        try {
          const uploaded = await uploadToMySkool(docs);
          getUploadedPaths(uploaded).forEach((path) => {
            this.documents.push(path);
          });
          this.myObj.documents = this.documents.join(",");
        } catch (err) {
          console.error(err);
        } finally {
          this.uploading = false;
        }
      }
    },

    validationForm() {
      return new Promise((resolve, reject) => {
        // resolve(true);
        this.CheckName();
        this.CheckSContact();

        if (this.CheckName() == false || this.CheckSContact() == false) {
          // console.log("invlaid");
          reject(false);
        } else {
          if (this.myObj.id == 0) {
            // console.log("val");
            resolve(true); //
          } else {
            resolve(true);
          }
        }
      });
    },
    validationFamilyInfo() {
      return new Promise((resolve, reject) => {
        resolve(true);
        // this.CheckCnic();
        // this.CheckMCnic();
        // this.CheckEmail();
        // if (
        //   this.CheckCnic() == false ||
        //   this.CheckMCnic() == false ||
        //   this.CheckEmail() == false
        // ) {
        //   reject(false);
        // } else {
        //   resolve(true);
        // }
      });
    },
    validationEmergency() {
      return new Promise((resolve, reject) => {
        resolve(true);
        // this.CheckEmerName();
        // this.CheckEmerRelation();
        // this.CheckEmerContact();
        // if (
        //   this.CheckEmerName() == false ||
        //   this.CheckEmerRelation() == false ||
        //   this.CheckEmerContact() == false
        // ) {
        //   reject(false);
        //   console.log("rej");
        // } else {
        //   resolve(true);
        // }
      });
    },
    deleteLogo() {
      this.myObj.picture = null;
      this.plus = "";
      this.logoloading = false;
      if (this.$refs.fileProfile) {
        this.$refs.fileProfile.value = null;
      }
    },
    async uploadSalarySlip() {
      const input = this.$refs.staffSalarySlipInput;
      const file = input && input.files && input.files[0];
      if (!file) return;
      this.salarySlipLoading = true;
      try {
        const uploaded = await uploadToMySkool([file]);
        this.myObj.salarySlip = getUploadPath(uploaded[0]) || "";
      } catch (err) {
        console.log(err);
        this.$bvToast.toast(this.$t("Failed to upload salary slip."), {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.salarySlipLoading = false;
        if (input) input.value = "";
      }
    },
    removeSalarySlip() {
      this.myObj.salarySlip = "";
      if (this.$refs.staffSalarySlipInput) {
        this.$refs.staffSalarySlipInput.value = "";
      }
    },
    async uploadStaffCv() {
      const input = this.$refs.staffCvFileInput;
      const file = input && input.files && input.files[0];
      if (!file) return;
      this.cvLoading = true;
      try {
        const uploaded = await uploadToMySkool([file]);
        this.myObj.cv = getUploadPath(uploaded[0]) || "";
      } catch (err) {
        console.log(err);
        this.$bvToast.toast(this.$t("Failed to upload CV."), {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.cvLoading = false;
        if (input) input.value = "";
      }
    },
    removeStaffCv() {
      this.myObj.cv = "";
      if (this.$refs.staffCvFileInput) this.$refs.staffCvFileInput.value = "";
    },
    async convertHeicToJpeg(file) {
      const isHeic =
        /\.(heic|heif)$/i.test(file.name) ||
        ["image/heic", "image/heif"].includes(
          (file.type || "").toLowerCase()
        );

      if (!isHeic) {
        return file;
      }

      try {
        const result = await heic2any({
          blob: file,
          toType: "image/jpeg",
          quality: 0.9
        });
  
        const jpegBlob = Array.isArray(result) ? result[0] : result;
  
        const jpegName = file.name.replace(/\.(heic|heif)$/i, ".jpg");
  
        return new File([jpegBlob], jpegName, {
          type: "image/jpeg",
          lastModified: Date.now()
        });
      }
      catch(err) {
        console.log(err);
        return file;
      }
    },
    async selectlogo() {
      const file = this.$refs.fileProfile.files[0];
      if (!file) return;

      this.fileProfile = file;
      this.logoloading = true;

      // Convert HEIC to JPEG; other files remain unchanged.
      const fileToUpload = await this.convertHeicToJpeg(file);

      const localPreview = URL.createObjectURL(fileToUpload);
      this.plus = localPreview;

      try {
        const uploaded = await uploadToMySkool([fileToUpload]);
        const fn1 = getUploadPath(uploaded[0]);
        URL.revokeObjectURL(localPreview);
        this.plus = fn1;
        this.myObj.picture = fn1;
        this.logoloading = "loaded";
      } catch (err) {
        URL.revokeObjectURL(localPreview);
        this.plus = "";
        this.logoloading = false;
        console.log(err);
      } finally {
        if (this.$refs.fileProfile) this.$refs.fileProfile.value = "";
      }
    },

    CheckName() {
      var elem = this.$refs["name"];
      if (this.myObj.name == "" || this.myObj.name < 3) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckCampus() {
      if (this.isHeadOffice) {
        var elem = this.$refs["camp"];
        if (this.myObj.campusID == 0) {
          return (elem.state = false);
        } else {
          // Load report to staff when campus is selected
          this.loadReportToStaff();
          return (elem.state = true);
        }
      } else {
        // Load report to staff for non-head office users
        this.loadReportToStaff();
        return true;
      }
    },
    CheckECode() {
      if (this.reportDomain == "myskoolhpgs") {
        if (this.localID > 0 && this.formRights.hidePayroll) {
          return true;
        } else {
          var elem = this.$refs["emp_code"];
          let x = parseInt(this.myObj.empCode);
          if (isNaN(x) || x <= 0) {
            return (elem.state = false);
          } else {
            return (elem.state = true);
          }
        }
      } else return true;
    },
    CheckAppointment() {
      if (this.localID > 0 && this.formRights.hidePayroll) {
        return true;
      } else {
        var elem = this.$refs["appoint_date"];
        if (!this.myObj.appointedOn) {
          return (elem.state = false);
        } else {
          return (elem.state = true);
        }
      }
    },
    CheckFName() {
      var elem = this.$refs["fname"];
      if (this.myObj.father_name == "" || this.myObj.father_name < 3) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckDesignation() {
      var elem = this.$refs["design"];
      if (this.myObj.designation == "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckFContact() {
      // console.log(this.myObj.contact_father);
      // console.log(this.myObj.contact_father.length);
      if (this.myObj.contact_father.length == 12) {
        this.fphoneerror = false;
        return true;
      } else {
        this.fphoneerror = true;
        return false;
      }
    },
    CheckMContact() {
      // console.log(this.myObj.contact_mother);
      // console.log(this.myObj.contact_mother.length);
      if (this.myObj.contact_mother.length == 12) {
        this.mphoneerror = false;
        return true;
      } else {
        this.mphoneerror = true;
        return false;
      }
    },
    CheckContact() {
      // console.log(this.myObj.contact_student);
      // console.log(this.myObj.contact_student.length);
      if (this.myObj.contact_student.length == 12) {
        this.phoneerror = false;
        return true;
      } else {
        this.phoneerror = true;
        return false;
      }
    },
    CheckSContact(s, e) {
      // console.log(this.myObj.contact_number);
      // console.log(this.myObj.contact_student.length);
      // if (this.myObj.contact_number && this.myObj.contact_number.length == 12) {
      //   this.sPhoneerror = false;
      //   return true;
      // } else {
      //   this.sPhoneerror = true;
      //   return false;
      // }

      if (this.myObj.contact_number) {
        if (e.valid) {
          this.sPhoneerror = false;
          this.myObj.contact_number = e.number;
          // console.log(this.myObj.contact_number);
        } else {
          this.sPhoneerror = true;
        }
      } else {
        this.sPhoneerror = false;
      }
    },

    CheckEmerName() {
      var elem = this.$refs["emerName"];
      if (
        this.myObj.emergency_name == null ||
        this.myObj.emergency_name == ""
      ) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    checkDep() {
      var elem = this.$refs["dep"];
      if (this.myObj.departmentID == 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckEmerRelation() {
      var elem = this.$refs["emerRel"];
      if (
        this.myObj.emergency_relationship == null ||
        this.myObj.emergency_relationship == ""
      ) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckEmerContact() {
      if (this.myObj.emergency_contact.length == 12) {
        // console.log(this.myObj.emergency_contact.length);
        this.emerPhoneerror = false;
        return true;
      } else {
        this.emerPhoneerror = true;
        return false;
      }
    },
    CheckFees() {
      const regex = /^(\d*([.,](?=\d{3}))?\d+)+((?!\2)[.,]\d\d)?$/;
      var elem = this.$refs["monthly_fee"];
      if (regex.test(this.myObj.monthly_fee)) {
        if (this.myObj.monthly_fee == 0) {
          return (elem.state = false);
        } else {
          return (elem.state = true);
        }
      } else {
        return (elem.state = false);
      }
    },
    CheckGrNo() {
      var elem = this.$refs["grNo"];
      if (this.myObj.grNo == null || this.myObj.grNo == "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckTcID() {
      var elem = this.$refs["tcID"];
      if (this.teachingClassObj.clsID == 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    setSections() {
      if (this.selectedSections.at(-1) == 0) {
        this.selectedSections = [0];
      } else {
        this.selectedSections = this.selectedSections.filter((el) => el != 0);
      }
      // console.log(this.selectedSections);
    },
    CheckTSection() {
      // console.log(this.teachingClassObj.sections.length);
      var elem = this.$refs["tSection"];
      if (this.selectedSections.length === 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    setSubjects() {
      if (this.selectedSubs.at(-1) == 0) {
        this.selectedSubs = [0];
      } else {
        this.selectedSubs = this.selectedSubs.filter((el) => el != 0);
      }
      // console.log(this.selectedSubs);
    },
    CheckTSubject() {
      // console.log(this.teachingClassObj.subjects.length);
      var elem = this.$refs["tSubjects"];
      if (this.selectedSubs.length === 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    FillSections(text) {
      // console.log(this.teachingClassObj.clsID);

      this.classes.forEach((elem) => {
        if (elem.cls.id === this.teachingClassObj.clsID) {
          if (elem.sections.length != 0) {
            // console.log("not null");
            this.admittedSections = [...elem.sections];
            this.admittedSections.unshift({ section: "All Sections", id: 0 });
          } else {
            // console.log("null");
            this.admittedSections = [];
          }
        }
      });
      if (text != "filled") this.selectedSections = [];
      // console.log("Thisis", this.admittedSections);

      if (this.subObj && this.subObj.valueBool) {
        this.selectedSubs = [];
        this.subjects = [];
        this.LoadByClass();
      }
    },
    async assign() {
      this.teachingClassObj.tID = parseInt(this.$route.params.id);
      // console.log(this.teachingClassObj);

      this.CheckTcID();
      this.CheckTSubject();
      this.CheckTSection();
      if (
        this.CheckTcID() == false ||
        this.CheckTSubject() == false ||
        this.CheckTSection() == false
      ) {
        return this.$bvToast.toast(this.$t("Please enter all the required fields."), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-right",
        });
      } else {
        this.assigning = true;

        if (this.selectedSections[0] == 0) {
          let secArr = this.classes.find(
            (el) => el.cls.id === this.teachingClassObj.clsID
          ).sections;
          let secs = secArr.reduce((acc, el) => {
            acc.push(el.id);
            return acc;
          }, []);
          this.teachingClassObj.sections = secs;
        } else {
          this.teachingClassObj.sections = this.selectedSections;
        }

        // console.log(this.teachingClassObj.sections);

        if (this.selectedSubs[0] == 0) {
          let subs = this.subjects.reduce((acc, el) => {
            if (el.id != 0) acc.push(el.id);
            return acc;
          }, []);
          this.teachingClassObj.subjects = subs;
        } else {
          this.teachingClassObj.subjects = this.selectedSubs;
        }
        // console.log(this.teachingClassObj.subjects);

        var status = await this.post({
          url:
            this.$store.state.domain +
            "TeacherClasses/SaveData?db=" +
            this.$store.state.userData.db,
          body: this.teachingClassObj,
          message: "Classes assigned successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) {
          this.LoadTeachingClasses();
          this.teachingClassObj = {
            tID: this.$route.params.id,
            clsID: 0,
            sections: [],
            subjects: [],
            campusID: this.$store.state.userData.cId,
          };
          this.selectedSections = [];
          this.selectedSubs = [];
        }
        this.assigning = false;
      }
    },
    FillSectionsCurrent() {
      //return console.log(this.classes);
      this.classes.forEach((elem) => {
        if (elem.id == this.myObj.class_current) {
          this.currentSections = elem.sections.split(",");
        }
      });
      // console.log("Thisis", this.admittedSections);
    },
    async LoadData(id) {
      // "staff/LoadSelectedStaff?db=" +
      var obj = {
        url:
          this.$store.state.domain +
          "staff/LoadSelectedStaffWithShare?db=" +
          this.$store.state.userData.db +
          "&id=" +
          id,
        token: this.$store.state.userData.token,
      };
      let res = await this.get(obj);
      //  console.log(res);
      this.myObj = res.staff;
      this.sanitizeStaffDesignation();
      // streamID 0 / missing => All Streams (sentinel option id)
      this.normalizeStaffStreamID();
      // Map userLinkID back to userID for domains with user link field
      if (this.showUserLinkField) {
        // Ensure users are loaded before setting userID
        if (this.iussUserOptions.length === 0) {
          await this.loadIussUsers();
        }
        if (this.myObj.userLinkID && !this.myObj.userID) {
          this.myObj.userID = this.myObj.userLinkID;
        }
        // Convert userID to number to match with option IDs
        if (this.myObj.userID != null) {
          this.myObj.userID = Number(this.myObj.userID);
        }
      }
      // Set default values for deduction toggles if not present
      if (this.myObj.eobiDeduct === undefined) {
        this.myObj.eobiDeduct = false;
      }
      if (this.myObj.pfDeduct === undefined) {
        this.myObj.pfDeduct = false;
      }
      if (this.myObj.offLeavesAllowed === undefined) {
        this.myObj.offLeavesAllowed = false;
      }
      if (this.myObj.offAttendanceAllowances === undefined) {
        this.myObj.offAttendanceAllowances = false;
      }
      this.loanData = res.advanceAndLoans;
      this.loanDetails = res.loanDetails;
      this.deposits = res.securityDepositGrid;
      this.depoSaveList = res.securityDeposit;
      if (this.depoSaveList == null) this.depoSaveList = [];
      this.history = res.incrementHistory;

      // Handle children data from API response
      if (res.children && res.children.length > 0) {
        // If API returns children data, use it
        this.myObj.children = res.children;
      } else {
        // If no children data, ensure at least one empty entry for editing
        this.myObj.children = [
          {
            id: 0, // Children information record ID (set by backend)
            tID: 0, // Staff/Teacher ID (will be set to myObj.id)
            sID: 0, // Student ID (child's student ID)
            name: "", // Child's name
            className: "", // Child's class/grade
            image: "", // Child's profile image
            campusID: this.$store.state.userData.cId, // Campus ID
          },
        ];
      }

      this.AddingInfo = res.staffAllowances;
      if (this.AddingInfo.length == 0) {
        this.AddingInfo = [
          {
            id: 0,
            tID: this.myObj.id,
            amount: 0,
            allowanceID: 0,
            campusID: this.myObj.campusID,
          },
        ];
      } else this.changeAllow();

      this.deductInfo = res.staffDeductions;
      if (this.deductInfo.length == 0) {
        this.deductInfo = [
          {
            id: 0,
            tID: this.myObj.id,
            amount: 0,
            deductionID: 0,
            campusID: this.myObj.campusID,
          },
        ];
      } else this.changeDed();

      this.timingInfo = res.timings;
      if (this.timingInfo.length == 0) {
        this.timingInfo = [
          {
            id: 0,
            tID: this.myObj.id,
            day: "monday",
            timeIn: "2024-05-05T08:00:00",
            timeOut: "2024-05-05T13:00:00",
            dayOff: false,
            reportingTime: "2024-05-05T08:00:00",
            campusID: this.myObj.campusID,
          },
        ];
      }
      this.sortTimingInfoByDay();
      this.changeDay();

      this.settingData = res.policy;
      this.setPolicy();

      if (this.myObj.picture !== null && this.myObj.picture !== "") {
        this.plus = this.myObj.picture;
        this.logoloading = "loaded";
      }
      if (this.myObj.documents) {
        this.documents = this.myObj.documents.split(",");
      }

      // Merge saved staffDocumentDetails into docData after LoadStaffDocs finishes
      if (res.staffDocumentDetails && res.staffDocumentDetails.length > 0) {
        // Save for later merge once LoadStaffDocs has populated docData
        this._savedDocDetails = res.staffDocumentDetails;
      }

      this.pClassData = res.classShare;
      this.LoadSettings("check");

      if (this.reportDomain == "myskoolhpgs") {
        this.loadAccounts();
      }

      await this.LoadInfo();
      if (this.pClassData.length > 0) {
        this.pClassData.forEach((el, ind) => {
          this.FillPSec(el, ind, "filled");
        });
      }
      this.LoadDep();

      // Ensure designation is properly set for v-select after data is loaded
      this.sanitizeStaffDesignation();
      if (
        this.myObj.designation &&
        this.designations.length > 0 &&
        !this.isCorruptDesignationValue(this.myObj.designation)
      ) {
        const designationExists = this.designations.find(
          (d) => d.name === this.myObj.designation
        );
        if (!designationExists) {
          this.designations.push({
            id: this.designations.length + 1,
            name: this.myObj.designation,
            departmentID: Number(this.myObj.departmentID) || 0,
          });
        }
      }

      // Load selected staff for edit mode - wait to ensure reportsTo is set from API response
      this.$nextTick(() => {
        // Small delay to ensure all data is properly set
        setTimeout(() => {
          this.loadSelectedStaffForEdit();
        }, 100);
      });
    },
    setPolicy() {
      this.leavesObj = this.settingData.find(
        (el) => el.categoy == "Leave Allowed"
      );

      // console.log(this.leavesObj);
      this.lateObj = this.settingData.find(
        (el) => el.categoy == "Late Coming Scale"
      );
      // console.log(this.lateObj);
      this.shortObj = this.settingData.find(
        (el) => el.categoy == "Short Leave Scale"
      );
      // console.log(this.shortObj);
      this.attObj = this.settingData.find(
        (el) => el.categoy == "Attendance Allowance"
      );
      // console.log(this.attObj);
      this.encashObj = this.settingData.find(
        (el) => el.categoy == "Leave Encashment"
      );
      // console.log(this.encashObj);

      this.maxObj = this.settingData.find(
        (el) => el.categoy == "Max Leave per Month"
      );
      if (!this.maxObj) {
        this.maxObj = {
          id: 0,
          tID: this.$route.params.id,
          categoy: "Max Leave per Month",
          enabled: this.leavesObj.option == "Yearly",
          number: 0,
          option: "",
          campusID: this.$store.state.userData.cId,
        };
      }
      // console.log(this.maxObj);
      this.minuteObj = this.settingData.find(
        (el) => el.categoy == "Late Coming Deduction per Minute"
      );
      if (!this.minuteObj) {
        this.minuteObj = {
          id: 0,
          tID: this.$route.params.id,
          categoy: "Late Coming Deduction per Minute",
          enabled: false,
          number: 0,
          option: "",
          campusID: this.$store.state.userData.cId,
        };
      }
      // console.log(this.minuteObj);
      this.earlyObj = this.settingData.find(
        (el) => el.categoy == "Early Departure"
      );
      if (!this.earlyObj) {
        this.earlyObj = {
          id: 0,
          tID: this.$route.params.id,
          categoy: "Early Departure",
          enabled: false,
          number: 0,
          option: "No",
          campusID: this.$store.state.userData.cId,
        };
      }
      // console.log(this.earlyObj);
      this.sickObj = this.settingData.find((el) => el.categoy == "Sick Leave");
      if (!this.sickObj) {
        this.sickObj = {
          id: 0,
          tID: this.$route.params.id,
          categoy: "Sick Leave",
          enabled: this.leavesObj.enabled,
          number: 0,
          option: this.leavesObj.option,
          campusID: this.$store.state.userData.cId,
        };
      }
    },
    policyOnAdd() {
      this.leavesObj = {
        id: 0,
        tID: 0,
        categoy: "Leave Allowed",
        enabled: false,
        number: 0,
        option: "Monthly",
        campusID: this.$store.state.userData.cId,
      };
      this.maxObj = {
        id: 0,
        tID: 0,
        categoy: "Max Leave per Month",
        enabled: false,
        number: 0,
        option: "",
        campusID: this.$store.state.userData.cId,
      };
      this.minuteObj = {
        id: 0,
        tID: 0,
        categoy: "Late Coming Deduction per Minute",
        enabled: false,
        number: 0,
        option: "",
        campusID: this.$store.state.userData.cId,
      };
      this.earlyObj = {
        id: 0,
        tID: 0,
        categoy: "Early Departure",
        enabled: false,
        number: 0,
        option: "No",
        campusID: this.$store.state.userData.cId,
      };
      this.lateObj = {
        id: 0,
        tID: 0,
        categoy: "Late Coming Scale",
        enabled: false,
        number: 0,
        option: "No",
        campusID: this.$store.state.userData.cId,
      };
      this.shortObj = {
        id: 0,
        tID: 0,
        categoy: "Short Leave Scale",
        enabled: false,
        number: 0,
        option: "No",
        campusID: this.$store.state.userData.cId,
      };
      this.attObj = {
        id: 0,
        tID: 0,
        categoy: "Attendance Allowance",
        enabled: false,
        number: 0,
        option: "Fixed amount",
        campusID: this.$store.state.userData.cId,
      };
      this.encashObj = {
        id: 0,
        tID: 0,
        categoy: "Leave Encashment",
        enabled: false,
        number: 0,
        option: "",
        campusID: this.$store.state.userData.cId,
      };
      this.sickObj = {
        id: 0,
        tID: 0,
        categoy: "Sick Leave",
        enabled: false,
        number: 0,
        option: "Monthly",
        campusID: this.$store.state.userData.cId,
      };
    },
    setMaxLeaves() {
      if (this.leavesObj.option == "Yearly") {
        this.maxObj.enabled = true;
      } else this.maxObj.enabled = false;
      // console.log(this.maxObj);
    },
    async LoadInfo() {
      this.dataloading = true;

      var obj = {
        url:
          this.$store.state.domain +
          "myclasses/LoadClasses?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          (this.myObj.campusID || this.$store.state.userData.cId) +
          "&dID=0",
        token: this.$store.state.userData.token,
      };
      this.classes = await this.get(obj);
      this.classesOptions = this.classes.map((el) => el.cls);

      this.dataloading = false;
    },
    async LoadDep() {
      var obj = {
        url:
          this.$store.state.domain +
          "StaffDepartments?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          (this.myObj.campusID || this.$store.state.userData.cId),
        token: this.$store.state.userData.token,
      };
      const result = await this.get(obj);
      const list = Array.isArray(result)
        ? result
        : Array.isArray(result?.data)
        ? result.data
        : [];
      this.deptOptions = list.map((dep) => ({
        ...dep,
        id: Number(dep.id),
        name: dep.name,
      }));
      if (this.myObj.departmentID != null) {
        this.myObj.departmentID = Number(this.myObj.departmentID) || 0;
      }
    },
    async LoadSettings(text) {
      let res = await this.post({
        url:
          this.$store.state.domain +
          "Settings/getSettings?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        body: ["subject_class", "payroll_share", "manage-rfID", "head_office"],
        context: this,
        token: this.$store.state.userData.token,
        subjects: true,
      });
      this.subObj = res.find((el) => el.key == "subject_class");
      this.pObj = res.find((el) => el.key == "payroll_share");
      this.rfObj = res.find((el) => el.key == "manage-rfID");
      this.isHeadOffice = res.find((el) => el.key == "head_office");

      if (this.isHeadOffice) {
        this.LoadCampuses();
      }

      if (this.pObj && this.pObj.valueBool) {
        this.LoadPSubs();
        if (text == "check" && this.pClassData.length > 0) {
          this.payrollByShare = true;
        }
      }

      if (this.subObj && this.subObj.valueBool) {
        this.subjects = [];
      } else this.LoadSubject();
    },
    async LoadSettingsAll() {
      let res = await this.post({
        url:
          this.$store.state.domain +
          "Settings/getSettingsAll?db=" +
          this.$store.state.userData.db,
        body: [
          "class_teacher_attd",
          "sd_amount_edit",
          "salary-campus-load",
          "increment-total",
          "salary_ledger",
          "doc_setting",
        ],
        context: this,
        token: this.$store.state.userData.token,
        subjects: true,
      });
      this.ctObj = res.find((el) => el.key == "class_teacher_attd");
      this.sdObj = res.find((el) => el.key == "sd_amount_edit");
      this.salaryCampusObj = res.find((el) => el.key == "salary-campus-load");
      this.incrementTotalObj = res.find((el) => el.key == "increment-total");
      this.salaryLedgerObj = res.find((el) => el.key == "salary_ledger");
      this.docSetting = res.find((el) => el.key == "doc_setting");

      if (this.docSetting && this.docSetting.valueBool) {
        this.LoadStaffDocs();
      }

      if (this.salaryLedgerObj?.valueBool) {
        this.LoadDeductionLinks();
        this.LoadCOA();
      }
    },
    async LoadCOA() {
      var obj = {
        url:
          this.$store.state.domain +
          "Accounts/LoadData?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.coaAccounts = await this.get(obj);
    },
    async LoadDeductionLinks() {
      var obj2 = {
        url:
          this.$store.state.domain +
          "Settings/GetAllByCampus?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&settingKey=deduction_links",
        token: this.$store.state.userData.token,
      };
      this.linkedAccounts = await this.get(obj2);
      // console.log("deduction links", this.linkedAccounts);
    },
    async LoadSubject() {
      var obj = {
        url:
          this.$store.state.domain +
          "Subjects?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.subjects = await this.get(obj);
      if (this.subjects.length > 0) {
        this.subjects.unshift({ subject: "All Subjects", id: 0 });
      }

      // console.log(this.subjects);
    },
    async LoadByClass() {
      var obj = {
        url:
          this.$store.state.domain +
          "Subjects/LoadSubjectClassData?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&clsID=" +
          this.teachingClassObj.clsID,
        token: this.$store.state.userData.token,
      };
      this.subjects = await this.get(obj);
      if (this.subjects.length > 0) {
        this.subjects.unshift({ subject: "All Subjects", id: 0 });
      }
      // console.log(this.subjects);
    },
    async LoadTeachingClasses() {
      // var axios = require("axios");
      // var config = {
      //   method: "get",
      //   url:
      //     this.$store.state.domain +
      //     "TeacherClasses/LoadData?db=" +
      //     this.$store.state.userData.db +
      //     "&tID=" +
      //     this.$route.params.id,
      //   headers: {
      //     Authorization: "bearer " + this.$store.state.userData.token,
      //   },
      // };

      // axios(config)
      //   .then((response) => {
      //     console.log(response.data);
      //     this.teachingClasses = response.data.data;
      //     console.log(this.teachingClasses);
      //     this.newTeachingList = [];
      //     this.teachingClasses.forEach((el) => {
      //       el.secSubjectsData.forEach((sec) => {
      //         let data = {
      //           clsID: el.cID,
      //           className: el.className,
      //           sections: [sec.sectionID],
      //           sectionName: sec.sectionName,
      //           subjects: sec.subjects,
      //         };
      //         this.newTeachingList.push(data);
      //       });
      //     });
      //   })
      //   .catch(function (error) {
      //     console.log(error);
      //   });
      var obj = {
        url:
          this.$store.state.domain +
          "TeacherClasses/LoadDataWebNew?db=" +
          this.$store.state.userData.db +
          "&tID=" +
          this.$route.params.id,
        token: this.$store.state.userData.token,
      };
      this.gridData = await this.get(obj);
      // console.log("grid", this.gridData);
    },
    async setTeacher(item, val) {
      let text = "Do you want to mark as Class Teacher?";
      if (!val) text = "Do you want to remove as Class Teacher?";
      let result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: text,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("Yes"),
      });

      if (result.isConfirmed) {
        let msg = "Marked as class teacher successfully!";
        if (!val) msg = "Removed as class teacher successfully!";
        var status = await this.post({
          url:
            this.$store.state.domain +
            "TeacherClasses/setClassTeacher?db=" +
            this.$store.state.userData.db +
            "&campusID=" +
            this.$store.state.userData.cId +
            "&tID=" +
            item.tID +
            "&clsID=" +
            item.clsID +
            "&secID=" +
            item.secID +
            "&val=" +
            val,
          body: null,
          message: msg,
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) this.LoadTeachingClasses();
      }
    },
    async DeleteSub(item) {
      // console.log(item);
      let result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("Yes, Delete it!"),
      });

      if (result.isConfirmed) {
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "Staff/DeleteSubjects?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&clsID=" +
            item.clsID +
            "&secID=" +
            item.secID +
            "&tID=" +
            item.tID,
          body: null,
          message: "subject removed successfully!",
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) this.LoadTeachingClasses();
      }
    },
    async removeSingle(row, sub) {
      // console.log(row, sub);
      let result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("Yes, Delete it!"),
      });

      if (result.isConfirmed) {
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "TeacherClasses/" +
            sub.id +
            "?db=" +
            this.$store.state.userData.db,
          body: null,
          message: "subject removed successfully!",
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) this.LoadTeachingClasses();
      }
      // let filteredSubs = row.subjects.filter((el) => el !== sub);
      // console.log(filteredSubs);

      // let subIDs = [];
      // this.subjects.forEach((sb) => {
      //   filteredSubs.forEach((el) => {
      //     if (el == sb.subject) {
      //       subIDs.push(sb.id);
      //     }
      //   });
      // });

      // console.log(subIDs);

      // let saveObj = {
      //   tID: row.tID,
      //   clsID: row.clsID,
      //   sections: [row.secID],
      //   subjects: subIDs,
      //   campusID: this.$store.state.userData.cId,
      // };
      // console.log(saveObj);

      // var status = await this.post({
      //   url:
      //     this.$store.state.domain +
      //     "TeacherClasses/SaveData?db=" +
      //     this.$store.state.userData.db,
      //   body: saveObj,
      //   message: "Classes assigned successfully.",
      //   context: this,
      //   token: this.$store.state.userData.token,
      // });
      // if (status) {
      //   this.LoadTeachingClasses();
      // }
    },
    setHalfHint() {
      var elem = this.$refs["shortScale"];
      var re = /^[0-9]+$/;

      if (this.shortObj.number != "" && this.shortObj.number != 0) {
        if (re.test(this.shortObj.number) && this.shortObj.number > 0) {
          this.showHalfHint = true;
          return (elem.state = true);
        } else {
          this.showHalfHint = false;
          return (elem.state = false);
        }
      } else {
        this.showHalfHint = false;
        return (elem.state = false);
      }
    },
    setLateHint() {
      var elem = this.$refs["lateScale"];
      if (this.lateObj.number != "" && this.lateObj.number != 0) {
        var re = /^[0-9]+$/;

        if (re.test(this.lateObj.number) && this.lateObj.number > 0) {
          this.showLateHint = true;
          return (elem.state = true);
        } else {
          this.showLateHint = false;
          return (elem.state = false);
        }
      } else {
        this.showLateHint = false;
        return (elem.state = false);
      }
    },

    checkAllowed() {
      if (this.localID > 0 && this.formRights.hidePayroll) {
        return true;
      } else {
        if (this.leavesObj.enabled) {
          var elem = this.$refs["all_cat"];
          if (this.leavesObj.option == "") {
            return (elem.state = false);
          } else {
            elem.state = true;
            if (this.checkLeaves() == true) {
              if (this.leavesObj.option == "Yearly")
                return this.checkMaxLeave();
            } else return false;
          }
        } else return true;
      }
    },
    checkLeaves() {
      var elem = this.$refs["leave_allowed"];
      var re = /^[0-9]+$/;
      let x = parseInt(this.leavesObj.number);
      if (!re.test(this.leavesObj.number) || isNaN(x) || x < 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    checkSalary() {
      if (this.localID > 0 && this.formRights.hidePayroll) {
        return true;
      } else {
        var elem = this.$refs["salary"];
        var re = /^[0-9]+$/;
        let x = parseInt(this.myObj.totalSalary);
        if (!re.test(this.myObj.totalSalary) || isNaN(x) || x < 0) {
          return (elem.state = false);
        } else {
          return (elem.state = true);
        }
      }
    },
    checkShare() {
      if (this.localID > 0 && this.formRights.hidePayroll) {
        return true;
      } else if (this.reportDomain == "myskoolskh" && this.myObj.sharePerc) {
        var elem = this.$refs["share"];
        let x = parseInt(this.myObj.sharePerc);
        if (isNaN(x) || x < 0 || x > 100) {
          return (elem.state = false);
        } else {
          return (elem.state = true);
        }
      }
      return true;
    },
    checkMaxLeave() {
      var elem = this.$refs["max_allowed"];
      var re = /^[0-9]+$/;
      let x = parseInt(this.maxObj.number);
      if (
        !re.test(this.maxObj.number) ||
        isNaN(x) ||
        x <= 0 ||
        x > this.leavesObj.number
      ) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    checkLate() {
      if (this.localID > 0 && this.formRights.hidePayroll) {
        return true;
      } else {
        if (this.lateObj.enabled) {
          return this.setLateHint();
        } else return true;
      }
    },
    checkShort() {
      if (this.localID > 0 && this.formRights.hidePayroll) {
        return true;
      } else {
        if (this.shortObj.enabled) {
          return this.setHalfHint();
        } else return true;
      }
    },
    checkFields() {
      setTimeout(() => {
        this.checkAtt();
      }, 200);
    },
    checkAtt() {
      if (this.localID > 0 && this.formRights.hidePayroll) {
        return true;
      } else {
        if (this.attObj.enabled) {
          if (this.attObj.option == "Fixed amount")
            return this.checkAmountAtt();
          else if (this.attObj.option == "Percentage") return this.checkPerc();
          // else return this.checkDays();
          else return true;
        } else return true;
      }
    },
    checkAmountAtt() {
      var elem = this.$refs["amount"];
      var re = /^[0-9]+$/;
      let x = parseInt(this.attObj.number);
      if (!re.test(this.attObj.number) || isNaN(x) || x < 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    checkPerc() {
      var elem = this.$refs["percentage"];
      // let x = parseFloat(this.attObj.number);
      var re = /^[0-9]+$/;
      let x = parseInt(this.attObj.number);
      if (!re.test(this.attObj.number) || isNaN(x) || x < 0 || x > 100) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    checkMax() {
      if (this.localID > 0 && this.formRights.hidePayroll) {
        return true;
      } else {
        if (this.encashObj.enabled) {
          var elem = this.$refs["maxEn"];
          var re = /^[0-9]+$/;
          let x = parseInt(this.encashObj.number);
          if (
            !re.test(this.encashObj.number) ||
            isNaN(x) ||
            x <= 0 ||
            x > this.leavesObj.number
          ) {
            return (elem.state = false);
          } else {
            return (elem.state = true);
          }
        } else return true;
      }
    },
    checkPerMin() {
      if (this.localID > 0 && this.formRights.hidePayroll) {
        return true;
      } else {
        if (this.minuteObj.enabled) {
          var elem = this.$refs["lateAmount"];
          var re = /^[0-9]+$/;
          let x = parseInt(this.minuteObj.number);
          if (!re.test(this.minuteObj.number) || isNaN(x) || x < 0) {
            return (elem.state = false);
          } else {
            return (elem.state = true);
          }
        }
        return true;
      }
    },
    setEarlyHint() {
      var elem = this.$refs["earlyScale"];
      if (this.earlyObj.number != "" && this.earlyObj.number != 0) {
        var re = /^[0-9]+$/;

        if (re.test(this.earlyObj.number) && this.earlyObj.number > 0) {
          this.showEarly = true;
          return (elem.state = true);
        } else {
          this.showEarly = false;
          return (elem.state = false);
        }
      } else {
        this.showEarly = false;
        return (elem.state = false);
      }
    },
    checkEarly() {
      if (this.localID > 0 && this.formRights.hidePayroll) {
        return true;
      } else {
        if (this.earlyObj.enabled) {
          return this.setEarlyHint();
        } else return true;
      }
    },
    checkPayrollShare() {
      let state = true;
      if (this.pObj && this.pObj.valueBool && this.payrollByShare) {
        this.pClassData.forEach((el) => {
          let x = parseInt(el.baseValue);
          let y = parseInt(el.sharePerStudent);
          if (
            el.cls == 0 ||
            el.secID == 0 ||
            el.subjectID == 0 ||
            !el.startingFrom ||
            isNaN(x) ||
            x < 0 ||
            isNaN(y) ||
            y <= 0
          ) {
            state = false;
          } else {
            el.baseValue = x;
            el.sharePerStudent = y;
          }
        });
      } else state = false;
      return state;
    },
    async formSubmitted() {
      this.submitting = true;
      this.CheckCampus();
      // this.CheckECode();
      this.CheckName();
      this.checkDep();
      this.CheckAppointment();
      this.checkSalary();
      // this.CheckSContact();
      if (this.CheckCampus() == false) {
        this.submitting = false;
        return this.$bvToast.toast(this.$t("Please select the campus"), {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      }
      // else if (this.CheckECode() == false) {
      //   this.submitting = false;
      //   return this.$bvToast.toast(this.$t("Please enter Employee code"), {
      //     title: this.$t("Error!"),
      //     variant: "danger",
      //     solid: true,
      //   });
      // }
      else if (this.checkDep() == false) {
        this.submitting = false;
        return this.$bvToast.toast(this.$t("Please select staff department."), {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      } else if (this.CheckName() == false) {
        this.submitting = false;
        return this.$bvToast.toast(this.$t("Invalid name entered!"), {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      } else if (this.CheckAppointment() == false) {
        this.submitting = false;
        return this.$bvToast.toast(this.$t("Please enter appointment date!"), {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      }
      // else if (this.sPhoneerror) {
      //   this.submitting = false;
      //   return this.$bvToast.toast(this.$t("Invalid contact entered!"), {
      //     title: this.$t("Error!"),
      //     variant: "danger",
      //     solid: true,
      //   });
      // }
      else if (this.CheckTimings() == false) {
        this.submitting = false;
        return this.$bvToast.toast(this.$t("Please enter the timings correctly!"), {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      } else if (this.checkSalary() == false) {
        this.submitting = false;
        return this.$bvToast.toast(this.$t("Please enter the salary correctly!"), {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      } else if (this.checkShare() == false) {
        this.submitting = false;
        return this.$bvToast.toast(this.$t("Please enter the share correctly!"), {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      } else if (
        this.CheckValues() == false ||
        this.checkAllowed() == false ||
        this.checkLate() == false ||
        this.checkShort() == false ||
        this.checkAtt() == false ||
        this.checkMax() == false ||
        this.checkPerMin() == false ||
        this.checkEarly() == false
      ) {
        this.submitting = false;
        return this.$bvToast.toast(
          this.$t("Please enter the payroll details correctly!"),
          {
            title: this.$t("Error!"),
            variant: "danger",
            solid: true,
          }
        );
      } else {
        this.myObj.totalSalary = parseInt(this.myObj.totalSalary);
        if (this.myObj.basicSalary != "" && this.myObj.basicSalary != null) {
          this.myObj.basicSalary = parseInt(this.myObj.basicSalary);
        } else this.myObj.basicSalary = 0;

        if (!this.myObj.sharePerc) {
          this.myObj.sharePerc = 0;
        } else {
          this.myObj.sharePerc = parseInt(this.myObj.sharePerc);
        }

        if (this.myObj.empCode) {
          let x = parseInt(this.myObj.empCode);
          if (isNaN(x) || x <= 0) this.myObj.empCode = 0;
          else this.myObj.empCode = x;
        } else this.myObj.empCode = 0;

        // console.log("!!!!!!!!!!!!!", this.myObj);
        if (this.myObj.date_of_birth == "") {
          this.myObj.date_of_birth = null;
        }
        if (this.myObj.appointedOn == "") {
          this.myObj.appointedOn = null;
        }
        if (this.myObj.leaves_allowed_from == "") {
          this.myObj.leaves_allowed_from = null;
        }
        if (this.myObj.appointedOn && !this.myObj.leaves_allowed_from) {
          this.myObj.leaves_allowed_from = this.myObj.appointedOn;
        }
        if (!this.myObj.registrationDate) {
          this.myObj.registrationDate = this.myObj.appointedOn
            ? this.myObj.appointedOn
            : new Date().toJSON();
        }
        if (this.myObj.resginationDate == "") {
          this.myObj.resginationDate = null;
        }
        if (this.myObj.leaving_date == "") {
          this.myObj.leaving_date = null;
        }
        if (this.myObj.rejoining_date == "") {
          this.myObj.rejoining_date = null;
        }
        if (this.myObj.partTimerDays) {
          this.myObj.partTimerDays = this.myObj.partTimerDays.toString();
        }

        // policy parsing
        if (!this.leavesObj.number) this.leavesObj.number = 0;
        else this.leavesObj.number = parseInt(this.leavesObj.number);
        if (!this.lateObj.number) this.lateObj.number = 0;
        else this.lateObj.number = parseInt(this.lateObj.number);
        if (!this.shortObj.number) this.shortObj.number = 0;
        else this.shortObj.number = parseInt(this.shortObj.number);
        if (!this.attObj.number) this.attObj.number = 0;
        else this.attObj.number = parseInt(this.attObj.number);
        if (!this.encashObj.number) this.encashObj.number = 0;
        else this.encashObj.number = parseInt(this.encashObj.number);
        if (!this.maxObj.number) this.maxObj.number = 0;
        else this.maxObj.number = parseInt(this.maxObj.number);
        if (!this.minuteObj.number) this.minuteObj.number = 0;
        else this.minuteObj.number = parseInt(this.minuteObj.number);
        if (!this.earlyObj.number) this.earlyObj.number = 0;
        else this.earlyObj.number = parseInt(this.earlyObj.number);
        if (!this.sickObj.number) this.sickObj.number = 0;
        else this.sickObj.number = parseInt(this.sickObj.number);

        // same toggle for sick and casual leaves
        this.sickObj.option = this.leavesObj.option;
        this.sickObj.enabled = this.leavesObj.enabled;

        // Convert previousSalary to number to match backend System.Int64 requirement
        this.myObj.previousSalary =
          parseInt(this.myObj.previousSalary, 10) || 0;

        let newLoadDet = this.loanData.reduce((acc, el) => {
          if (el.records) {
            acc = [...acc, ...el.records];
          }
          return acc;
        }, []);
        // console.log(newLoadDet);

        // Map userID to userLinkID for save payload
        const staffToSave = { ...this.myObj };
        staffToSave.streamID = streamIdForApi(staffToSave.streamID);
        staffToSave.offLeavesAllowed = this.isMyskoolStJacobDomain
          ? !!staffToSave.offLeavesAllowed
          : false;
        staffToSave.offAttendanceAllowances = this.isMyskoolStJacobDomain
          ? !!staffToSave.offAttendanceAllowances
          : false;
        if (this.showUserLinkField) {
          // Convert userID to string if it exists and is valid
          if (
            staffToSave.userID != null &&
            staffToSave.userID !== undefined &&
            staffToSave.userID !== ""
          ) {
            staffToSave.userLinkID = String(staffToSave.userID);
          } else {
            // If userID is null/undefined/empty, remove userLinkID from payload to avoid validation errors
            delete staffToSave.userLinkID;
          }
        }

        let saveObj = {
          staff: staffToSave,
          timings: this.timingInfo,
          incrementHistory: this.history,
          staffAllowances: this.AddingInfo,
          staffDeductions: this.deductInfo,
          advanceAndLoans: this.loanData,
          securityDeposit: this.depoSaveList,
          policy: [
            this.leavesObj,
            this.lateObj,
            this.shortObj,
            this.attObj,
            this.encashObj,
            this.maxObj,
            this.minuteObj,
            this.earlyObj,
            this.sickObj,
          ],
          loanDetails: [...this.loanDetails, ...newLoadDet],
          securityDepositGrid: this.deposits,
          classShare: this.checkPayrollShare() ? this.pClassData : [],
          children:
            this.reportDomain === "myskooliuss" ||
            this.reportDomain === "myskool"
              ? this.myObj.children
              : [],
          staffDocumentDetails:
            this.docSetting && this.docSetting.valueBool
              ? this.docData.map((dd) => ({
                  id: 0,
                  docID: dd.id,
                  url: dd.url || null,
                  meta_title: dd.title,
                }))
              : [],
        };
        // console.log(saveObj);
        var message = "Staff added successfully.";
        if (this.myObj.id > 0) message = "Staff updated successfully.";

        // "staff/SaveWithPolicy?db=" +
        const userID =
          this.$store.state.userData.userID ||
          this.$store.state.userData.userLinkID ||
          this.$store.state.userData.localID ||
          0;
        var status = await this.postObj({
          url:
            this.$store.state.domain +
            "staff/SaveWithShare?db=" +
            this.$store.state.userData.db +
            "&userID=" +
            userID,
          body: saveObj,
          message: message,
          context: this,
          token: this.$store.state.userData.token,
          showError: true,
        });
        this.submitting = false;
        if (status) {
          if (this.myObj.id == 0) {
            if (status.staff.id > 0) {
              var domain =
                window.location.pathname.split("/")[0] +
                "/" +
                window.location.pathname.split("/")[1] +
                "/" +
                status.staff.id;

              window.open(domain, "_self");
            }
          } else {
            this.$router.push("/manage-staff");
          }
        }
      }
    },
    openGraceTimeModal() {
      this.$bvModal.show("grace-time-modal");
      this.graceTimeInput = this.myObj.graceMins || 0;
    },
    checkGrace() {
      var elem = this.$refs["grace"];
      let x = parseInt(this.graceTimeInput);
      if (isNaN(x) || x <= 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    async applyGrace() {
      if (this.checkGrace() == false) {
        this.$bvToast.toast(this.$t("Grace time must be greater than 0 minutes"), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-right",
        });
      } else {
        let result = await Swal.fire({
          title: this.$t("Are you sure?"),
          text:
            "Do you want to update the staff Check-in time by " +
            this.graceTimeInput +
            " minutes?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          confirmButtonText: this.$t("Yes"),
        });
        if (result.isConfirmed) {
          this.applying = true;
          this.timingInfo = this.timingInfo.filter(
            (el) => el.day != "" && el.timeIn != "" && el.timeOut != ""
          );
          this.graceTimeInput = parseInt(this.graceTimeInput);
          this.myObj.graceMins = this.graceTimeInput;

          let obj = {
            tID: this.myObj.id,
            graceTime: this.graceTimeInput,
            timings: this.timingInfo,
          };
          // console.log(obj);

          var status = await this.post({
            url:
              this.$store.state.domain +
              "staff/ApplyGraceTime?db=" +
              this.$store.state.userData.db +
              "&cID=" +
              this.$store.state.userData.cId,
            body: obj,
            message: "Grace time applied successfully",
            context: this,
            token: this.$store.state.userData.token,
            returnData: true,
          });
          this.applying = false;
          if (status) {
            this.$bvModal.hide("grace-time-modal");
            if (Array.isArray(status)) {
              this.timingInfo = status;
              this.sortTimingInfoByDay();
            }
          }
        }
      }
    },
    openReturnModal(item) {
      this.returnLoanObj = { ...item };
      this.$bvModal.show("return-loan-modal");
    },

    async saveLoanReturn() {
      // Validate return date
      if (!this.returnLoanObj.returnedOn) {
        this.$refs.returnDate.state = false;
        return;
      }

      // Show confirmation dialog before proceeding
      const result = await Swal.fire({
        title: "Confirm Return",
        text: "Are you sure you want to mark this loan as returned?",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#7367F0",
        cancelButtonColor: "#EA5455",
        confirmButtonText: "Yes, mark as returned",
        cancelButtonText: this.$t("Cancel"),
      });

      // If user cancels, exit the function
      if (!result.isConfirmed) {
        return;
      }

      this.returningLoan = true;

      try {
        // Set isReturned to true for the API call
        this.returnLoanObj.isReturned = true;
        this.returnLoanObj.salaryID = 0;

        const status = await this.put({
          url:
            this.$store.state.domain +
            "StaffAdvaceAndLoansInstallmentPlan/" +
            this.returnLoanObj.id +
            "?db=" +
            this.$store.state.userData.db,
          body: this.returnLoanObj,
          message: "Loan marked as returned successfully!",
          context: this,
          token: this.$store.state.userData.token,
        });

        if (status) {
          this.$bvModal.hide("return-loan-modal");

          // Find and update the original item in the loan list
          const loanItemIndex = this.installData.findIndex(
            (loan) => loan.id === this.returnLoanObj.id
          );
          if (loanItemIndex !== -1) {
            // Update the item directly in the array to reflect the changes
            this.installData[loanItemIndex].isReturned = true;
            this.installData[loanItemIndex].returnedOn =
              this.returnLoanObj.returnedOn;
          }
        }
      } catch (error) {
        console.error("Error returning loan:", error);
      } finally {
        this.returningLoan = false;
      }
    },

    // Children Management Methods
    addChild() {
      this.myObj.children.push({
        id: 0,
        tID: 0,
        sID: 0,
        name: "",
        className: "",
        image: "",
        campusID: this.$store.state.userData.cId,
      });
    },

    removeChild(index) {
      if (this.myObj.children.length > 1) {
        this.myObj.children.splice(index, 1);
      }
    },

    handleChildAvatarUpload(index) {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.onchange = (e) => {
        const file = e.target.files[0];
        if (file) {
          const reader = new FileReader();
          reader.onload = (e) => {
            this.myObj.children[index].image = e.target.result;
          };
          reader.readAsDataURL(file);
        }
      };
      input.click();
    },

    getInitials(name) {
      if (!name) return "";
      const names = name.split(" ");
      if (names.length === 1) {
        return names[0].charAt(0).toUpperCase();
      }
      return (
        names[0].charAt(0) + names[names.length - 1].charAt(0)
      ).toUpperCase();
    },

    getStudentInitials(name) {
      if (!name) return "";
      const names = name.split(" ");
      if (names.length === 1) {
        return names[0].charAt(0).toUpperCase();
      }
      return (
        names[0].charAt(0) + names[names.length - 1].charAt(0)
      ).toUpperCase();
    },

    // Children Selector Methods
    openChildrenSelector() {
      this.childrenSelectorVisible = true;
      this.childrenSearchQuery = "";
      this.filteredChildrenOptions = [];
    },

    async onChildrenSearchChange() {
      if (this.childrenSearchQuery.length < 2) {
        this.filteredChildrenOptions = [];
        return;
      }

      this.childrenSearchLoading = true;
      try {
        const obj = {
          url: `${this.$store.state.domain}Students/Search?db=${
            this.$store.state.userData.db
          }&cID=${this.$store.state.userData.cId}&keyword=${encodeURIComponent(
            this.childrenSearchQuery
          )}&type=`,
          token: this.$store.state.userData.token,
        };

        const response = await this.get(obj);

        // Handle different response structures
        let studentsData = [];
        if (response && response.data && Array.isArray(response.data)) {
          studentsData = response.data;
        } else if (Array.isArray(response)) {
          studentsData = response;
        }

        this.filteredChildrenOptions = studentsData.map((student) => ({
          ...student,
          name: student.name || "Unknown Student",
        }));
      } catch (error) {
        console.error("Student search error:", error);
        this.filteredChildrenOptions = [];
      } finally {
        this.childrenSearchLoading = false;
      }
    },

    selectChild(student) {
      // Find the first empty child slot (sID = 0 or empty name)
      const emptyChildIndex = this.myObj.children.findIndex(
        (child) =>
          child.sID === 0 ||
          child.sID === "" ||
          !child.name ||
          child.name === ""
      );

      const childData = {
        id: 0, // Will be set by backend
        tID: this.myObj.id || 0, // Staff ID
        sID: student.id, // Student ID
        name: student.name,
        className: student.cls_current ? student.cls_current.name : "",
        image: student.picture || "",
        campusID: this.$store.state.userData.cId,
      };

      if (emptyChildIndex !== -1) {
        // Fill the first empty slot
        this.$set(this.myObj.children, emptyChildIndex, childData);
      } else {
        // No empty slot found, add a new child
        this.myObj.children.push(childData);
      }

      // Close the sidebar
      this.childrenSelectorVisible = false;
    },

    // Handle existing pay mode data and auto-adjust dropdown
    handleExistingPayMode() {
      if (this.myObj.payMode) {
        // Map old values to new values for backward compatibility
        const payModeMapping = {
          "Cash Salary": "Cash",
          "Bank Transfer": "Bank",
          "Cheque Salary": "Cheque",
          Cash: "Cash",
          Bank: "Bank",
          Cheque: "Cheque",
        };

        // If the current payMode exists in mapping, update it
        if (payModeMapping[this.myObj.payMode]) {
          this.myObj.payMode = payModeMapping[this.myObj.payMode];
        }
      }
    },

    // Deduction Policy Methods
    async loadDeductionTypes() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Deductions?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        };

        const response = await this.get(obj);
        let rawData = [];
        if (response && Array.isArray(response)) {
          rawData = response;
        } else if (response && response.data && Array.isArray(response.data)) {
          rawData = response.data;
        }

        // Filter only deduction types (exclude contribution types)
        this.deductionTypes = rawData
          .filter(
            (item) =>
              item.title !== "PF Contribution" &&
              item.title !== "EOBI Contribution"
          )
          .map((item) => ({
            id: item.id,
            title: item.title,
            name: item.title,
          }));

        console.log("Loaded deduction types:", this.deductionTypes);
      } catch (error) {
        console.error("Error loading deduction types:", error);
        this.deductionTypes = [];
      }
    },

    async loadDeductionPolicyData() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "DeductionPolicy?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        };

        const response = await this.get(obj);
        let rawData = [];
        if (response && Array.isArray(response)) {
          rawData = response;
        } else if (response && response.data && Array.isArray(response.data)) {
          rawData = response.data;
        }

        // Filter only deduction data (exclude contribution data)
        this.deductionPolicyData = rawData
          .filter(
            (item) => item && typeof item === "object" && item.id !== undefined
          )
          .filter((item) => {
            const policyType = this.deductionTypes.find(
              (type) => type.id === item.allowanceTypeID
            );
            return (
              policyType &&
              policyType.title !== "PF Contribution" &&
              policyType.title !== "EOBI Contribution"
            );
          });

        console.log("Loaded deduction policy data:", this.deductionPolicyData);
      } catch (error) {
        console.error("Error loading deduction policy data:", error);
        this.deductionPolicyData = [];
      }
    },

    initializeDeductionPolicy() {
      if (!this.deductionPolicyData || this.deductionPolicyData.length == 0) {
        this.deductionPolicySett = [
          {
            id: 0,
            allowanceTypeID: null,
            allowanceName: "",
            perc: 0,
            campusID: this.$store.state.userData.cId,
          },
        ];
      } else {
        this.deductionPolicySett = this.deductionPolicyData.map((el) => ({
          id: el.id || 0,
          allowanceTypeID: el.allowanceTypeID || null,
          allowanceName: el.allowanceName || "",
          perc: el.perc || 0,
          campusID: el.campusID || this.$store.state.userData.cId,
        }));
      }

      if (this.deductionPolicySett.length === 0) {
        this.deductionPolicySett = [
          {
            id: 0,
            allowanceTypeID: null,
            allowanceName: "",
            perc: 0,
            campusID: this.$store.state.userData.cId,
          },
        ];
      }
    },

    changeDeductionPolicySettAllow() {
      this.deductionPolicySettIDs = this.deductionPolicySett.reduce(
        (acc, el) => {
          if (el.allowanceTypeID) {
            acc.push(el.allowanceTypeID);
          }
          return acc;
        },
        []
      );

      // Update allowance names when allowance type changes
      this.deductionPolicySett.forEach((item) => {
        if (item.allowanceTypeID) {
          const allowanceType = this.deductionTypes.find(
            (type) => type.id === item.allowanceTypeID
          );
          if (allowanceType) {
            item.allowanceName = allowanceType.title;
          }
        }
      });
    },

    addDeductionPolicySett(item) {
      this.deductionPolicySett = [
        ...this.deductionPolicySett,
        {
          id: 0,
          allowanceTypeID: null,
          allowanceName: "",
          perc: 0,
          campusID: this.$store.state.userData.cId,
        },
      ];
    },

    async removeDeductionPolicySett(item, ind) {
      if (item.id == 0) {
        this.deductionPolicySett = this.deductionPolicySett.filter(
          (_, index) => index !== ind
        );
        this.changeDeductionPolicySettAllow();
      } else {
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "DeductionPolicy/" +
            item.id +
            "?db=" +
            this.$store.state.userData.db,
          body: null,
          grading: true,
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) {
          this.deductionPolicySett = this.deductionPolicySett.filter(
            (_, index) => index !== ind
          );
          this.deductionPolicyData = this.deductionPolicyData.filter(
            (el) => el.id !== item.id
          );
          this.changeDeductionPolicySettAllow();
        }
      }
    },

    checkDeductionPolicySett() {
      let state = true;
      this.deductionPolicySett.forEach((el) => {
        el.perc = parseFloat(el.perc);
        if (el.allowanceTypeID == null || isNaN(el.perc) || el.perc < 0) {
          state = false;
        }
      });
      return state;
    },

    async saveDeductionPolicySett() {
      if (!this.checkDeductionPolicySett()) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t("Error"),
            text: "Please fill all required fields correctly.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
        return;
      }

      this.savingDeductionPolicySett = true;
      try {
        const saveData = this.deductionPolicySett.map((item) => {
          // Get the allowance name from the selected type
          const allowanceType = this.deductionTypes.find(
            (type) => type.id === item.allowanceTypeID
          );
          const allowanceName = allowanceType ? allowanceType.title : "";

          return {
            id: item.id,
            allowanceTypeID: item.allowanceTypeID,
            allowanceName: allowanceName,
            perc: item.perc,
            campusID: item.campusID,
          };
        });

        console.log(
          "Saving deduction policy data with allowance names:",
          saveData
        );

        const response = await this.post({
          url:
            this.$store.state.domain +
            "DeductionPolicy/SaveData?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          body: saveData,
          message: "Deduction policy saved successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });

        if (response) {
          await this.loadDeductionPolicyData();
          this.initializeDeductionPolicy();
        }
      } catch (error) {
        console.error("Error saving deduction policy:", error);
      } finally {
        this.savingDeductionPolicySett = false;
      }
    },

    // Contribution Policy Methods
    async loadContributionTypes() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Deductions?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        };

        const response = await this.get(obj);
        let rawData = [];
        if (response && Array.isArray(response)) {
          rawData = response;
        } else if (response && response.data && Array.isArray(response.data)) {
          rawData = response.data;
        }

        // Filter only contribution types
        this.contributionTypes = rawData
          .filter(
            (item) =>
              item.title === "PF Contribution" ||
              item.title === "EOBI Contribution"
          )
          .map((item) => ({
            id: item.id,
            title: item.title,
            name: item.title,
          }));

        // If no contribution types found, add them manually
        if (this.contributionTypes.length === 0) {
          this.contributionTypes = [
            { id: 1001, title: "PF Contribution", name: "PF Contribution" },
            { id: 1002, title: "EOBI Contribution", name: "EOBI Contribution" },
          ];
        }

        console.log("Loaded contribution types:", this.contributionTypes);
      } catch (error) {
        console.error("Error loading contribution types:", error);
        this.contributionTypes = [];
      }
    },

    async loadContributionPolicyData() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "DeductionPolicy?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        };

        const response = await this.get(obj);
        let rawData = [];
        if (response && Array.isArray(response)) {
          rawData = response;
        } else if (response && response.data && Array.isArray(response.data)) {
          rawData = response.data;
        }

        // Filter only contribution data
        this.contributionPolicyData = rawData
          .filter(
            (item) => item && typeof item === "object" && item.id !== undefined
          )
          .filter((item) => {
            const policyType = this.contributionTypes.find(
              (type) => type.id === item.allowanceTypeID
            );
            return (
              policyType &&
              (policyType.title === "PF Contribution" ||
                policyType.title === "EOBI Contribution")
            );
          })
          .map((item) => {
            // Ensure allowanceName is populated from contributionTypes
            const policyType = this.contributionTypes.find(
              (type) => type.id === item.allowanceTypeID
            );
            return {
              ...item,
              allowanceName: policyType
                ? policyType.title
                : item.allowanceName || "",
            };
          });

        console.log(
          "Loaded contribution policy data:",
          this.contributionPolicyData
        );
      } catch (error) {
        console.error("Error loading contribution policy data:", error);
        this.contributionPolicyData = [];
      }
    },

    initializeContributionPolicy() {
      if (
        !this.contributionPolicyData ||
        this.contributionPolicyData.length == 0
      ) {
        this.contributionPolicySett = [
          {
            id: 0,
            allowanceTypeID: null,
            allowanceName: "",
            perc: 0,
            campusID: this.$store.state.userData.cId,
            eobiContribution: 0,
            PFcontribution: 0,
          },
        ];
      } else {
        this.contributionPolicySett = this.contributionPolicyData.map((el) => ({
          id: el.id || 0,
          allowanceTypeID: el.allowanceTypeID || null,
          allowanceName: el.allowanceName || "",
          perc: el.perc || 0,
          campusID: el.campusID || this.$store.state.userData.cId,
          eobiContribution: el.eobiContribution || 0,
          PFcontribution: el.PFcontribution || 0,
        }));
      }

      if (this.contributionPolicySett.length === 0) {
        this.contributionPolicySett = [
          {
            id: 0,
            allowanceTypeID: null,
            allowanceName: "",
            perc: 0,
            campusID: this.$store.state.userData.cId,
            eobiContribution: 0,
            PFcontribution: 0,
          },
        ];
      }

      // Populate allowanceName for existing entries based on allowanceTypeID
      this.contributionPolicySett.forEach((item) => {
        if (item.allowanceTypeID) {
          const policyType = this.contributionTypes.find(
            (type) => type.id === item.allowanceTypeID
          );
          if (policyType) {
            item.allowanceName = policyType.title;
          }
        }
      });
    },

    isLockedContribution(item) {
      const policyType = this.contributionTypes.find(
        (type) => type.id === item.allowanceTypeID
      );
      return (
        policyType &&
        (policyType.title === "PF Contribution" ||
          policyType.title === "EOBI Contribution")
      );
    },

    changeContributionPolicySettAllow() {
      this.contributionPolicySettIDs = this.contributionPolicySett.reduce(
        (acc, el) => {
          if (el.allowanceTypeID) {
            acc.push(el.allowanceTypeID);
          }
          return acc;
        },
        []
      );

      // Update allowanceName when allowanceTypeID changes
      this.contributionPolicySett.forEach((item) => {
        if (item.allowanceTypeID) {
          const policyType = this.contributionTypes.find(
            (type) => type.id === item.allowanceTypeID
          );
          if (policyType) {
            item.allowanceName = policyType.title;
          }
        }
      });
    },

    addContributionPolicySett(item) {
      this.contributionPolicySett = [
        ...this.contributionPolicySett,
        {
          id: 0,
          allowanceTypeID: null,
          allowanceName: "",
          perc: 0,
          campusID: this.$store.state.userData.cId,
          eobiContribution: 0,
          PFcontribution: 0,
        },
      ];
    },

    async removeContributionPolicySett(item, ind) {
      if (item.id == 0) {
        this.contributionPolicySett = this.contributionPolicySett.filter(
          (_, index) => index !== ind
        );
        this.changeContributionPolicySettAllow();
      } else {
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "DeductionPolicy/" +
            item.id +
            "?db=" +
            this.$store.state.userData.db,
          body: null,
          grading: true,
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) {
          this.contributionPolicySett = this.contributionPolicySett.filter(
            (_, index) => index !== ind
          );
          this.contributionPolicyData = this.contributionPolicyData.filter(
            (el) => el.id !== item.id
          );
          this.changeContributionPolicySettAllow();
        }
      }
    },

    checkContributionPolicySett() {
      let state = true;
      this.contributionPolicySett.forEach((el) => {
        el.perc = parseFloat(el.perc);
        if (el.allowanceTypeID == null || isNaN(el.perc) || el.perc < 0) {
          state = false;
        }
      });
      return state;
    },

    async saveContributionPolicySett() {
      if (!this.checkContributionPolicySett()) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t("Error"),
            text: "Please fill all required fields correctly.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
        return;
      }

      this.savingContributionPolicySett = true;
      try {
        const saveData = this.contributionPolicySett.map((item) => {
          const policyType = this.contributionTypes.find(
            (type) => type.id === item.allowanceTypeID
          );
          const isPF = policyType && policyType.title === "PF Contribution";
          const isEOBI = policyType && policyType.title === "EOBI Contribution";

          return {
            id: item.id,
            allowanceTypeID: item.allowanceTypeID,
            allowanceName: item.allowanceName,
            perc: item.perc,
            campusID: item.campusID,
            eobiContribution: isEOBI ? item.perc : 0,
            PFcontribution: isPF ? item.perc : 0,
          };
        });

        const response = await this.post({
          url:
            this.$store.state.domain +
            "DeductionPolicy/SaveData?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          body: saveData,
          message: "Contribution policy saved successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });

        if (response) {
          await this.loadContributionPolicyData();
          this.initializeContributionPolicy();
        }
      } catch (error) {
        console.error("Error saving contribution policy:", error);
      } finally {
        this.savingContributionPolicySett = false;
      }
    },
  },
};
</script>
<style>
.heading {
  padding: 0;
  margin: 2.171303074670571vw 0;
  /* text-shadow: -4px 0 5px white, 0 4px 5px white, 4px 0 5px white,
      0 -4px 5px white; */
}
.input-form {
  margin: 0 70px;
  background: #fff;
}
#fileInput .custom-file-label {
  color: #707070;
  border: 1px dashed #33312f;
  text-align: center;
  padding: 2.5rem 1.5rem;
  width: 100%;
}
#fileInput input {
  display: none;
}
#imgInput input {
  display: none;
}
#imgInput {
  /* height: 8rem; */
  width: 50%;
}
#imgInput div {
  height: 8vw;
}
#imgInput .custom-file-label {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.type {
  background-color: red;
  border-radius: 10px;
  font-size: 0.8rem;
  padding: 10px;
  color: white;
}
.filename {
  padding-left: 25px;
}
.delete {
  margin: 0 0 0 auto;
  color: red;
}
#checkbox-1 {
  margin-right: 10px !important;
}
.submit {
  background-color: #00c192;
  color: #fff;
  width: 16.5vw;
  height: 4vw;
  font-weight: bold;
  border-color: #00c192;
}
.form-file-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 20px;
}
/* #prop-det, #prop-det div {
    height: 100%;
  } */

.item-group {
  width: 50%;
  /* margin-top: 2.269399707174231vw; */
}
.inputfields,
.inputfields:focus {
  border: 1px solid #33312f;
  border-radius: 0.5856515373352855vw;
  height: 2.562225475841874vw;
  outline: none !important;
  resize: none;
}
textarea.form-control {
  height: 8vw !important;
}
@media only screen and (max-width: 990px) {
  .mob-setting {
    padding: 0 1rem !important;
    width: 100vw !important;
    margin: 0 auto !important;
  }
  .mobile-view {
    margin: 0 !important;
  }
  /* .col-form-label,
  .form-file-text,
  .custom-control-label {
    font-size: 3vw;
  } */
  .item-group {
    margin-top: 0;
  }
  .margin-top-mobile {
    margin-top: 5vw;
  }
  /* .col-form-label {
    display: none;
  } */
  .form-input-mobile-text {
    width: 92vw;
    margin: 10px auto;
    border-radius: 2.8301610541727673vw;
    height: 12.781844802342606vw;
    font-size: 5.171303074670571vw;
    background-color: #f2f2f2 !important;
    border: 1px solid #33312f;
  }
  textarea.form-control {
    height: 12.781844802342606vw !important;
  }
  .form-input-mobile-text:focus {
    background-color: #00c192 !important;
    color: #fff !important;
  }
  .form-input-mobile-text::placeholder {
    /* Chrome, Firefox, Opera, Safari 10.1+ */
    color: rgb(190, 190, 190);
    opacity: 1; /* Firefox */
    font-family: DINNextRoundedLTW01-Regular;
  }

  .form-input-mobile-text:-ms-input-placeholder {
    /* Internet Explorer 10-11 */
    color: rgb(190, 190, 190);
  }

  .form-input-mobile-text::-ms-input-placeholder {
    /* Microsoft Edge */
    color: rgb(190, 190, 190);
  }

  .form-input-mobile-text:focus::placeholder {
    /* Chrome, Firefox, Opera, Safari 10.1+ */
    color: #fff;
  }
  .form-file-input-mobile {
    border-radius: 2.8301610541727673vw;
    height: 12.781844802342606vw;
    background-color: #f2f2f2 !important;
    color: #fff !important;
  }
  .custom-file-label {
    border-radius: 2.8301610541727673vw !important;
    height: 12.781844802342606vw;
    background-color: #f2f2f2 !important;
    color: #000 !important;
    height: 40vw;
    border: 1px solid #33312f;
  }
  .submit {
    width: 50vw;
    height: 10vw;
  }
  .filename {
    font-size: 3vw;
  }
  .shadowHead {
    font-size: 8vw !important;
  }
  #imgInput div {
    height: 29vw !important;
  }
  .item-group {
    height: 30vw !important;
  }
}

@media only screen and (min-width: 1366px) {
  .submit {
    width: 15.5vw;
    height: 7.8vh;
  }
}
@media only screen and (min-width: 1440px) {
  .submit {
    width: 14.7vw;
    height: 6.7vh;
  }
}
@media only screen and (min-width: 1536px) {
  .submit {
    width: 13.8vw;
    height: 6.9vh;
  }
}
@media only screen and (min-width: 1920px) {
  .submit {
    width: 11vw;
    height: 5.5vh;
  }
}
#overlay {
  position: fixed; /* Sit on top of the page content */
  display: none; /* Hidden by default */
  width: 100%; /* Full width (cover the whole page) */
  height: 100%; /* Full height (cover the whole page) */
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5); /* Black background with opacity */
  z-index: 2; /* Specify a stack order in case you're using a different order for other elements */
  cursor: pointer; /* Add a pointer on hover */
}
</style>
<style scoped>
.rcorners2 {
  border-radius: 100px;
  border: 4px solid var(--primary);
  padding: 2px;
}

.happiness-photo-group {
  margin-bottom: 0;
}

.happiness-photo-slot {
  display: flex;
  justify-content: center;
}

.happiness-photo-frame {
  position: relative;
  width: 140px;
  height: 140px;
  flex-shrink: 0;
}

.happiness-photo-frame--empty {
  border-radius: 50%;
  border: 4px solid var(--primary);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
}

.happiness-photo-frame--clickable {
  cursor: pointer;
}

.happiness-photo-img {
  width: 140px;
  height: 140px;
  border-radius: 50%;
  border: 4px solid var(--primary);
  padding: 2px;
  object-fit: cover;
  display: block;
  cursor: pointer;
  background: #f3f4f6;
}

.happiness-photo-upload-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.72);
  border-radius: 50%;
  z-index: 3;
}

.happiness-photo-delete-btn,
.staff-photo-delete-btn {
  position: absolute !important;
  bottom: 0;
  right: 0;
  z-index: 5;
  width: 44px !important;
  height: 44px !important;
  min-width: 44px !important;
  padding: 0 !important;
  border-radius: 10px !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.22);
}

.happiness-photo-delete-btn {
  background-color: #82868b !important;
  border-color: #82868b !important;
}

.happiness-photo-delete-btn:hover,
.happiness-photo-delete-btn:focus,
.happiness-photo-delete-btn:active {
  background-color: #6e7378 !important;
  border-color: #6e7378 !important;
}
.sticky {
  position: fixed;
  bottom: 0;
  right: 0;
  margin-right: 60px;
  margin-bottom: 30px;
  /* position: fixed;
      bottom: 5%;
      z-index: 1031;  */
}

.file-uploader {
  position: relative;
  border: 1px solid #000;
  width: 100%;
  line-height: 30px;
  text-align: center;
  border-radius: 10px;
  font-family: "Helvetica", sans-serif;
  height: 100px;
  display: flex;
  justify-content: center;
  flex-direction: column;
}

.file_upload {
  opacity: 0;

  /* IE 8 */
  -ms-filter: "progid:DXImageTransform.Microsoft.Alpha(Opacity=0)";

  /* IE 5-7 */
  filter: alpha(opacity=0);

  /* Netscape or and older firefox browsers */
  -moz-opacity: 0;

  /* older Safari browsers */
  -khtml-opacity: 0;

  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
}
.file {
  display: flex;
  justify-content: space-between;
  margin: 10px 0;
}

.file-type {
  background-color: #e62e2d;
  font-family: "Helvetica", sans-serif;
  color: white;
  border-radius: 10px;
  margin-right: 15px;
  height: 50px;
  width: 50px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
}

.file-name {
  font-family: "Helvetica", sans-serif;
  margin: 0px;
}

.file-delete i {
  color: #e62e2d;
  font-size: 2rem;
  cursor: pointer;
}

/* Staff option styles from circulars.vue */
.staff-option-item {
  padding: 10px 16px;
  border-radius: 8px;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  position: relative;
}

.staff-option-item:hover {
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  border-color: #28a745;
}

.staff-main-info {
  width: 100%;
}

.staff-name-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.staff-name {
  color: #1a237e;
  font-size: 15px;
  font-weight: 600;
  margin: 0;
  line-height: 1.2;
  transition: color 0.2s ease;
}

.staff-details-compact {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.detail-item-compact {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #424242;
  font-size: 11px;
  padding: 3px 8px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.detail-item-compact:hover {
  background: white;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.detail-item-compact .detail-icon {
  color: #28a745;
  flex-shrink: 0;
  transition: color 0.2s ease;
}

.detail-item-compact strong {
  color: #2c3e50;
  font-weight: 600;
  transition: color 0.2s ease;
}

.staff-designation {
  background: linear-gradient(135deg, #fff3e0, #ffe0b2) !important;
  border-color: #ff9800 !important;
  color: #e65100 !important;
}

.staff-designation .detail-icon {
  color: #f57c00 !important;
}

.staff-designation strong {
  color: #bf360c !important;
}

.staff-icon {
  display: inline-flex;
  align-items: center;
  padding: 3px 6px;
  border-radius: 8px;
  background: rgba(103, 126, 234, 0.1);
  border: 1px solid rgba(103, 126, 234, 0.2);
}

.staff-icon .detail-icon {
  color: #667eea;
  transition: color 0.2s ease;
}

/* Children Information Card Styling */
.child-avatar-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-wrapper {
  position: relative;
  display: inline-block;
}

.avatar-upload-btn {
  position: absolute;
  bottom: -5px;
  right: -5px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  border: 2px solid white;
}

/* Children Selector Styling */
.children-sidebar-content {
  padding: 16px;
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e9ecef;
}

.sidebar-header h5 {
  color: #495057;
  font-weight: 600;
}

.close-btn {
  padding: 4px 8px;
  border-radius: 4px;
}

.search-section {
  margin-bottom: 16px;
}

.search-input {
  border-radius: 6px;
  border: 1px solid #ced4da;
  padding: 8px 12px;
  font-size: 14px;
}

.search-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.children-list-container {
  flex: 1;
  overflow-y: auto;
  padding: 4px 8px 4px 0;
}

.student-option-card {
  padding: 8px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
  position: relative;
}

.student-option-card:hover {
  background: #f8f9fa;
  border-color: #007bff;
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.15);
  z-index: 1;
}

.student-option-card:active {
  box-shadow: 0 1px 4px rgba(0, 123, 255, 0.2);
}

.student-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.student-avatar {
  flex-shrink: 0;
}

.student-details {
  flex: 1;
  min-width: 0;
}

.student-name {
  font-weight: 600;
  color: #495057;
  font-size: 14px;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.student-class {
  font-size: 12px;
  color: #6c757d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gr-number {
  margin-left: 6px;
  color: #007bff;
  font-weight: 500;
}

.add-icon {
  color: #28a745;
  flex-shrink: 0;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;
}

.empty-state p {
  margin: 8px 0 0 0;
  font-size: 14px;
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px;
  color: #6c757d;
  font-size: 14px;
}

/* Custom scrollbar for children list */
.children-list-container::-webkit-scrollbar {
  width: 4px;
}

.children-list-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.children-list-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.children-list-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.st-jacob-salary-toggles-row {
  margin-top: 0.35rem;
  margin-bottom: 0.35rem;
  padding-top: 0.85rem;
  padding-bottom: 0.85rem;
  border-top: 1px solid #ebe9f1;
  border-bottom: 1px solid #ebe9f1;
}

.st-jacob-salary-toggle {
  padding: 0.35rem 0;
  min-height: 2.25rem;
}
</style>
<style lang="scss">
@import "@core/scss/vue/libs/vue-wizard.scss";
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
